/**
 * Thai. Every string that reaches t() from src/ has an entry here, so the
 * language switch is a complete translation rather than a partial one that
 * falls back to English mid sentence.
 *
 * THE KEY IS THE ENGLISH STRING, per t.ts. That is what makes this file
 * mechanically checkable: scripts/check-i18n.mjs extracts every t() call in
 * src/ and fails if one has no entry here, or if a translation drops or
 * invents a {placeholder}. It runs inside npm run checks.
 *
 * WHAT STAYS IN LATIN SCRIPT, deliberately: product and company names (SV
 * Cloud, GitHub, Loop, Stripe), anything a person types or copies (npm
 * install -g svcloud, SVAGENTS.md, formatCurrency), the other platforms'
 * vocabulary in the landing page's comparison table, which is quoted rather
 * than translated, and the two design direction names (Instrument,
 * Cleanroom), which are internal labels rather than product vocabulary.
 * Translating any of those produces a string a person cannot act on.
 *
 * WHAT IS NOT HERE, and must not be: fixture data. Recipe titles, file names,
 * table names, commit messages, branch names and people's names are values
 * the product prints as data, not copy, and a Thai app showing a Thai
 * translation of a row in its own database would be lying about the row.
 *
 * Grouped by the screen the string is used on, in the order the extractor
 * found them, because a translator reviewing one screen should be able to
 * read that screen without hunting alphabetically.
 */

export const th: Record<string, string> = {
  /* components/brand */
  "SV Cloud": "SV Cloud",

  /* components/demo */
  "1x": "1x",
  "4x": "4x",
  "Cleanroom": "Cleanroom",
  "Component kit": "ชุดคอมโพเนนต์",
  "Day": "กลางวัน",
  "DEMO": "เดโม",
  "Demo controls": "ตัวควบคุมเดโม",
  "Direction": "ทิศทางงานออกแบบ",
  "EN": "EN",
  "Graduate": "ย้ายออก",
  "Hide demo controls": "ซ่อนตัวควบคุมเดโม",
  "Instant": "ทันที",
  "Instrument": "Instrument",
  "Language": "ภาษา",
  "Next build fails": "ให้การสร้างครั้งถัดไปล้มเหลว",
  "Night": "กลางคืน",
  "Nina": "Nina",
  "Press D to show or hide these controls.": "กด D เพื่อแสดงหรือซ่อนตัวควบคุมเหล่านี้",
  "Pseudo": "ภาษาจำลอง",
  "Reset the demo": "รีเซ็ตเดโม",
  "Sam": "Sam",
  "Scenario": "สถานการณ์",
  "Speed": "ความเร็ว",
  "TH": "TH",
  "Theme": "ธีม",

  /* components/patterns */
  "Analytics choice": "ตัวเลือกการวัดผลการใช้งาน",
  "May we measure how this site is used? With your OK we count page visits and clicks using PostHog, with no screen recording and no email address. Nothing is collected unless you allow it.": "เราขอวัดผลการใช้งานเว็บนี้ได้ไหม หากคุณอนุญาต เราจะนับการเข้าชมหน้าและการคลิกด้วย PostHog โดยไม่บันทึกหน้าจอและไม่เก็บอีเมล หากคุณไม่อนุญาต เราจะไม่เก็บข้อมูลใดๆ",
  "Allow": "อนุญาต",
  "Don't allow": "ไม่อนุญาต",
  "Privacy choices": "ตัวเลือกความเป็นส่วนตัว",
  "{addOn}, {state}": "{addOn}, {state}",
  "{column} changes from {before} to {after}.": "{column} เปลี่ยนจาก {before} เป็น {after}",
  "{label} copied.": "คัดลอก {label} แล้ว",
  "{label} used": "ใช้ {label} ไปแล้ว",
  "{label} by project": "{label} แยกตามโปรเจกต์",
  "Deleted project": "โปรเจกต์ที่ลบแล้ว",
  "Others": "อื่น ๆ",
  "{m}m {s}s": "{m} นาที {s} วินาที",
  "{n} changes not saved yet": "ยังไม่ได้บันทึก {n} รายการ",
  "{n} min": "{n} นาที",
  "{n} new rows": "{n} แถวใหม่",
  "{n} seconds": "{n} วินาที",
  "{n}%": "{n}%",
  "{n}% used": "ใช้ไป {n}%",
  "1 change not saved yet": "ยังไม่ได้บันทึก 1 รายการ",
  "1 new row": "1 แถวใหม่",
  "1 second": "1 วินาที",
  "a deploy is going out": "กำลังดีพลอย",
  "A push to main": "การพุชขึ้น main",
  "a recipe site with photos and a search box": "เว็บสูตรอาหารพร้อมรูปและช่องค้นหา",
  "Account": "บัญชี",
  "ADVANCED": "ขั้นสูง",
  "Anyone holding these can read and change everything in your database. Your app already has them, so you only need them for a tool of your own.": "ใครก็ตามที่ถือสิ่งเหล่านี้อ่านและแก้ไขทุกอย่างในฐานข้อมูลของคุณได้ แอปของคุณมีอยู่แล้ว คุณจึงต้องใช้ก็ต่อเมื่อจะใช้กับเครื่องมือของคุณเอง",
  "Attention": "ต้องดู",
  "Back": "ย้อนกลับ",
  "building your app": "กำลังสร้างแอปของคุณ",
  "Cancel": "ยกเลิก",
  "checking that it answers": "กำลังตรวจว่าตอบสนอง",
  "COLUMN": "คอลัมน์",
  "compiling your code": "กำลังคอมไพล์โค้ดของคุณ",
  "Copied": "คัดลอกแล้ว",
  "Copied the web address for {name}": "คัดลอกที่อยู่เว็บของ {name} แล้ว",
  "Copy": "คัดลอก",
  "Copy {label}": "คัดลอก {label}",
  "Copy fix request": "คัดลอกคำขอให้แก้",
  "Copy this and paste it to your coding agent.": "คัดลอกข้อความนี้ไปวางให้เอเจนต์เขียนโค้ดของคุณ",
  "copy this to your agent": "คัดลอกไปให้เอเจนต์ของคุณ",
  "Copy web address": "คัดลอกที่อยู่เว็บ",
  "Could not copy the web address. Select it on the page instead.": "คัดลอกที่อยู่เว็บไม่สำเร็จ ให้เลือกข้อความบนหน้าแทน",
  "Data": "ข้อมูล",
  "Database": "ฐานข้อมูล",
  "Didn't upload": "อัปโหลดไม่สำเร็จ",
  "Didn't work": "ไม่สำเร็จ",
  "Discard": "ทิ้งไป",
  "do not deploy it yet": "ยังไม่ต้องดีพลอย",
  "Document": "เอกสาร",
  "Done": "เสร็จสิ้น",
  "Down": "ล่ม",
  "Drawing": "ภาพวาด",
  "Drop to upload": "วางเพื่ออัปโหลด",
  "Drop to upload to {folder}": "วางเพื่ออัปโหลดไปยัง {folder}",
  "empty": "ว่าง",
  "Enter to open": "Enter เพื่อเปิด",
  "Escape to close": "Escape เพื่อปิด",
  "Everyone else says": "ที่อื่นเรียกว่า",
  "everything is fine": "ทุกอย่างปกติดี",
  "Everything on this account": "ทุกอย่างในบัญชีนี้",
  "Everything's fine. No problems in the last 24 hours.": "ทุกอย่างปกติดี ไม่มีปัญหาใน 24 ชั่วโมงที่ผ่านมา",
  "Everything's fine. No visitors yet.": "ทุกอย่างปกติดี ยังไม่มีผู้เข้าชม",
  "File": "ไฟล์",
  "Files can be up to {cap}. This one is {size}.": "ไฟล์มีขนาดได้ไม่เกิน {cap} ไฟล์นี้มีขนาด {size}",
  "First seen {when}": "พบครั้งแรกเมื่อ {when}",
  "Fix request": "คำขอให้แก้",
  "Fix request copied to your clipboard.": "คัดลอกคำขอให้แก้ไปยังคลิปบอร์ดแล้ว",
  "formatCurency is not defined": "formatCurency is not defined",
  "From your code": "จากโค้ดของคุณ",
  "Go": "ไป",
  "here is my plan": "นี่คือแผนของฉัน",
  "Hide": "ซ่อน",
  "Host": "โฮสต์",
  "Image": "รูปภาพ",
  "installing packages": "กำลังติดตั้งแพ็กเกจ",
  "Keep editing": "แก้ไขต่อ",
  "Last seen {when}": "พบล่าสุดเมื่อ {when}",
  "Leave this page": "ออกจากหน้านี้",
  "live": "ออนไลน์",
  "Live": "ออนไลน์",
  "making it small": "กำลังย่อขนาด",
  "needs you": "ต้องการคุณ",
  "New project": "โปรเจกต์ใหม่",
  "New tab": "แท็บใหม่",
  "Next": "ถัดไป",
  "No traffic yet": "ยังไม่มีผู้เข้าชม",
  "Nothing has come through yet.": "ยังไม่มีอะไรเข้ามา",
  "Nothing in {table} changes until you save.": "ไม่มีอะไรใน {table} เปลี่ยนจนกว่าคุณจะบันทึก",
  "Nothing is hidden. The technical details are one press away.": "ไม่มีอะไรถูกซ่อน รายละเอียดทางเทคนิคอยู่ห่างไปแค่กดครั้งเดียว",
  "Nothing matches what you typed.": "ไม่มีอะไรตรงกับที่คุณพิมพ์",
  "NOW": "ตอนนี้",
  "Off": "ปิด",
  "OK": "ตกลง",
  "On": "เปิด",
  "one fact about your app": "ข้อเท็จจริงหนึ่งอย่างเกี่ยวกับแอปของคุณ",
  "one file spells it two ways": "ไฟล์เดียวสะกดสองแบบ",
  "one thing needs a look": "มีหนึ่งเรื่องที่ต้องดู",
  "Open": "เปิด",
  "Open {name}": "เปิด {name}",
  "Open the docs": "เปิดเอกสาร",
  "opens in a new tab": "เปิดในแท็บใหม่",
  "Over the limit by {amount}": "เกินขีดจำกัดอยู่ {amount}",
  "Password": "รหัสผ่าน",
  "Paste this to your coding agent": "วางข้อความนี้ให้เอเจนต์เขียนโค้ดของคุณ",
  "Paused": "หยุดชั่วคราว",
  "Play it again": "เล่นอีกครั้ง",
  "pointing the address at it": "กำลังชี้ที่อยู่มาที่แอป",
  "Port": "พอร์ต",
  "Privacy policy": "นโยบายความเป็นส่วนตัว",
  "Projects": "โปรเจกต์",
  "Prompt": "พรอมต์",
  "Put the last working version back": "นำเวอร์ชันล่าสุดที่ใช้ได้กลับมา",
  "Putting an older version back": "กำลังนำเวอร์ชันเก่ากลับมา",
  "reading your prompt": "กำลังอ่านพรอมต์ของคุณ",
  "Ready": "พร้อม",
  "Region": "ภูมิภาค",
  "Replay the tour": "ดูทัวร์อีกครั้ง",
  "Resets in {n} days": "รีเซ็ตในอีก {n} วัน",
  "Resets today": "รีเซ็ตวันนี้",
  "Resets tomorrow": "รีเซ็ตพรุ่งนี้",
  "Reveal": "แสดงค่า",
  "Review and save": "ตรวจแล้วบันทึก",
  "Review your changes": "ตรวจการเปลี่ยนแปลงของคุณ",
  "rows": "แถว",
  "Save {n} changes": "บันทึก {n} รายการ",
  "Save 1 change": "บันทึก 1 รายการ",
  "Save changes": "บันทึกการเปลี่ยนแปลง",
  "Search projects, screens and things to do": "ค้นหาโปรเจกต์ หน้าจอ และสิ่งที่ต้องทำ",
  "Search SV Cloud": "ค้นหาใน SV Cloud",
  "See errors": "ดูข้อผิดพลาด",
  "See usage": "ดูการใช้งาน",
  "See what happened": "ดูว่าเกิดอะไรขึ้น",
  "Seen {n} times": "พบ {n} ครั้ง",
  "Seen once": "พบครั้งเดียว",
  "Select every row on this page": "เลือกทุกแถวในหน้านี้",
  "Select this row": "เลือกแถวนี้",
  "Selected": "เลือกอยู่",
  "Setting up": "กำลังตั้งค่า",
  "Show technical details": "แสดงรายละเอียดทางเทคนิค",
  "Show the next state": "แสดงสถานะถัดไป",
  "Skip the tour": "ข้ามทัวร์",
  "Start": "เริ่ม",
  "starting your app": "กำลังเริ่มแอปของคุณ",
  "Step {n} of {total}": "ขั้นที่ {n} จาก {total}",
  "Stopped": "หยุดแล้ว",
  "SV Cloud is ready for your first deploy.": "SV Cloud พร้อมสำหรับการดีพลอยครั้งแรกของคุณแล้ว",
  "SV Cloud says": "SV Cloud เรียกว่า",
  "table": "ตาราง",
  "the tool that writes your code": "เครื่องมือที่เขียนโค้ดให้คุณ",
  "The transfer stopped partway. Nothing was saved.": "การโอนย้ายหยุดกลางทาง ไม่มีอะไรถูกบันทึก",
  "These are the real words your project page uses.": "นี่คือคำจริงที่หน้าโปรเจกต์ของคุณใช้",
  "This is taking longer than usual. You can leave this page, we'll email you.": "ใช้เวลานานกว่าปกติ คุณออกจากหน้านี้ได้ เราจะส่งอีเมลแจ้งคุณ",
  "This look ships in one theme. Night is the only palette it has, so there is nothing to pick.": "รูปลักษณ์นี้มาพร้อมธีมเดียว มีเฉพาะชุดสีกลางคืน จึงไม่มีอะไรให้เลือก",
  "This project is paused. Your web address shows a holding page.": "โปรเจกต์นี้หยุดชั่วคราวอยู่ ที่อยู่เว็บของคุณแสดงหน้าพักไว้",
  "This project is paused because your account used up its allowance. Your web address shows a holding page.": "โปรเจกต์นี้หยุดชั่วคราวเพราะบัญชีของคุณใช้โควตาหมดแล้ว ที่อยู่เว็บของคุณแสดงหน้าพักไว้",
  "This searches your projects by name and by web address, the screens inside a project such as Deploys, Database, Files and Keys and settings, and a few things to do like starting a new project.": "ช่องนี้ค้นโปรเจกต์ของคุณจากชื่อและที่อยู่เว็บ ค้นหน้าจอในโปรเจกต์ เช่น การดีพลอย ฐานข้อมูล ไฟล์ และคีย์กับการตั้งค่า รวมถึงสิ่งที่ทำได้บางอย่าง เช่น การเริ่มโปรเจกต์ใหม่",
  "Try again": "ลองอีกครั้ง",
  "Turn it back on": "เปิดกลับคืน",
  "Turn on Advanced": "เปิดโหมดขั้นสูง",
  "Type the project name to confirm": "พิมพ์ชื่อโปรเจกต์เพื่อยืนยัน",
  "Up and down to move": "ขึ้นลงเพื่อเลื่อน",
  "Uploading": "กำลังอัปโหลด",
  "uploading your build": "กำลังอัปโหลดไฟล์ที่บิลด์แล้วของคุณ",
  "Uploads are paused right now.": "ตอนนี้การอัปโหลดถูกหยุดไว้ชั่วคราว",
  "Usage": "การใช้งาน",
  "User": "ผู้ใช้",
  "Visit": "เปิดดู",
  "Visit your app": "เปิดแอปของคุณ",
  "Want these details everywhere?": "อยากเห็นรายละเอียดเหล่านี้ทุกที่ไหม",
  "WAS": "เดิมคือ",
  "We're building your app's database, storage and web address. This takes a minute or two.": "เรากำลังสร้างฐานข้อมูล พื้นที่เก็บข้อมูล และที่อยู่เว็บของแอปคุณ ใช้เวลาหนึ่งถึงสองนาที",
  "what do you want to build?": "คุณอยากสร้างอะไร",
  "What SV Cloud calls things": "SV Cloud เรียกสิ่งต่างๆ ว่าอะไร",
  "what SV Cloud says": "SV Cloud เรียกว่า",
  "What went wrong": "เกิดอะไรผิดพลาด",
  "What's in this?": "ในนี้มีอะไรบ้าง",
  "Whether your app is fine": "แอปของคุณปกติดีไหม",
  "wiring up the search box": "กำลังต่อช่องค้นหา",
  "working": "กำลังทำงาน",
  "Working out your usage": "กำลังคำนวณการใช้งานของคุณ",
  "writing the database schema": "กำลังเขียนสคีมาของฐานข้อมูล",
  "You started it": "คุณเป็นคนเริ่ม",
  "You're close to your {resource} limit. {used} of {limit} used.": "คุณใกล้ถึงขีดจำกัด {resource} แล้ว ใช้ไป {used} จาก {limit}",
  "You're over your {resource} limit.": "คุณใช้ {resource} เกินขีดจำกัดแล้ว",
  "Your {addOn} didn't finish setting up.": "{addOn} ของคุณตั้งค่าไม่เสร็จ",
  "Your app is down. Every request in the last {n} minutes came back with an error.": "แอปของคุณล่ม ทุกคำขอใน {n} นาทีที่ผ่านมาได้ข้อผิดพลาดกลับมา",
  "Your app is down. Every request in the last minute came back with an error.": "แอปของคุณล่ม ทุกคำขอในนาทีที่ผ่านมาได้ข้อผิดพลาดกลับมา",
  "your app is live": "แอปของคุณออนไลน์แล้ว",
  "Your app returned errors on {n} requests in the last hour.": "แอปของคุณส่งข้อผิดพลาดกลับใน {n} คำขอในชั่วโมงที่ผ่านมา",
  "Your app returned errors on 1 request in the last hour.": "แอปของคุณส่งข้อผิดพลาดกลับใน 1 คำขอในชั่วโมงที่ผ่านมา",
  "your app right now": "แอปของคุณตอนนี้",
  "Your files hold images, documents and data. Programs are not stored here.": "ไฟล์ของคุณเก็บรูปภาพ เอกสาร และข้อมูล ไม่ได้ใช้เก็บโปรแกรม",
  "Your last deploy didn't work. Your app is still running the version from {date}.": "การดีพลอยครั้งล่าสุดของคุณไม่สำเร็จ แอปของคุณยังใช้เวอร์ชันจากวันที่ {date}",
  "your project page": "หน้าโปรเจกต์ของคุณ",
  "Your system is asking for a dark screen right now, so Sync with system means Night.": "ตอนนี้ระบบของคุณขอหน้าจอแบบมืด การซิงก์กับระบบจึงหมายถึงกลางคืน",
  "Your system is asking for a light screen right now, so Sync with system means Day.": "ตอนนี้ระบบของคุณขอหน้าจอแบบสว่าง การซิงก์กับระบบจึงหมายถึงกลางวัน",

  /* components/shell */
  "Terms of service": "ข้อกำหนดการให้บริการ",
  "Account menu": "เมนูบัญชี",
  "Account settings": "ตั้งค่าบัญชี",
  "Activity": "ความเคลื่อนไหว",
  "Add-ons": "ส่วนเสริม",
  "Advanced": "ขั้นสูง",
  "Advanced adds technical details. It never hides anything.": "โหมดขั้นสูงเพิ่มรายละเอียดทางเทคนิค และไม่ซ่อนอะไรเลย",
  "All projects": "โปรเจกต์ทั้งหมด",
  "An unexpected error occurred. This crash has been logged to telemetry.": "เกิดข้อผิดพลาดที่ไม่คาดคิด ข้อขัดข้องนี้ถูกบันทึกไว้ในระบบติดตามแล้ว",
  "Claim your project": "รับโปรเจกต์ของคุณ",
  "Close": "ปิด",
  "Compute Usage": "การใช้การประมวลผล",
  "Connect your code": "เชื่อมต่อโค้ดของคุณ",
  "Contact us": "ติดต่อเรา",
  "Create your account": "สร้างบัญชีของคุณ",
  "Deadline notice": "ประกาศกำหนดเวลา",
  "Deploy": "ดีพลอย",
  "Deploys": "การดีพลอย",
  "Docs": "เอกสาร",
  "Documentation": "เอกสารประกอบ",
  "Email": "อีเมล",
  "Everything in the console is reachable with the keyboard alone.": "ทุกอย่างในคอนโซลเข้าถึงได้ด้วยแป้นพิมพ์อย่างเดียว",
  "Exit": "ออก",
  "Facebook": "Facebook",
  "Files": "ไฟล์",
  "Filter projects": "กรองโปรเจกต์",
  "Filter projects...": "กรองโปรเจกต์...",
  "Go to project": "ไปที่โปรเจกต์",
  "God View": "มุมมองผู้ดูแล",
  "Help": "ช่วยเหลือ",
  "Instagram": "Instagram",
  "Join the waitlist": "ลงชื่อรอใช้งาน",
  "Keyboard shortcuts": "ทางลัดแป้นพิมพ์",
  "Keys and settings": "คีย์และการตั้งค่า",
  "Kit": "ชุดคอมโพเนนต์",
  "Level of detail": "ระดับรายละเอียด",
  "LINE": "LINE",
  "LINE account": "บัญชี LINE",
  "Live logs": "ล็อกสด",
  "No projects found": "ไม่พบโปรเจกต์",
  "Not found": "ไม่พบ",
  "On Windows and Linux, use Ctrl wherever this list says Cmd.": "บน Windows และ Linux ให้ใช้ Ctrl ทุกที่ที่รายการนี้เขียนว่า Cmd",
  "Overview": "ภาพรวม",
  "Product": "ผลิตภัณฑ์",
  "Project switcher": "ตัวสลับโปรเจกต์",
  "Run a query": "เรียกใช้คำสั่งค้น",
  "Search": "ค้นหา",
  "Sections": "ส่วนต่างๆ",
  "Set up your account": "ตั้งค่าบัญชีของคุณ",
  "Settings": "การตั้งค่า",
  "Sign in": "เข้าสู่ระบบ",
  "Sign out": "ออกจากระบบ",
  "Silicon Valley Innovation Co., Ltd. Bangkok, Thailand.": "บริษัท ซิลิคอน วัลเลย์ อินโนเวชั่น จำกัด กรุงเทพมหานคร ประเทศไทย",
  "Simple": "พื้นฐาน",
  "Skip to content": "ข้ามไปยังเนื้อหา",
  "Something went wrong on this screen": "หน้าจอนี้มีปัญหา",
  "Support email": "อีเมลฝ่ายสนับสนุน",
  "SV Academy": "SV Academy",
  "SV Cloud is built by SV Academy.": "SV Cloud สร้างโดย SV Academy",
  "Switch project: {name}": "สลับโปรเจกต์: {name}",
  "Tell us the name of the project you are asking about, and we can see what it is doing.": "บอกชื่อโปรเจกต์ที่คุณกำลังถามถึง แล้วเราจะดูให้ว่ามันเป็นอย่างไร",
  "TikTok": "TikTok",
  "Traffic": "ผู้เข้าชม",
  "Viewing as {email}, as an admin.": "กำลังดูในมุมของ {email} ในฐานะผู้ดูแล",
  "Wait list": "รายชื่อรอ",
  "Waitlist": "รายชื่อรอ",
  "We answer in Thai and in English, on weekdays, Bangkok time.": "เราตอบทั้งภาษาไทยและภาษาอังกฤษ ในวันทำการ ตามเวลากรุงเทพฯ",
  "Your first deploy": "การดีพลอยครั้งแรกของคุณ",
  "YouTube": "YouTube",

  /* lib/real-api */
  "Added a column to {table}: {column}": "เพิ่มคอลัมน์ใน {table}: {column}",
  "Added a row to {table}": "เพิ่มแถวใน {table}",
  "Connected the repository": "เชื่อมต่อรีโพซิทอรีแล้ว",
  "Created a table: {table}": "สร้างตาราง: {table}",
  "Deleted a row from {table}": "ลบแถวจาก {table}",
  "Deleted this project": "ลบโปรเจกต์นี้แล้ว",
  "Deployed {version}": "ดีพลอย {version} แล้ว",
  "Finished setting up": "ตั้งค่าเสร็จแล้ว",
  "Removed a key: {key}": "นำคีย์ออก: {key}",
  "Renamed this project to {name}": "เปลี่ยนชื่อโปรเจกต์นี้เป็น {name}",
  "Paused this project": "หยุดโปรเจกต์นี้ชั่วคราว",
  "Turned this project back on": "เปิดโปรเจกต์นี้กลับคืน",
  "Rolled back to {version}": "ย้อนกลับไปที่ {version} แล้ว",
  "Set a key: {key}": "ตั้งค่าคีย์: {key}",
  "unknown route": "เส้นทางที่ไม่รู้จัก",
  "Updated a row in {table}": "อัปเดตแถวใน {table}",

  /* lib/run-ledger */
  "retrying": "กำลังลองใหม่",

  /* screens/Kit */
  "1 DOWN": "1 ล่ม",
  "10 READY FOR FIRST DEPLOY": "10 พร้อมดีพลอยครั้งแรก",
  "2 PAUSED": "2 หยุดชั่วคราว",
  "3 DEPLOY DID NOT WORK": "3 ดีพลอยไม่สำเร็จ",
  "4 ADD-ON DID NOT FINISH": "4 ส่วนเสริมทำไม่สำเร็จ",
  "5 OVER THE LIMIT": "5 เกินขีดจำกัด",
  "6 CLOSE TO THE LIMIT": "6 ใกล้ถึงขีดจำกัด",
  "7 ERRORS ELEVATED": "7 ข้อผิดพลาดสูงขึ้น",
  "8 FINE, WITH TRAFFIC": "8 ปกติ มีผู้เข้าชม",
  "9 FINE, NO VISITORS": "9 ปกติ ไม่มีผู้เข้าชม",
  "A native select, restyled. The platform keyboard map, the mobile picker and the screen reader behavior come for free, and nothing in this product is worth reimplementing them for.": "select ดั้งเดิมของแพลตฟอร์ม จัดสไตล์ใหม่ แป้นพิมพ์ ตัวเลือกบนมือถือ และพฤติกรรมของโปรแกรมอ่านหน้าจอมาให้ครบโดยไม่ต้องทำเอง และไม่มีอะไรในผลิตภัณฑ์นี้คุ้มค่าพอให้เขียนใหม่",
  "A restrained line-art mark, one sentence, one action. Never more than one action, and the action is never Learn more. The reassurance state has no button at all, because a button would imply there is something to do.": "ภาพลายเส้นเรียบง่าย หนึ่งประโยค หนึ่งการกระทำ ไม่เกินหนึ่งการกระทำ และการกระทำนั้นไม่ใช่ ดูเพิ่มเติม สถานะให้ความมั่นใจไม่มีปุ่มเลย เพราะปุ่มจะสื่อว่ามีอะไรต้องทำ",
  "A scrim, an opaque panel, trapped focus, and one escape route that always works. Opening moves focus into the panel, Tab cycles inside it, Escape and the scrim both close, and closing returns focus to whatever opened it.": "ฉากหลังทึบ แผงทึบแสง โฟกัสถูกกักไว้ และทางออกเดียวที่ใช้ได้เสมอ เมื่อเปิด โฟกัสจะย้ายเข้าแผง Tab วนอยู่ภายใน Escape และฉากหลังปิดได้ทั้งคู่ และเมื่อปิดโฟกัสจะกลับไปยังสิ่งที่เปิดมัน",
  "A sunken slab, a mono literal, and a copy control pinned right that never shifts layout when its glyph swaps to a check for 1500ms. Click anywhere in the field to copy. Web addresses truncate from the middle so the subdomain and the ending both stay readable. No toast: the check is the receipt.": "แผ่นจม ตัวอักษร mono และปุ่มคัดลอกตรึงขวาที่ไม่ทำให้เลย์เอาต์ขยับตอนสลับเป็นเครื่องหมายถูก 1500 มิลลิวินาที คลิกที่ใดก็ได้ในช่องเพื่อคัดลอก ที่อยู่เว็บถูกตัดกลางเพื่อให้ซับโดเมนและส่วนท้ายยังอ่านได้ ไม่มีข้อความแจ้ง เครื่องหมายถูกคือใบเสร็จ",
  "AS A STILL": "แบบภาพนิ่ง",
  "AT 100 PERCENT": "ที่ 100 เปอร์เซ็นต์",
  "AT 80 PERCENT": "ที่ 80 เปอร์เซ็นต์",
  "Branch": "แบรนช์",
  "BUILD FAILURE, THE CANONICAL USE": "บิลด์ล้มเหลว กรณีมาตรฐาน",
  "Build minutes": "นาทีบิลด์",
  "Changing your plan takes effect on your next bill.": "การเปลี่ยนแพลนจะมีผลในรอบบิลถัดไป",
  "CHECKED, WITH HELP": "ติ๊กแล้ว พร้อมคำอธิบาย",
  "COLLAPSED": "ย่อไว้",
  "Commit": "คอมมิต",
  "COMPACT": "แบบกระชับ",
  "CONFIRMATION": "การยืนยัน",
  "Connecting all the wires": "กำลังต่อสายทั้งหมด",
  "DANGER OUTLINE": "เส้นขอบสีอันตราย",
  "database rows": "แถวในฐานข้อมูล",
  "Database rows": "แถวในฐานข้อมูล",
  "DEFAULT": "ค่าเริ่มต้น",
  "Delete Herb and Ash": "ลบ Herb and Ash",
  "Delete Herb and Ash?": "ลบ Herb and Ash ไหม",
  "Delete this key": "ลบคีย์นี้",
  "Delete this key?": "ลบคีย์นี้ไหม",
  "Deploy id": "รหัสดีพลอย",
  "DISABLED": "ปิดใช้งาน",
  "EDIT A CELL: ENTER, TYPE, ENTER": "แก้ไขช่อง: Enter พิมพ์ Enter",
  "email add-on": "ส่วนเสริมอีเมล",
  "Email me when a deploy does not work": "ส่งอีเมลหาฉันเมื่อดีพลอยไม่สำเร็จ",
  "Environment": "สภาพแวดล้อม",
  "ERROR": "ข้อผิดพลาด",
  "Errors": "ข้อผิดพลาด",
  "Every component in every state, on one page. The quality bar gets set and reviewed here, once, instead of on twenty screens.": "ทุกคอมโพเนนต์ในทุกสถานะ อยู่ในหน้าเดียว มาตรฐานคุณภาพถูกกำหนดและตรวจที่นี่ครั้งเดียว แทนที่จะกระจายไปยี่สิบหน้าจอ",
  "Exactly one maximum contrast ink block per screen, and it is always the next thing to do. Everything else is an outline or a bare label. Disabled sits flat, because an inert control must not read as available. Danger is an outline, never a fill.": "หนึ่งหน้าจอมีบล็อกทึบคอนทราสต์สูงสุดได้หนึ่งชิ้นเท่านั้น และมันคือสิ่งที่ต้องทำต่อไปเสมอ ที่เหลือเป็นแบบเส้นขอบหรือป้ายเปล่า ปุ่มที่ปิดใช้งานจะแบนราบ เพราะปุ่มที่กดไม่ได้ต้องไม่ดูเหมือนกดได้ ปุ่มอันตรายใช้เส้นขอบเสมอ ไม่ใช่พื้นทึบ",
  "EXPANDED IN PLACE": "ขยายอยู่กับที่",
  "File link": "ลิงก์ไฟล์",
  "File storage": "พื้นที่เก็บไฟล์",
  "FIRST EXPAND, WITH THE OFFER": "การขยายครั้งแรก พร้อมข้อเสนอ",
  "GHOST": "แบบโปร่ง",
  "INLINE, IN A TABLE": "แบบอินไลน์ในตาราง",
  "Installing what your app needs": "กำลังติดตั้งสิ่งที่แอปของคุณต้องใช้",
  "invoices": "ใบแจ้งหนี้",
  "IRREVERSIBLE, INERT UNTIL THE NAME MATCHES": "ย้อนกลับไม่ได้ กดไม่ได้จนกว่าชื่อจะตรงกัน",
  "Key": "คีย์",
  "Key name": "ชื่อคีย์",
  "Key names can use letters, numbers and underscores.": "ชื่อคีย์ใช้ตัวอักษร ตัวเลข และขีดล่างได้",
  "Label, control, then help or error, never both at once. Mono is reserved for a copyable literal: a key name, an identifier, a project slug.": "ป้ายกำกับ ตัวควบคุม แล้วตามด้วยคำอธิบายหรือข้อผิดพลาด ไม่แสดงพร้อมกัน ฟอนต์ mono สงวนไว้สำหรับข้อความที่คัดลอกได้ เช่น ชื่อคีย์ รหัสอ้างอิง หรือสลักของโปรเจกต์",
  "Large": "ใหญ่",
  "LARGE, THE FIRST BUILD PAYOFF": "ขนาดใหญ่ ผลลัพธ์ของการสร้างครั้งแรก",
  "LEAN: THE GEOMETRY A PHONE GETS": "แบบกระชับ เลย์เอาต์ที่มือถือได้รับ",
  "LIVE, ESCAPE AND THE SCRIM BOTH CANCEL": "สถานะจริง Escape และฉากหลังยกเลิกได้ทั้งคู่",
  "LIVE, FOCUS TRAPPED": "สถานะจริง โฟกัสถูกกักไว้",
  "LIVE, FROM THE TIMELINE ENGINE": "สถานะจริง จากเอนจินไทม์ไลน์",
  "LOADING": "กำลังโหลด",
  "LONG RUNNING, THE ESCAPE HATCH": "ใช้เวลานาน ทางออกสำรอง",
  "Medium": "กลาง",
  "MIDDLE TRUNCATED": "ตัดตรงกลาง",
  "Never a bare spinner, ever. All steps are visible from the start, none reorder, none disappear, and completed steps stay on screen when a later one fails. Work in flight is neutral, because colored motion reads as an alarm. Progress is read from the clock, so a refresh mid build keeps the build correct.": "ไม่มีวงกลมหมุนเปล่าเด็ดขาด ทุกขั้นตอนมองเห็นได้ตั้งแต่ต้น ไม่มีการสลับลำดับ ไม่มีขั้นตอนหายไป และขั้นที่เสร็จแล้วยังอยู่บนหน้าจอเมื่อขั้นถัดไปล้มเหลว งานที่กำลังทำอยู่ใช้สีกลาง เพราะการเคลื่อนไหวที่มีสีอ่านได้เหมือนสัญญาณเตือน ความคืบหน้าอ่านจากนาฬิกา การรีเฟรชกลางคันจึงไม่ทำให้การสร้างผิดเพี้ยน",
  "NO ACTIVITY": "ไม่มีความเคลื่อนไหว",
  "No errors in the last 7 days.": "ไม่มีข้อผิดพลาดใน 7 วันที่ผ่านมา",
  "NO ERRORS, REASSURANCE, NO ACTION": "ไม่มีข้อผิดพลาด ให้ความมั่นใจ ไม่มีสิ่งที่ต้องทำ",
  "NO FILES": "ไม่มีไฟล์",
  "NO KEYS, THE ADD FORM IS THE ACTION": "ไม่มีคีย์ ฟอร์มเพิ่มคือสิ่งที่ต้องทำ",
  "NO PROJECTS": "ไม่มีโปรเจกต์",
  "NO TABLES, THE AGENT PROMPT VARIANT": "ไม่มีตาราง แบบที่มีพรอมต์สำหรับเอเจนต์",
  "NO TRAFFIC": "ไม่มีผู้เข้าชม",
  "NO TRAFFIC YET": "ยังไม่มีผู้เข้าชม",
  "No visitors yet.": "ยังไม่มีผู้เข้าชม",
  "Nothing has happened yet. Your first deploy will show up here.": "ยังไม่มีอะไรเกิดขึ้น การดีพลอยครั้งแรกของคุณจะแสดงที่นี่",
  "Nothing on this page names a color, a font or a duration. Every flip is a token change, so a direction that looks wrong here is a token file to fix, not twenty components.": "ไม่มีอะไรในหน้านี้ระบุสี ฟอนต์ หรือระยะเวลาโดยตรง ทุกการสลับคือการเปลี่ยนโทเคน ทิศทางที่ดูผิดตรงนี้จึงเป็นไฟล์โทเคนที่ต้องแก้ ไม่ใช่คอมโพเนนต์ยี่สิบตัว",
  "One at a time, bottom centre, four seconds, confirmations only. There is no error tone and there will not be one: a message a person has to read must not be able to leave on its own, so errors are owned by the page.": "ทีละหนึ่ง กลางล่าง สี่วินาที ใช้กับการยืนยันเท่านั้น ไม่มีโทนสีของข้อผิดพลาดและจะไม่มี ข้อความที่คนต้องอ่านต้องหายไปเองไม่ได้ ข้อผิดพลาดจึงเป็นหน้าที่ของหน้าจอ",
  "One file calls a function named formatCurency, but the file that defines it spells it formatCurrency. The build stopped before it could finish, so nothing changed on your live site.": "ไฟล์หนึ่งเรียกฟังก์ชันชื่อ formatCurency แต่ไฟล์ที่ประกาศไว้สะกดว่า formatCurrency การสร้างจึงหยุดก่อนเสร็จ และไม่มีอะไรบนเว็บที่ออนไลน์อยู่เปลี่ยนแปลง",
  "One file reads a total on an invoice that came back empty. It happens when an invoice was created but nothing was added to it yet.": "ไฟล์หนึ่งอ่านยอดรวมจากใบแจ้งหนี้ที่ได้ค่าว่างกลับมา เกิดขึ้นเมื่อใบแจ้งหนี้ถูกสร้างแล้วแต่ยังไม่มีรายการใดถูกเพิ่มเข้าไป",
  "ONE JOURNEY, THEN AT REST": "หนึ่งเส้นทาง แล้วหยุดนิ่ง",
  "One per project, ever. A pure precedence function picks the single sentence that answers whether the app is fine, and gives it exactly one action, which is the screen ink block. Thresholds on screen so they can be argued about: 80 percent of a resource, and 5 percent of requests or 10 errors in an hour, over a floor of 20 requests.": "หนึ่งโปรเจกต์ต่อหนึ่งอันเท่านั้น ฟังก์ชันลำดับความสำคัญล้วนเลือกประโยคเดียวที่ตอบว่าแอปปกติดีไหม และให้การกระทำเพียงหนึ่งอย่าง ซึ่งคือบล็อกทึบของหน้าจอ เกณฑ์แสดงบนหน้าจอเพื่อให้ถกกันได้ 80 เปอร์เซ็นต์ของทรัพยากร และ 5 เปอร์เซ็นต์ของคำขอหรือ 10 ข้อผิดพลาดในหนึ่งชั่วโมง โดยมีขั้นต่ำที่ 20 คำขอ",
  "Open the dialog": "เปิดกล่องโต้ตอบ",
  "OUTLINE": "แบบเส้นขอบ",
  "OVER THE LIMIT": "เกินขีดจำกัด",
  "PERCENT ONLY": "แสดงเปอร์เซ็นต์เท่านั้น",
  "BY PROJECT": "แยกตามโปรเจกต์",
  "BY PROJECT, NEAR": "แยกตามโปรเจกต์ ใกล้เต็ม",
  "Plan": "แพลน",
  "Press D for the demo dock. It flips the brand direction between Instrument and Cleanroom, the theme between Night and Day, and the language between English, Thai and the pseudo locale that proves the layout survives 1.4x expansion.": "กด D เพื่อเปิดแผงเดโม มันสลับทิศทางงานออกแบบระหว่าง Instrument กับ Cleanroom สลับธีมระหว่างกลางคืนกับกลางวัน และสลับภาษาระหว่างอังกฤษ ไทย และภาษาจำลองที่ใช้พิสูจน์ว่าเลย์เอาต์ทนการขยาย 1.4 เท่าได้",
  "Press Run to drive this from the same engine the product screens use.": "กด เรียกใช้ เพื่อสั่งงานจากเอนจินเดียวกับที่หน้าจอผลิตภัณฑ์ใช้",
  "Production": "โปรดักชัน",
  "Project name": "ชื่อโปรเจกต์",
  "Project sections": "ส่วนต่างๆ ของโปรเจกต์",
  "Publishing your web address": "กำลังเผยแพร่ที่อยู่เว็บของคุณ",
  "Put that version back": "นำเวอร์ชันนั้นกลับมา",
  "Put the last working version back?": "นำเวอร์ชันล่าสุดที่ใช้ได้กลับมาไหม",
  "Quiet but not hidden. Expands in place, never a modal, never a route, never above the simple content. It remembers nothing: Advanced mode is the persistence mechanism, and with Advanced on the trigger is not rendered at all.": "เงียบแต่ไม่ถูกซ่อน ขยายอยู่กับที่ ไม่ใช่หน้าต่างซ้อน ไม่ใช่เส้นทางใหม่ และไม่เคยอยู่เหนือเนื้อหาพื้นฐาน มันไม่จำอะไรเลย โหมดขั้นสูงคือกลไกจดจำ และเมื่อเปิดโหมดขั้นสูงแล้ว ตัวเปิดนี้จะไม่ถูกแสดงเลย",
  "Reading your code": "กำลังอ่านโค้ดของคุณ",
  "Recent deploys": "การดีพลอยล่าสุด",
  "Releasing your web address": "กำลังปล่อยที่อยู่เว็บของคุณ",
  "requests": "คำขอ",
  "Requests": "คำขอ",
  "REVERSIBLE, NO TYPING": "ย้อนกลับได้ ไม่ต้องพิมพ์ยืนยัน",
  "Run the first build": "เริ่มการสร้างครั้งแรก",
  "RUNTIME ERROR GROUP": "กลุ่มข้อผิดพลาดขณะทำงาน",
  "Saving": "กำลังบันทึก",
  "SELECTED, WITH COUNTS": "เลือกแล้ว พร้อมจำนวน",
  "Send weekly reports": "ส่งรายงานรายสัปดาห์",
  "SENSITIVE, MASKED": "ข้อมูลอ่อนไหว ปิดบังไว้",
  "Setting up your database": "กำลังตั้งค่าฐานข้อมูลของคุณ",
  "Setting your database aside": "กำลังเก็บฐานข้อมูลของคุณไว้",
  "Setting your files aside": "กำลังเก็บไฟล์ของคุณไว้",
  "Shape coded and word labeled, without exception. Five shapes, three colors, and the word is never dropped at any size. Turn every color off and the screen still works. Nothing glows: Instrument carries enabled and inert through fill weight instead.": "ใช้รูปทรงกำกับและมีคำกำกับเสมอ ไม่มีข้อยกเว้น ห้ารูปทรง สามสี และคำไม่ถูกตัดออกไม่ว่าขนาดใด ปิดสีทั้งหมดแล้วหน้าจอยังใช้งานได้ ไม่มีอะไรเรืองแสง Instrument สื่อสถานะเปิดใช้และกดไม่ได้ผ่านน้ำหนักของพื้นแทน",
  "Show a toast": "แสดงข้อความแจ้ง",
  "Show technical details everywhere": "แสดงรายละเอียดทางเทคนิคทุกที่",
  "Show the build log": "แสดงล็อกการบิลด์",
  "Show the stack trace": "แสดงสแตกเทรซ",
  "SINGLE NARRATED LINE": "บรรทัดบรรยายเดียว",
  "SIZES 28 / 32 / 36": "ขนาด 28 / 32 / 36",
  "Small": "เล็ก",
  "SOLID, THE INK BLOCK": "แบบทึบ บล็อกหลัก",
  "Some invoices have no line items, and your app tries to add them up anyway.": "ใบแจ้งหนี้บางใบไม่มีรายการย่อย แต่แอปของคุณยังพยายามรวมยอดอยู่ดี",
  "Speed {speed}, from the dock": "ความเร็ว {speed} จากแผงเดโม",
  "Starter": "Starter",
  "STEP LEDGER, ONE STEP FAILED": "รายการขั้นตอน มีหนึ่งขั้นล้มเหลว",
  "STICKY HEADER, MONO NUMERIC COLUMN": "หัวตารางตรึง คอลัมน์ตัวเลขแบบ mono",
  "Summary, plain cause with the location on its own line, actions, raw disclosure. The last two are never optional. The fix request is written as a prompt, it carries the code context, and it closes by telling the agent not to deploy, which is the most valuable line in the payload.": "สรุป สาเหตุแบบภาษาง่ายพร้อมตำแหน่งบนบรรทัดของตัวเอง การกระทำ และส่วนเปิดเผยข้อมูลดิบ สองอย่างหลังไม่เคยเป็นตัวเลือก คำขอให้แก้เขียนเป็นพรอมต์ พกบริบทของโค้ดไปด้วย และปิดท้ายด้วยการบอกเอเจนต์ว่าอย่าเพิ่งดีพลอย ซึ่งเป็นบรรทัดที่มีค่าที่สุดในข้อความนั้น",
  "Tables will appear here when your app creates them.": "ตารางจะปรากฏที่นี่เมื่อแอปของคุณสร้างขึ้น",
  "Taking your app offline": "กำลังนำแอปของคุณออฟไลน์",
  "The Advanced state of every disclosure is one URL away: open this page with ?adv=1, or use the Simple and Advanced switch at the bottom of the rail. Both open the same page, so the two artboards can sit side by side.": "สถานะขั้นสูงของทุกส่วนเปิดเผยอยู่ห่างไปแค่ URL เดียว เปิดหน้านี้ด้วย ?adv=1 หรือใช้สวิตช์พื้นฐานกับขั้นสูงที่ท้ายแถบด้านข้าง ทั้งสองทางเปิดหน้าเดียวกัน อาร์ตบอร์ดสองแบบจึงวางเทียบกันได้",
  "The bar is decoration, the number is the content. Zero is a real state with its own words, not an empty track. Over the limit draws the fill to the end, hatches past the end of the track, and writes the overage in words. A percent-only meter drops the amount entirely: an abstract unit has no reading a person can act on.": "แถบคือการตกแต่ง ตัวเลขคือเนื้อหา ศูนย์เป็นสถานะจริงที่มีถ้อยคำของตัวเอง ไม่ใช่รางเปล่า เมื่อเกินขีดจำกัด แถบจะเต็มจนสุด ลากลายทแยงเลยปลายราง และเขียนส่วนที่เกินเป็นคำ มาตรที่แสดงเฉพาะเปอร์เซ็นต์จะตัดจำนวนออกทั้งหมด เพราะหน่วยนามธรรมไม่มีค่าที่คนนำไปทำอะไรต่อได้",
  "The box is drawn, the input is real. One line of consequence sits under the label, in the vocabulary a person already uses.": "กรอบถูกวาดขึ้น แต่ช่องกรอกเป็นของจริง ใต้ป้ายกำกับมีหนึ่งบรรทัดบอกผลที่ตามมา ด้วยคำที่คนใช้อยู่แล้ว",
  "The flight. Two rails of cards ride from behind the screen toward the viewer, once. It waits paused on an authored frame, plays exactly one journey when the reader reaches it, and rests on the frame it started from, because every card is offset by one whole cycle divided by the card count. Scrolling through it dollies the camera. Card art is a separate deliverable, so the cards are token plates today.": "การบิน การ์ดสองแถวเคลื่อนจากหลังจอมาหาผู้ชมหนึ่งครั้ง มันหยุดรออยู่ที่เฟรมที่กำหนดไว้ เล่นหนึ่งรอบพอดีเมื่อผู้อ่านเลื่อนมาถึง แล้วหยุดที่เฟรมเดิมที่เริ่มมา เพราะการ์ดทุกใบเหลื่อมกันเท่ากับหนึ่งรอบหารด้วยจำนวนการ์ด การเลื่อนผ่านคือการเคลื่อนกล้อง ภาพบนการ์ดเป็นงานส่งมอบแยกต่างหาก วันนี้การ์ดจึงเป็นแผ่นสีจากโทเคน",
  "The header sticks, so a scrolled list never loses what its columns mean. The numeric column is mono, tabular and right aligned. A column of words never gets the mono flag, however much it would tidy the alignment.": "หัวตารางตรึงอยู่ รายการที่เลื่อนแล้วจึงไม่สูญเสียความหมายของคอลัมน์ คอลัมน์ตัวเลขใช้ฟอนต์ mono แบบตารางและชิดขวา คอลัมน์ที่เป็นคำไม่ใช้ mono ไม่ว่ามันจะทำให้เรียงสวยขึ้นแค่ไหน",
  "The ledger version: a hairline runs the width of the container and the selected tab thickens its own segment. Arrows move, Home and End jump, and only the selected tab is in the tab order.": "แบบรายการ เส้นบางพาดตลอดความกว้างของกล่อง และแท็บที่เลือกจะหนาขึ้นเฉพาะช่วงของตัวเอง ปุ่มลูกศรเลื่อน Home และ End กระโดด และมีเฉพาะแท็บที่เลือกอยู่ในลำดับ Tab",
  "The M1 cut: sticky header, arrow keys and Home and End to move, Enter to edit, Escape to cancel, and staged edits that do not touch the data until Save. Every cell is a literal out of a database, so every cell is mono and numbers are right aligned. Virtualization, the sticky first column and the diff sheet land in M3.": "รุ่น M1 หัวตารางตรึง ปุ่มลูกศรกับ Home และ End เพื่อเลื่อน Enter เพื่อแก้ไข Escape เพื่อยกเลิก และการแก้ไขที่พักไว้โดยไม่แตะข้อมูลจนกว่าจะบันทึก ทุกช่องคือค่าตรงจากฐานข้อมูล ทุกช่องจึงใช้ฟอนต์ mono และตัวเลขชิดขวา การทำ virtualization คอลัมน์แรกแบบตรึง และแผ่นเทียบความต่างจะมาใน M3",
  "The panel is a real tab panel, and it is focusable, so a keyboard reaches it.": "แผงนี้เป็นแท็บแพเนลจริงและโฟกัสได้ แป้นพิมพ์จึงเข้าถึงได้",
  "The sky, for the signed out landing only. One WebGL canvas whose five colour stops and ten knobs are read from the token layer at mount, so the flip in the demo dock repaints it in one frame with no rebuild and no lost context. Try it: press D, switch the direction, and watch the graphite go violet. It is never mounted in the console.": "ท้องฟ้า ใช้เฉพาะหน้าแรกตอนยังไม่เข้าสู่ระบบ แคนวาส WebGL หนึ่งชิ้นที่อ่านจุดสีห้าจุดและปุ่มปรับสิบตัวจากชั้นโทเคนตอนติดตั้ง การสลับในแผงเดโมจึงวาดใหม่ในเฟรมเดียวโดยไม่ต้องบิลด์ใหม่และไม่เสียบริบท ลองดู กด D สลับทิศทาง แล้วดูสีกราไฟต์กลายเป็นม่วง มันไม่เคยถูกติดตั้งในคอนโซล",
  "THE SKY, RUNNING": "ท้องฟ้า กำลังทำงาน",
  "THE STILL: REDUCED MOTION, A PHONE, SAVE DATA, NO WEBGL": "ภาพนิ่ง ลดการเคลื่อนไหว บนมือถือ โหมดประหยัดข้อมูล ไม่มี WebGL",
  "THE STILL: REDUCED MOTION, A SMALL PHONE, SAVE DATA": "ภาพนิ่ง ลดการเคลื่อนไหว บนมือถือจอเล็ก โหมดประหยัดข้อมูล",
  "This is the address people will type to reach your app.": "นี่คือที่อยู่ที่คนจะพิมพ์เพื่อเข้าถึงแอปของคุณ",
  "This is the same switch as Advanced in your account menu.": "นี่คือสวิตช์เดียวกับ ขั้นสูง ในเมนูบัญชีของคุณ",
  "Took": "ใช้เวลา",
  "Try this deploy again": "ลองดีพลอยนี้อีกครั้ง",
  "Type to confirm is reserved for the irreversible. Using it on reversible actions trains people to type through dialogs, which is what makes it stop working on the one that matters. The enumeration mixes what survives with what does not, and the action repeats the verb and the object.": "การพิมพ์เพื่อยืนยันสงวนไว้สำหรับสิ่งที่ย้อนกลับไม่ได้ การใช้กับสิ่งที่ย้อนกลับได้จะฝึกให้คนพิมพ์ผ่านกล่องโต้ตอบไปเฉยๆ ซึ่งทำให้มันใช้ไม่ได้ผลกับกรณีที่สำคัญจริง รายการที่แจกแจงจะผสมสิ่งที่รอดกับสิ่งที่ไม่รอด และปุ่มการกระทำจะย้ำทั้งคำกริยาและสิ่งที่ถูกกระทำ",
  "UNCHECKED": "ยังไม่ติ๊ก",
  "UNDER 80 PERCENT": "ต่ำกว่า 80 เปอร์เซ็นต์",
  "Upload": "อัปโหลด",
  "Uploaded files are deleted.": "ไฟล์ที่อัปโหลดไว้จะถูกลบ",
  "Uploading leek-stew.jpg": "กำลังอัปโหลด leek-stew.jpg",
  "Usage counts start the first time somebody opens your app.": "การนับการใช้งานเริ่มครั้งแรกที่มีคนเปิดแอปของคุณ",
  "Web address": "ที่อยู่เว็บ",
  "What changed": "อะไรเปลี่ยนไป",
  "Who": "ใคร",
  "WITH HELP": "พร้อมคำอธิบาย",
  "You can add it again at any time.": "คุณเพิ่มกลับได้ทุกเมื่อ",
  "You haven't added any keys yet.": "คุณยังไม่ได้เพิ่มคีย์ใดเลย",
  "You haven't built anything yet.": "คุณยังไม่ได้สร้างอะไรเลย",
  "Your app hasn't stored any files yet.": "แอปของคุณยังไม่ได้เก็บไฟล์ใดเลย",
  "Your app is running the version from 18 August.": "แอปของคุณกำลังใช้เวอร์ชันจากวันที่ 18 สิงหาคม",
  "Your app stops being able to use this key on the next deploy.": "แอปของคุณจะใช้คีย์นี้ไม่ได้ตั้งแต่การดีพลอยครั้งถัดไป",
  "Your app will go back to the version from 18 August. Nothing in your database changes.": "แอปของคุณจะกลับไปใช้เวอร์ชันจากวันที่ 18 สิงหาคม ไม่มีอะไรในฐานข้อมูลของคุณเปลี่ยน",
  "Your code has a spelling mistake in a function name.": "โค้ดของคุณสะกดชื่อฟังก์ชันผิด",
  "Your code on GitHub is untouched.": "โค้ดของคุณบน GitHub ไม่ถูกแตะต้อง",
  "Your database and everything in it is deleted.": "ฐานข้อมูลของคุณและทุกอย่างในนั้นจะถูกลบ",
  "Your key was saved.": "บันทึกคีย์ของคุณแล้ว",
  "Your other projects keep running.": "โปรเจกต์อื่นของคุณยังทำงานต่อ",
  "Your web address stops working straight away.": "ที่อยู่เว็บของคุณจะหยุดทำงานทันที",

  /* screens/NotFound */
  "Go to your projects": "ไปที่โปรเจกต์ของคุณ",
  "That page is not here": "ไม่มีหน้านั้นที่นี่",
  "The address you followed does not match anything in this prototype.": "ที่อยู่ที่คุณตามมาไม่ตรงกับสิ่งใดในต้นแบบนี้",

  /* screens/_Stub */
  "Built from:": "สร้างจาก:",
  "Lands in": "ถึงเมื่อ",
  "Not built yet": "ยังไม่ได้สร้าง",
  "Route": "เส้นทาง",
  "This route renders inside its real layout so the shell, the routing and the token layer can be reviewed before the screen exists.": "เส้นทางนี้แสดงผลอยู่ในเลย์เอาต์จริง เพื่อให้ตรวจโครงหน้า การกำหนดเส้นทาง และชั้นโทเคนได้ก่อนที่หน้าจอจะมีจริง",

  /* screens/account */
  "On: {plan} renews automatically every month for ${price}, paid from your credits, until you turn this off. We email you before each renewal. Turn it off at any time and your plan ends with its cycle.": "เปิด: {plan} จะต่ออายุอัตโนมัติทุกเดือน ราคา ${price} โดยหักจากเครดิตของคุณ จนกว่าคุณจะปิด เราจะส่งอีเมลแจ้งก่อนการต่ออายุแต่ละครั้ง ปิดได้ทุกเมื่อ แล้วแพลนจะสิ้นสุดเมื่อจบรอบ",
  "Off: nothing renews, and your plan ends with its cycle. Turn this on to renew {plan} automatically every month for ${price}, paid from your credits, until you turn it off.": "ปิด: จะไม่มีการต่ออายุ และแพลนจะสิ้นสุดเมื่อจบรอบ เปิดเพื่อต่ออายุ {plan} อัตโนมัติทุกเดือน ราคา ${price} โดยหักจากเครดิตของคุณ จนกว่าคุณจะปิด",
  "Type your email address to confirm": "พิมพ์อีเมลของคุณเพื่อยืนยัน",
  "Delete account": "ลบบัญชี",
  "Removes your sign in and your contact details. Delete your projects first; this does not delete them.": "ลบข้อมูลการลงชื่อเข้าใช้และข้อมูลติดต่อของคุณ โปรดลบโปรเจกต์ก่อน การลบบัญชีไม่ได้ลบโปรเจกต์",
  "Delete your projects first, then delete the account.": "โปรดลบโปรเจกต์ของคุณก่อน แล้วจึงลบบัญชี",
  "Something went wrong deleting your account.": "เกิดข้อผิดพลาดระหว่างลบบัญชีของคุณ",
  "Delete your account?": "ลบบัญชีของคุณใช่ไหม",
  "Your sign in, your name and email, your API tokens and your GitHub connection are removed.": "ข้อมูลการลงชื่อเข้าใช้ ชื่อและอีเมล โทเคน API และการเชื่อมต่อ GitHub ของคุณจะถูกลบ",
  "Billing records the law makes us keep stay, without your contact details.": "บันทึกการเรียกเก็บเงินที่กฎหมายกำหนดให้เก็บยังคงอยู่ แต่ไม่มีข้อมูลติดต่อของคุณ",
  "This cannot be undone. Signing in again later starts a new account.": "การลบนี้ย้อนกลับไม่ได้ หากลงชื่อเข้าใช้อีกในภายหลังจะเป็นบัญชีใหม่",
  "What this dashboard measures about you, and your choice about it.": "สิ่งที่แดชบอร์ดนี้วัดเกี่ยวกับคุณ และตัวเลือกของคุณ",
  "You allowed product analytics in this browser.": "คุณอนุญาตการวัดผลการใช้งานในเบราว์เซอร์นี้แล้ว",
  "You turned product analytics off in this browser.": "คุณปิดการวัดผลการใช้งานในเบราว์เซอร์นี้แล้ว",
  "Product analytics is off in this browser until you choose.": "การวัดผลการใช้งานปิดอยู่ในเบราว์เซอร์นี้จนกว่าคุณจะเลือก",
  "Account ID": "รหัสบัญชี",
  "Add a password": "เพิ่มรหัสผ่าน",
  "Advanced adds identifiers, raw numbers, logs, and your app's configuration. It never hides anything Simple shows.": "โหมดขั้นสูงเพิ่มรหัสอ้างอิง ตัวเลขดิบ ล็อก และการตั้งค่าของแอปคุณ และไม่เคยซ่อนสิ่งที่โหมดพื้นฐานแสดง",
  "Appearance": "การแสดงผล",
  "Change password": "เปลี่ยนรหัสผ่าน",
  "Current password": "รหัสผ่านปัจจุบัน",
  "Details": "รายละเอียด",
  "Email me when a deploy doesn't work": "ส่งอีเมลหาฉันเมื่อดีพลอยไม่สำเร็จ",
  "Email me when I'm close to a limit": "ส่งอีเมลหาฉันเมื่อใกล้ถึงขีดจำกัด",
  "Email me when one of my apps goes down": "ส่งอีเมลหาฉันเมื่อแอปของฉันล่ม",
  "English": "English",
  "Everything you put into SV Cloud can come back out, whenever you want it.": "ทุกอย่างที่คุณใส่เข้ามาใน SV Cloud นำออกไปได้เสมอ เมื่อไรก็ได้ที่คุณต้องการ",
  "Export everything": "ส่งออกทั้งหมด",
  "Export everything on your account: your projects, your database contents, and your files.": "ส่งออกทุกอย่างในบัญชีของคุณ ทั้งโปรเจกต์ เนื้อหาในฐานข้อมูล และไฟล์ของคุณ",
  "How the console looks. How much it tells you is the next section.": "หน้าตาของคอนโซล ส่วนที่ว่าจะบอกรายละเอียดมากแค่ไหนอยู่หัวข้อถัดไป",
  "Joined": "เข้าร่วมเมื่อ",
  "Loading your account": "กำลังโหลดบัญชีของคุณ",
  "Name": "ชื่อ",
  "New password": "รหัสผ่านใหม่",
  "New password again": "ยืนยันรหัสผ่านใหม่",
  "Notifications": "การแจ้งเตือน",
  "Profile": "โปรไฟล์",
  "Save": "บันทึก",
  "Saved": "บันทึกแล้ว",
  "Simple shows what happened and what to do next.": "โหมดพื้นฐานแสดงว่าเกิดอะไรขึ้นและต้องทำอะไรต่อ",
  "Thai": "ไทย",
  "These numbers are everything on your account, added up.": "ตัวเลขเหล่านี้คือทุกอย่างในบัญชีของคุณรวมกัน",
  "This is the address you sign in with. Contact us if it needs to change.": "นี่คือที่อยู่ที่คุณใช้เข้าสู่ระบบ ติดต่อเราหากต้องเปลี่ยน",
  "We'll email you a link when it's ready.": "เราจะส่งลิงก์ทางอีเมลให้คุณเมื่อพร้อม",
  "You sign in with GitHub. You don't have a password.": "คุณเข้าสู่ระบบด้วย GitHub คุณจึงไม่มีรหัสผ่าน",
  "You'll need to sign in again to reach your projects. Nothing else changes.": "คุณต้องเข้าสู่ระบบใหม่เพื่อเข้าถึงโปรเจกต์ของคุณ นอกนั้นไม่มีอะไรเปลี่ยน",
  "Your data": "ข้อมูลของคุณ",
  "Your picture comes from GitHub, where you sign in as {handle}.": "รูปของคุณมาจาก GitHub ซึ่งคุณเข้าสู่ระบบในชื่อ {handle}",

  /* screens/admin */
  "Accounts": "บัญชี",
  "Actions": "การกระทำ",
  "Building": "กำลังสร้าง",
  "Could not load accounts. Try again in a moment.": "โหลดบัญชีไม่สำเร็จ ลองอีกครั้งในอีกสักครู่",
  "Could not load the waitlist. Try again in a moment.": "โหลดรายชื่อรอไม่สำเร็จ ลองอีกครั้งในอีกสักครู่",
  "Could not search projects. Try again in a moment.": "ค้นหาโปรเจกต์ไม่สำเร็จ ลองอีกครั้งในอีกสักครู่",
  "Decline": "ปฏิเสธ",
  "Declined": "ปฏิเสธแล้ว",
  "Enrolled": "ลงทะเบียนแล้ว",
  "Every active account. View as one to see their dashboard exactly as they would.": "ทุกบัญชีที่ใช้งานอยู่ เลือกดูในมุมของบัญชีใดบัญชีหนึ่งเพื่อเห็นแดชบอร์ดแบบเดียวกับที่เขาเห็น",
  "God View sections": "ส่วนต่าง ๆ ของมุมมองผู้ดูแล",
  "Hand-pick who gets access to SV Cloud from the waitlist.": "เลือกด้วยตัวเองว่าใครในรายชื่อรอจะได้เข้าใช้ SV Cloud",
  "Invite": "เชิญ",
  "Invite created": "สร้างคำเชิญแล้ว",
  "Invite link": "ลิงก์คำเชิญ",
  "Invite revoked.": "เพิกถอนคำเชิญแล้ว",
  "Invite sent": "ส่งคำเชิญแล้ว",
  "Invite sent.": "ส่งคำเชิญแล้ว",
  "Invited": "เชิญแล้ว",
  "Loading accounts": "กำลังโหลดบัญชี",
  "Loading the waitlist": "กำลังโหลดรายชื่อรอ",
  "Marked declined.": "ทำเครื่องหมายว่าปฏิเสธแล้ว",
  "Mode": "โหมด",
  "No active accounts yet.": "ยังไม่มีบัญชีที่ใช้งานอยู่",
  "No owner": "ไม่มีเจ้าของ",
  "No projects match that search.": "ไม่พบโปรเจกต์ที่ตรงกับการค้นหานี้",
  "Nobody is on the waitlist yet.": "ยังไม่มีใครอยู่ในรายชื่อรอ",
  "Resend invite": "ส่งคำเชิญอีกครั้ง",
  "Revoke": "เพิกถอน",
  "Search for a project by name to find its owner.": "ค้นหาโปรเจกต์ด้วยชื่อเพื่อดูเจ้าของ",
  "Search projects by name": "ค้นหาโปรเจกต์ด้วยชื่อ",
  "Search projects by name...": "ค้นหาโปรเจกต์ด้วยชื่อ...",
  "Searching projects": "กำลังค้นหาโปรเจกต์",
  "Showing the most recent accounts only.": "แสดงเฉพาะบัญชีล่าสุดเท่านั้น",
  "Showing the most recent entries only.": "แสดงเฉพาะรายการล่าสุดเท่านั้น",
  "Showing the most recent matching projects only.": "แสดงเฉพาะโปรเจกต์ที่ตรงกันล่าสุดเท่านั้น",
  "Something went wrong. Try again in a moment.": "เกิดปัญหาบางอย่าง ลองอีกครั้งในอีกสักครู่",
  "Status": "สถานะ",
  "The invite email could not be sent. Share this link with {email} directly.": "ส่งอีเมลคำเชิญไม่สำเร็จ ให้ส่งลิงก์นี้ถึง {email} โดยตรง",
  "View as": "ดูในมุมของ",
  "Ban": "แบน",
  "Unban": "ยกเลิกการแบน",
  "Banned": "ถูกแบน",
  "Banned accounts": "บัญชีที่ถูกแบน",
  "Loading banned accounts": "กำลังโหลดบัญชีที่ถูกแบน",
  "Could not load banned accounts. Try again in a moment.": "โหลดบัญชีที่ถูกแบนไม่ได้ ลองอีกครั้งในอีกสักครู่",
  "Nobody is banned.": "ยังไม่มีใครถูกแบน",
  "Banned accounts cannot sign in to SV Cloud, the CLI, or MCP. Their apps keep running.": "บัญชีที่ถูกแบนจะเข้าสู่ระบบ SV Cloud, CLI หรือ MCP ไม่ได้ แต่แอปของเขายังทำงานต่อไป",
  "Ban {email}?": "แบน {email} ใช่ไหม",
  "They are signed out everywhere and cannot sign in again until you unban them. Their apps keep running.": "เขาจะถูกออกจากระบบทุกที่ และเข้าสู่ระบบใหม่ไม่ได้จนกว่าคุณจะยกเลิกการแบน แอปของเขายังทำงานต่อไป",
  "Only admins see this.": "ผู้ดูแลเท่านั้นที่เห็นข้อความนี้",
  "{email} is banned.": "แบน {email} แล้ว",
  "{email} can sign in again.": "{email} เข้าสู่ระบบได้อีกครั้งแล้ว",
  "Waiting": "กำลังรอ",

  /* screens/auth */
  "A booking page for my restaurant.": "หน้าจองโต๊ะสำหรับร้านอาหารของฉัน",
  "Allow {client}": "อนุญาต {client}",
  "Already have an account?": "มีบัญชีอยู่แล้วใช่ไหม",
  "Back to sign in": "กลับไปเข้าสู่ระบบ",
  "Back to SV Cloud": "กลับไปที่ SV Cloud",
  "By continuing you agree to our terms of service and our privacy notice.": "การดำเนินการต่อถือว่าคุณยอมรับเงื่อนไขการให้บริการและประกาศความเป็นส่วนตัวของเรา",
  "Check your email to finish setting up.": "ตรวจอีเมลของคุณเพื่อตั้งค่าให้เสร็จ",
  "Checking this sign-in request": "กำลังตรวจสอบคำขอเข้าสู่ระบบนี้",
  "Checking your invite": "กำลังตรวจคำเชิญของคุณ",
  "Checking your session": "กำลังตรวจเซสชันของคุณ",
  "Choose a password for this account.": "ตั้งรหัสผ่านสำหรับบัญชีนี้",
  "Connecting your Loop account": "กำลังเชื่อมต่อบัญชี Loop ของคุณ",
  "Continue with GitHub": "ดำเนินการต่อด้วย GitHub",
  "Continue with Loop": "ดำเนินการต่อด้วย Loop",
  "Could not complete sign-in. Ask the CLI to start over.": "เข้าสู่ระบบไม่สำเร็จ ให้ CLI เริ่มใหม่อีกครั้ง",
  "Create account": "สร้างบัญชี",
  "Create an account": "สร้างบัญชี",
  "Create my account": "สร้างบัญชีของฉัน",
  "Create your SV Cloud account": "สร้างบัญชี SV Cloud ของคุณ",
  "Email address": "ที่อยู่อีเมล",
  "Everyone": "ทุกคน",
  "Finishing sign-in": "กำลังเข้าสู่ระบบให้เสร็จ",
  "Invited automatically.": "เชิญโดยอัตโนมัติ",
  "Join SV Academy": "เข้าร่วม SV Academy",
  "Join the SV Cloud waitlist": "ลงชื่อรอใช้งาน SV Cloud",
  "Joining...": "กำลังเข้าร่วม...",
  "Let {client} access your account?": "ให้ {client} เข้าถึงบัญชีของคุณไหม",
  "New here?": "เพิ่งมาที่นี่ใช่ไหม",
  "Not now": "ยังไม่ใช่ตอนนี้",
  "Not ready for an account?": "ยังไม่พร้อมสมัครบัญชีใช่ไหม",
  "One sentence is enough. We read them, and they shape who we invite first.": "ประโยคเดียวก็พอ เราอ่านทุกข้อความ และมันมีผลต่อลำดับคนที่เราเชิญก่อน",
  "Opening GitHub": "กำลังเปิด GitHub",
  "Optional": "ไม่บังคับ",
  "or": "หรือ",
  "Resend in {n} seconds": "ส่งใหม่ได้ในอีก {n} วินาที",
  "Resend in 1 second": "ส่งใหม่ได้ในอีก 1 วินาที",
  "Send the link again": "ส่งลิงก์อีกครั้ง",
  "Set up your SV Cloud account": "ตั้งค่าบัญชี SV Cloud ของคุณ",
  "Sign in to SV Cloud": "เข้าสู่ระบบ SV Cloud",
  "Sign in with password": "เข้าสู่ระบบด้วยรหัสผ่าน",
  "Signing in": "กำลังเข้าสู่ระบบ",
  "Something went wrong connecting to Loop. Try again in a moment.": "การเชื่อมต่อกับ Loop มีปัญหา ลองอีกครั้งในอีกสักครู่",
  "SV Academy Graduates": "ผู้จบหลักสูตร SV Academy",
  "SV Cloud is not open to everybody yet. This is the order we are opening it in.": "SV Cloud ยังไม่เปิดให้ทุกคน นี่คือลำดับที่เรากำลังทยอยเปิด",
  "SV Cloud is open to Loop admins and coaches right now. By continuing you agree to our terms of service and our privacy notice.": "ตอนนี้ SV Cloud เปิดให้ผู้ดูแลและโค้ชของ Loop เท่านั้น การดำเนินการต่อถือว่าคุณยอมรับเงื่อนไขการให้บริการและประกาศความเป็นส่วนตัวของเรา",
  "SV Cloud isn't open to your account yet.": "SV Cloud ยังไม่เปิดให้บัญชีของคุณ",
  "The link works once and it expires in an hour. You can close this page.": "ลิงก์ใช้ได้ครั้งเดียวและหมดอายุในหนึ่งชั่วโมง คุณปิดหน้านี้ได้",
  "This invite link has already been used.": "ลิงก์คำเชิญนี้ถูกใช้ไปแล้ว",
  "This invite link has expired. Ask an admin to send a new one.": "ลิงก์คำเชิญนี้หมดอายุแล้ว ขอให้ผู้ดูแลส่งลิงก์ใหม่",
  "This invite link is no longer valid. Ask an admin to send a new one.": "ลิงก์คำเชิญนี้ใช้ไม่ได้แล้ว ขอให้ผู้ดูแลส่งลิงก์ใหม่",
  "This invite link is not valid.": "ลิงก์คำเชิญนี้ไม่ถูกต้อง",
  "This sign-in link is not valid. Ask the CLI to start over.": "ลิงก์เข้าสู่ระบบนี้ไม่ถูกต้อง ให้ CLI เริ่มใหม่อีกครั้ง",
  "Unable to join waitlist. Please try again.": "ลงชื่อรอไม่สำเร็จ กรุณาลองอีกครั้ง",
  "Use a different email address": "ใช้อีเมลอื่น",
  "Waitlist, now": "รายชื่อรอ ตอนนี้",
  "We are not holding web addresses. You pick yours when you make your first project, and you can change it later.": "เราไม่ได้จองที่อยู่เว็บไว้ให้ คุณเลือกของคุณเองตอนสร้างโปรเจกต์แรก และเปลี่ยนภายหลังได้",
  "We invite in small waves while we finish billing.": "เราเชิญเป็นระลอกเล็กๆ ระหว่างที่ทำระบบเรียกเก็บเงินให้เสร็จ",
  "We keep your email to invite you and for nothing else.": "เราเก็บอีเมลของคุณไว้เพื่อส่งคำเชิญเท่านั้น ไม่ใช้ทำอย่างอื่น",
  "We sent a link to {email}.": "เราส่งลิงก์ไปที่ {email} แล้ว",
  "We'll email you at {email} when it's your turn. See you soon.": "เราจะส่งอีเมลถึงคุณที่ {email} เมื่อถึงคิวของคุณ แล้วพบกันเร็วๆ นี้",
  "What do you want to build?": "คุณอยากสร้างอะไร",
  "When the doors open.": "เมื่อเปิดให้เข้าใช้",
  "Who gets in, and when": "ใครได้เข้าใช้ และเมื่อไร",
  "Withdraw any time at hello@sv-academy.org.": "ถอนความยินยอมได้ทุกเมื่อที่ hello@sv-academy.org",
  "You didn't finish signing in with GitHub. Nothing has changed on your account.": "คุณเข้าสู่ระบบด้วย GitHub ไม่เสร็จ ไม่มีอะไรในบัญชีของคุณเปลี่ยนแปลง",
  "You didn't finish signing in with Loop. Nothing has changed on your account.": "คุณเข้าสู่ระบบด้วย Loop ไม่เสร็จ ไม่มีอะไรในบัญชีของคุณเปลี่ยนแปลง",
  "You're on the list.": "คุณอยู่ในรายชื่อแล้ว",

  /* screens/graduation */
  "{host} keeps working. It is the same address, on the same day.": "{host} ยังใช้งานได้ตามเดิม ที่อยู่เดิม วันเดิม",
  "{host}, the same address": "{host} ที่อยู่เดิม",
  "{n} a month": "{n} ต่อเดือน",
  "{project} is ready to be yours": "{project} พร้อมเป็นของคุณแล้ว",
  "{project} is still running at its address, and this link still works until {date}.": "{project} ยังทำงานอยู่ที่ที่อยู่เดิม และลิงก์นี้ยังใช้ได้ถึง {date}",
  "{project} is still running, and it is still yours to claim.": "{project} ยังทำงานอยู่ และยังรอให้คุณรับเป็นเจ้าของ",
  "A claim link works once. If it was you, sign in and {project} is there.": "ลิงก์รับสิทธิ์ใช้ได้ครั้งเดียว ถ้าเป็นคุณเอง ให้เข้าสู่ระบบแล้ว {project} จะอยู่ที่นั่น",
  "Actually, I want it": "เปลี่ยนใจ ฉันเอา",
  "All right, nothing has been moved.": "เรียบร้อย ไม่มีอะไรถูกย้าย",
  "Arriving from {from}": "มาจาก {from}",
  "Ask for a new link": "ขอลิงก์ใหม่",
  "Before the claim link expires": "ก่อนลิงก์รับสิทธิ์หมดอายุ",
  "Claim link expires": "ลิงก์รับสิทธิ์หมดอายุ",
  "Cleanup date": "วันเก็บกวาด",
  "Every time you push to main, this updates automatically. That has not changed either.": "ทุกครั้งที่คุณพุชขึ้น main สิ่งนี้จะอัปเดตเองอัตโนมัติ ข้อนี้ก็ไม่เปลี่ยนเช่นกัน",
  "Go back": "ย้อนกลับ",
  "Go to your project": "ไปที่โปรเจกต์ของคุณ",
  "I don't want this project": "ฉันไม่ต้องการโปรเจกต์นี้",
  "If nobody claims it, the cleanup removes this project on {date} and {host} stops working.": "ถ้าไม่มีใครรับสิทธิ์ การเก็บกวาดจะลบโปรเจกต์นี้ในวันที่ {date} และ {host} จะหยุดทำงาน",
  "If you do nothing, the school's cleanup removes this project on {date}. Your code stays in GitHub.": "ถ้าคุณไม่ทำอะไร การเก็บกวาดของโรงเรียนจะลบโปรเจกต์นี้ในวันที่ {date} โค้ดของคุณยังอยู่ใน GitHub",
  "It has been answering here the whole time.": "มันตอบอยู่ที่นี่มาตลอด",
  "It may have been cut short when it was copied. Open the link from your email again, and if that does not work, ask for a new one.": "ลิงก์อาจถูกตัดตอนคัดลอก ลองเปิดลิงก์จากอีเมลของคุณอีกครั้ง ถ้ายังไม่ได้ให้ขอลิงก์ใหม่",
  "It may have been deleted, or the address may have a typo in it.": "อาจถูกลบไปแล้ว หรือที่อยู่อาจพิมพ์ผิด",
  "Its web address, its database, its files and its code all stay exactly as they are. What changes is the name on it.": "ที่อยู่เว็บ ฐานข้อมูล ไฟล์ และโค้ดของมันยังคงเดิมทั้งหมด สิ่งที่เปลี่ยนคือชื่อเจ้าของ",
  "Looking up your project": "กำลังค้นหาโปรเจกต์ของคุณ",
  "Move {project} to your account": "ย้าย {project} ไปยังบัญชีของคุณ",
  "Move it to my account": "ย้ายไปยังบัญชีของฉัน",
  "Moving {project} to your account": "กำลังย้าย {project} ไปยังบัญชีของคุณ",
  "None": "ไม่มี",
  "Nothing about your app changes. It does not go offline, even for a second.": "ไม่มีอะไรในแอปของคุณเปลี่ยน และมันไม่ออฟไลน์แม้แต่วินาทีเดียว",
  "Opening your project": "กำลังเปิดโปรเจกต์ของคุณ",
  "Owner": "เจ้าของ",
  "Project is removed": "โปรเจกต์ถูกลบ",
  "Same app, now yours.": "แอปเดิม ตอนนี้เป็นของคุณ",
  "Same web address. Same data. Nothing to move.": "ที่อยู่เว็บเดิม ข้อมูลเดิม ไม่มีอะไรต้องย้าย",
  "Sign in instead": "เข้าสู่ระบบแทน",
  "That project is not here.": "ไม่มีโปรเจกต์นั้นที่นี่",
  "The two dates": "สองวันที่",
  "The version that is running right now": "เวอร์ชันที่กำลังทำงานอยู่ตอนนี้",
  "They are different dates, and every message names both.": "เป็นคนละวันกัน และทุกข้อความจะระบุทั้งสองวัน",
  "This is {project}, the app you built. It stays exactly where it is.": "นี่คือ {project} แอปที่คุณสร้าง มันอยู่ที่เดิมทุกอย่าง",
  "This is a new account and it belongs to you. It does not have to use the email address you built the project with.": "นี่คือบัญชีใหม่และเป็นของคุณ ไม่จำเป็นต้องใช้อีเมลเดียวกับที่คุณใช้ตอนสร้างโปรเจกต์",
  "This link expired on {date}.": "ลิงก์นี้หมดอายุเมื่อ {date}",
  "This link expires on {date}.": "ลิงก์นี้หมดอายุวันที่ {date}",
  "This link isn't a claim link.": "ลิงก์นี้ไม่ใช่ลิงก์รับสิทธิ์",
  "This page is a comp sheet. It is not linked from anywhere in the product.": "หน้านี้เป็นแผ่นตัวอย่างงานออกแบบ ไม่มีลิงก์มาจากที่ใดในผลิตภัณฑ์",
  "This project has already been claimed.": "โปรเจกต์นี้ถูกรับสิทธิ์ไปแล้ว",
  "This takes a few seconds. Your app stays up throughout.": "ใช้เวลาไม่กี่วินาที แอปของคุณยังออนไลน์ตลอด",
  "Three messages go out before a project is removed. These are comps of them, not the product: two are email, and the last one is also a notice in the place the project was built.": "มีข้อความสามฉบับส่งออกก่อนโปรเจกต์ถูกลบ นี่คือตัวอย่างงานออกแบบ ไม่ใช่ตัวผลิตภัณฑ์ สองฉบับเป็นอีเมล และฉบับสุดท้ายเป็นประกาศในที่ที่โปรเจกต์ถูกสร้างขึ้นด้วย",
  "We couldn't finish moving {project}": "เราย้าย {project} ไม่สำเร็จ",
  "We've been told about this and will finish it. You don't need to do anything.": "เราได้รับแจ้งเรื่องนี้แล้วและจะจัดการให้เสร็จ คุณไม่ต้องทำอะไร",
  "What comes with it": "อะไรติดมาด้วย",
  "What did not change": "อะไรไม่เปลี่ยน",
  "What happens if you do nothing": "จะเกิดอะไรขึ้นถ้าคุณไม่ทำอะไร",
  "You": "คุณ",
  "Your code stays in GitHub.": "โค้ดของคุณยังอยู่ใน GitHub",
  "Your code stays in GitHub. We don't touch your repository.": "โค้ดของคุณยังอยู่ใน GitHub เราไม่แตะรีโพซิทอรีของคุณ",
  "Your database and everything in it": "ฐานข้อมูลของคุณและทุกอย่างในนั้น",
  "Your database comes with it, and everything in it.": "ฐานข้อมูลของคุณมาด้วย พร้อมทุกอย่างในนั้น",
  "Your deploy history": "ประวัติการดีพลอยของคุณ",
  "Your files": "ไฟล์ของคุณ",
  "Your files come with it.": "ไฟล์ของคุณมาด้วย",
  "Your GitHub repository": "รีโพซิทอรี GitHub ของคุณ",
  "Your keys and settings": "คีย์และการตั้งค่าของคุณ",
  "Your keys come with it, and your deploy history.": "คีย์ของคุณมาด้วย พร้อมประวัติการดีพลอย",
  "Your plan sets the limits on the project. Everything else is the same.": "แพลนของคุณเป็นตัวกำหนดขีดจำกัดของโปรเจกต์ นอกนั้นเหมือนเดิมทั้งหมด",
  "Your project": "โปรเจกต์ของคุณ",
  "Your project is safe and still running at {host}.": "โปรเจกต์ของคุณปลอดภัยและยังทำงานอยู่ที่ {host}",

  /* screens/marketing */
  "A Thai version of this page is on the way. Until it lands, write to us in Thai and we will answer in Thai.": "หน้านี้ฉบับภาษาไทยกำลังจะมา ระหว่างนี้เขียนหาเราเป็นภาษาไทยได้ แล้วเราจะตอบเป็นภาษาไทย",
  "Ask us to delete it.": "แจ้งให้เราลบได้",
  "Built for AI agents, not engineers.": "สร้างมาเพื่อเอเจนต์ AI ไม่ใช่วิศวกร",
  "Correct anything that is wrong.": "แก้ไขส่วนที่ไม่ถูกต้องได้",
  "deployed {n} minutes ago": "ดีพลอยเมื่อ {n} นาทีก่อน",
  "deployed {n} seconds ago": "ดีพลอยเมื่อ {n} วินาทีก่อน",
  "deployed a minute ago": "ดีพลอยเมื่อหนึ่งนาทีก่อน",
  "deployed a second ago": "ดีพลอยเมื่อหนึ่งวินาทีก่อน",
  "Every screen uses the word on the left. Nothing in the product will ever say a word on the right, and there is no glossary to go and learn first.": "ทุกหน้าจอใช้คำทางซ้าย ไม่มีส่วนใดของผลิตภัณฑ์ที่จะใช้คำทางขวา และไม่มีอภิธานศัพท์ที่ต้องไปเรียนก่อน",
  "Give your app a name": "ตั้งชื่อให้แอปของคุณ",
  "How long we keep it": "เราเก็บไว้นานแค่ไหน",
  "I already have an account": "ฉันมีบัญชีอยู่แล้ว",
  "If we change what we collect, we change this page before we change the product, and we put the new date at the top.": "ถ้าเราเปลี่ยนสิ่งที่เก็บ เราจะแก้หน้านี้ก่อนแก้ผลิตภัณฑ์ และใส่วันที่ใหม่ไว้ด้านบน",
  "If we get it wrong, you can complain to the Office of the Personal Data Protection Committee in Bangkok. You do not have to ask us first.": "ถ้าเราทำผิดพลาด คุณร้องเรียนต่อสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคลที่กรุงเทพฯ ได้ โดยไม่ต้องถามเราก่อน",
  "Last updated": "อัปเดตล่าสุด",
  "More to Come...": "ยังมีอีก...",
  "None of it follows you to other sites, and none of it is sent to us. There are no third party cookies on this page today. If that ever has to change, this page changes first and we ask you before we set one.": "ไม่มีข้อมูลใดตามคุณไปยังเว็บอื่น และไม่มีข้อมูลใดถูกส่งมาให้เรา วันนี้หน้านี้ไม่มีคุกกี้ของบุคคลที่สาม ถ้าวันหนึ่งต้องเปลี่ยน หน้านี้จะเปลี่ยนก่อน และเราจะถามคุณก่อนวางคุกกี้",
  "Privacy": "ความเป็นส่วนตัว",
  "Put your app on the internet, and know that it's fine.": "นำแอปของคุณขึ้นอินเทอร์เน็ต และรู้ได้ว่ามันปกติดี",
  "See what we hold about you.": "ดูว่าเราเก็บอะไรเกี่ยวกับคุณไว้บ้าง",
  "Seven words, and then you know the product.": "เจ็ดคำ แล้วคุณก็รู้จักผลิตภัณฑ์นี้",
  "Site": "เว็บไซต์",
  "Small enough that we can watch every first deploy and fix what goes wrong. Leave your email and we will tell you when your turn comes.": "เล็กพอที่เราจะดูการดีพลอยครั้งแรกของทุกคนและแก้สิ่งที่ผิดพลาดได้ ทิ้งอีเมลไว้ แล้วเราจะแจ้งเมื่อถึงคิวของคุณ",
  "So the page opens in the light or dark screen you picked.": "เพื่อให้หน้าเว็บเปิดในโหมดสว่างหรือมืดตามที่คุณเลือกไว้",
  "So the tour does not start over every time you come back.": "เพื่อไม่ให้ทัวร์เริ่มใหม่ทุกครั้งที่คุณกลับมา",
  "So we can tell you when your turn comes. It is the only thing we send to this list.": "เพื่อให้เราแจ้งคุณได้เมื่อถึงคิว เป็นสิ่งเดียวที่เราส่งไปยังรายชื่อนี้",
  "So we stop asking.": "เพื่อให้เราเลิกถามซ้ำ",
  "SV Cloud gives your code a web address, a database and file storage. Then it tells you, in words you already use, whether any of it is working.": "SV Cloud ให้ที่อยู่เว็บ ฐานข้อมูล และพื้นที่เก็บไฟล์แก่โค้ดของคุณ แล้วบอกคุณด้วยคำที่คุณใช้อยู่แล้วว่าทุกอย่างทำงานดีไหม",
  "SV Cloud is made by Silicon Valley Innovation Co., Ltd., the company behind SV Academy, in Bangkok, Thailand.": "SV Cloud จัดทำโดยบริษัท ซิลิคอน วัลเลย์ อินโนเวชั่น จำกัด บริษัทเบื้องหลัง SV Academy ที่กรุงเทพมหานคร ประเทศไทย",
  "SV Cloud is not open to everybody yet, so today the waitlist form is the only place this site asks you for anything.": "SV Cloud ยังไม่เปิดให้ทุกคน วันนี้ฟอร์มลงชื่อรอจึงเป็นที่เดียวที่เว็บนี้ขอข้อมูลจากคุณ",
  "Take back your consent. It stops there and then, and it does not undo what we did while we had it.": "ถอนความยินยอมของคุณได้ ผลจะหยุดทันที แต่ไม่ย้อนคืนสิ่งที่เราทำไปแล้วระหว่างที่ยังมีความยินยอม",
  "Talk to a person": "คุยกับเจ้าหน้าที่",
  "Tell us what you want done and which email address you used. We answer within 30 days, which is what the law gives us, and usually a lot sooner.": "บอกเราว่าคุณต้องการให้ทำอะไรและใช้อีเมลใด เราตอบภายใน 30 วันตามที่กฎหมายกำหนด และมักจะเร็วกว่านั้นมาก",
  "Three things, and they sit in your own browser's local storage on your own machine.": "สามอย่าง และทั้งหมดอยู่ในที่เก็บข้อมูลของเบราว์เซอร์บนเครื่องของคุณเอง",
  "To make your account, sign you in, and email you about your own projects.": "เพื่อสร้างบัญชีของคุณ ให้คุณเข้าสู่ระบบ และส่งอีเมลถึงคุณเรื่องโปรเจกต์ของคุณเอง",
  "Two things, and one of them does not exist yet.": "สองอย่าง และหนึ่งในนั้นยังไม่มีอยู่จริง",
  "Under Thailand's Personal Data Protection Act we are the data controller for this site. That means we decide what gets collected here, and we are the ones you hold to it.": "ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคลของประเทศไทย เราเป็นผู้ควบคุมข้อมูลของเว็บนี้ หมายความว่าเราเป็นผู้กำหนดว่าจะเก็บอะไรที่นี่ และเราคือผู้ที่คุณเรียกร้องรับผิดชอบได้",
  "Under the PDPA you can ask us to:": "ภายใต้ PDPA คุณขอให้เราดำเนินการต่อไปนี้ได้:",
  "Until you join SV Cloud, or until you ask us to delete it. Whichever comes first.": "จนกว่าคุณจะเข้าใช้ SV Cloud หรือจนกว่าคุณจะขอให้เราลบ แล้วแต่ว่าอย่างใดถึงก่อน",
  "We are letting people in a few at a time.": "เราทยอยเปิดให้ทีละไม่กี่คน",
  "We do not build a picture of you out of what you read here.": "เราไม่ประกอบภาพตัวตนของคุณจากสิ่งที่คุณอ่านที่นี่",
  "We do not sell your details, and we do not trade them.": "เราไม่ขายข้อมูลของคุณ และไม่นำไปแลกเปลี่ยน",
  "We do not show ads. Not here, not anywhere else.": "เราไม่แสดงโฆษณา ไม่ที่นี่ และไม่ที่ไหนเลย",
  "We need it to give you the thing you asked us for.": "เราต้องใช้ข้อมูลนี้เพื่อให้สิ่งที่คุณขอมา",
  "We put no third party trackers on this site. No analytics script, no advertising pixel, no social widget.": "เราไม่วางตัวติดตามของบุคคลที่สามบนเว็บนี้ ไม่มีสคริปต์วิเคราะห์ ไม่มีพิกเซลโฆษณา ไม่มีวิดเจ็ตโซเชียล",
  "What SV Cloud did": "SV Cloud ทำอะไรไป",
  "What SV Cloud does": "SV Cloud ทำอะไร",
  "What we do not do": "สิ่งที่เราไม่ทำ",
  "What we keep": "เราเก็บอะไรไว้บ้าง",
  "What we keep, why we are allowed to keep it, and how to get rid of it.": "เราเก็บอะไรไว้ ทำไมเราถึงเก็บได้ และจะลบทิ้งได้อย่างไร",
  "What your browser keeps": "เบราว์เซอร์ของคุณเก็บอะไรไว้",
  "When something breaks, you get a fix request to paste.": "เมื่อมีอะไรพัง คุณจะได้คำขอให้แก้ไว้คัดลอกไปวาง",
  "When this page changes": "เมื่อหน้านี้มีการเปลี่ยนแปลง",
  "While your account is open. Close the account and we delete it.": "ตลอดเวลาที่บัญชีของคุณยังเปิดอยู่ ปิดบัญชีแล้วเราจะลบทิ้ง",
  "Who we are": "เราคือใคร",
  "Why we may keep it": "ทำไมเราถึงเก็บไว้ได้",
  "Write to us and a person answers. It is not a ticket queue.": "เขียนหาเราแล้วจะมีคนตอบ ไม่ใช่คิวตั๋วอัตโนมัติ",
  "You can clear all of it from your browser at any time. The site keeps working. It just forgets your settings.": "คุณล้างทั้งหมดออกจากเบราว์เซอร์ได้ทุกเมื่อ เว็บยังใช้งานได้ตามปกติ เพียงแต่จะลืมการตั้งค่าของคุณ",
  "You said yes on the form. You can take that back whenever you like.": "คุณกดยินยอมไว้ในฟอร์ม และถอนได้เมื่อไรก็ได้",
  "Your answer to the notice at the bottom of the page": "คำตอบของคุณต่อประกาศที่ท้ายหน้า",
  "Your app tells you whether it is fine.": "แอปของคุณบอกคุณเองว่ามันปกติดีไหม",
  "Your email address and sign in details, once the service opens": "ที่อยู่อีเมลและข้อมูลการเข้าสู่ระบบของคุณ เมื่อบริการเปิดใช้",
  "Your email address, if you join the waitlist": "ที่อยู่อีเมลของคุณ หากคุณลงชื่อรอ",
  "Your place in the guided tour": "ตำแหน่งของคุณในทัวร์แนะนำ",
  "Your project is Live": "โปรเจกต์ของคุณออนไลน์แล้ว",
  "Your rights": "สิทธิของคุณ",
  "Your theme": "ธีมของคุณ",

  /* screens/onboarding */
  "{project} is live": "{project} ออนไลน์แล้ว",
  "{project} is almost live": "{project} ใกล้ออนไลน์แล้ว",
  "Your app is built. Its web address starts working in a few minutes, and this page will say when.": "แอปของคุณสร้างเสร็จแล้ว ที่อยู่เว็บจะเริ่มใช้งานได้ในอีกไม่กี่นาที และหน้านี้จะแจ้งให้ทราบเมื่อพร้อม",
  "{repo} already has files in it. SV Cloud starts from an empty repository so it can add the starter files without touching your work. Pick an empty one, or let us make a new repository for you.": "{repo} มีไฟล์อยู่แล้ว SV Cloud เริ่มจากรีโพว่างเปล่าเพื่อให้เพิ่มไฟล์ตั้งต้นได้โดยไม่แตะงานของคุณ เลือกรีโพว่าง หรือให้เราสร้างรีโพใหม่ให้",
  "{repo} already has files in it. SV Cloud will add its starter files in one new commit on top of what's there, nothing existing is touched or force-pushed.": "{repo} มีไฟล์อยู่แล้ว SV Cloud จะเพิ่มไฟล์ตั้งต้นเป็นคอมมิตใหม่หนึ่งครั้งทับบนของเดิม ไม่มีอะไรถูกแตะต้องหรือถูก force-push",
  "1. Install SV Cloud CLI (Mandatory)": "1. ติดตั้ง SV Cloud CLI (จำเป็น)",
  "2. Clone repository": "2. โคลนรีโพซิทอรี",
  "3. Prompt for your coding agent": "3. พรอมต์สำหรับเอเจนต์เขียนโค้ดของคุณ",
  "Already connected": "เชื่อมต่อแล้ว",
  "Available": "ว่างอยู่",
  "Checking": "กำลังตรวจสอบ",
  "Checking your GitHub connection...": "กำลังตรวจการเชื่อมต่อ GitHub ของคุณ...",
  "Choose repositories on GitHub": "เลือกรีโพซิทอรีบน GitHub",
  "CLI install and sign-in command": "คำสั่งติดตั้งและเข้าสู่ระบบ CLI",
  "Clone command": "คำสั่งโคลน",
  "Connect": "เชื่อมต่อ",
  "Connecting as {handle}": "กำลังเชื่อมต่อในชื่อ {handle}",
  "Connecting to GitHub": "กำลังเชื่อมต่อ GitHub",
  "Connecting…": "กำลังเชื่อมต่อ…",
  "Continue": "ดำเนินการต่อ",
  "Create it": "สร้างเลย",
  "Empty": "ว่าง",
  "Every time you push to main, this updates automatically.": "ทุกครั้งที่คุณพุชขึ้น main สิ่งนี้จะอัปเดตเองอัตโนมัติ",
  "Files we'll commit": "ไฟล์ที่เราจะคอมมิตให้",
  "Finish setting up": "ตั้งค่าให้เสร็จ",
  "Full setup prompt": "พรอมต์ตั้งค่าแบบครบ",
  "Where should your code live?": "โค้ดของคุณควรอยู่ที่ไหน",
  "Store code with SV Cloud": "เก็บโค้ดไว้กับ SV Cloud",
  "Store code with GitHub": "เก็บโค้ดไว้กับ GitHub",
  "Easiest": "ง่ายที่สุด",
  "Nothing else to sign up for. You can move it to GitHub later.": "ไม่ต้องสมัครอะไรเพิ่ม และย้ายไป GitHub ทีหลังได้",
  "For people who already use GitHub.": "สำหรับคนที่ใช้ GitHub อยู่แล้ว",
  "Storing code with SV Cloud is not available yet. Continue with GitHub instead.": "การเก็บโค้ดไว้กับ SV Cloud ยังใช้ไม่ได้ในตอนนี้ ให้ดำเนินการต่อด้วย GitHub แทน",
  "Project prompt": "พรอมต์ของโปรเจกต์",
  "That's the last thing you need this page for. Your agent can do everything else.": "นี่เป็นสิ่งสุดท้ายที่คุณต้องใช้หน้านี้ เอเจนต์ของคุณทำที่เหลือทั้งหมดได้",
  "First time on this computer? Set it up once and your agent can reach SV Cloud.": "ใช้คอมพิวเตอร์เครื่องนี้ครั้งแรกใช่ไหม ตั้งค่าครั้งเดียว แล้วเอเจนต์ของคุณจะเข้าถึง SV Cloud ได้",
  "Set up your computer": "ตั้งค่าคอมพิวเตอร์ของคุณ",
  "This one prompt also sets up this computer, so there is nothing to do first.": "พรอมต์เดียวนี้ตั้งค่าคอมพิวเตอร์เครื่องนี้ให้ด้วย จึงไม่มีอะไรต้องทำก่อน",
  "Where your code lives": "ที่เก็บโค้ดของคุณ",
  "Show the setup prompt": "แสดงพรอมต์ตั้งค่า",
  "Computer setup prompt": "พรอมต์ตั้งค่าคอมพิวเตอร์",
  "Paste this into your coding agent once. It installs what it needs and signs in for you.": "วางข้อความนี้ให้เอเจนต์เขียนโค้ดของคุณเพียงครั้งเดียว มันจะติดตั้งสิ่งที่ต้องใช้และลงชื่อเข้าใช้ให้คุณ",
  "It will open your browser once. Click Allow there. It will ask before anything needs your password.": "มันจะเปิดเบราว์เซอร์ของคุณหนึ่งครั้ง ให้กดอนุญาตที่นั่น และมันจะถามก่อนที่จะต้องใช้รหัสผ่านของคุณ",
  "I've done this": "ฉันทำแล้ว",
  "Git clone command": "คำสั่ง git clone",
  "GitHub CLI clone command": "คำสั่งโคลนด้วย GitHub CLI",
  "GitHub isn't responding right now. This is on their side, not yours.": "ตอนนี้ GitHub ไม่ตอบสนอง เป็นที่ฝั่งเขา ไม่ใช่ฝั่งคุณ",
  "GitHub status": "สถานะ GitHub",
  "Has files": "มีไฟล์อยู่",
  "I don't have a repository yet": "ฉันยังไม่มีรีโพซิทอรี",
  "In Simple mode this always follows the name of your app.": "ในโหมดพื้นฐาน ส่วนนี้จะตามชื่อแอปของคุณเสมอ",
  "It does all three steps below. Or do them yourself:": "พรอมต์นี้ทำครบทั้งสามขั้นด้านล่าง หรือจะทำเองก็ได้",
  "Looking for connected repositories...": "กำลังค้นหารีโพซิทอรีที่เชื่อมต่อไว้...",
  "Lost track of the setup. Reload this page to check on it.": "ติดตามการตั้งค่าไม่ได้แล้ว โหลดหน้านี้ใหม่เพื่อตรวจสอบ",
  "Make a new repository instead": "สร้างรีโพซิทอรีใหม่แทน",
  "Manage which repositories SV Cloud can see": "จัดการว่ารีโพซิทอรีใดที่ SV Cloud มองเห็นได้",
  "My recipe site": "เว็บสูตรอาหารของฉัน",
  "Name your app": "ตั้งชื่อแอปของคุณ",
  "Nothing has been set up yet, so nothing needs undoing.": "ยังไม่มีการตั้งค่าใด จึงไม่มีอะไรต้องย้อนคืน",
  "One template exists today. More will not change what Simple mode does.": "วันนี้มีเทมเพลตเดียว การมีเพิ่มจะไม่เปลี่ยนสิ่งที่โหมดพื้นฐานทำ",
  "Paste this into your coding agent": "วางข้อความนี้ให้เอเจนต์เขียนโค้ดของคุณ",
  "Pick a repository": "เลือกรีโพซิทอรี",
  "Pick a repository instead": "เลือกรีโพซิทอรีแทน",
  "Putting your app on the internet.": "กำลังนำแอปของคุณขึ้นอินเทอร์เน็ต",
  "Search your repositories": "ค้นหารีโพซิทอรีของคุณ",
  "Setting up my SV Cloud project failed: {error}": "การตั้งค่าโปรเจกต์ SV Cloud ของฉันล้มเหลว: {error}",
  "Setting up your app": "กำลังตั้งค่าแอปของคุณ",
  "Setting up your project": "กำลังตั้งค่าโปรเจกต์ของคุณ",
  "Setup didn't finish.": "การตั้งค่าไม่เสร็จ",
  "Setup prompt": "พรอมต์ตั้งค่า",
  "Show the build output": "แสดงผลลัพธ์การบิลด์",
  "Something went wrong connecting your project. Try again.": "การเชื่อมต่อโปรเจกต์ของคุณมีปัญหา ลองอีกครั้ง",
  "Something went wrong while setting up your project.": "เกิดปัญหาระหว่างตั้งค่าโปรเจกต์ของคุณ",
  "Start over": "เริ่มใหม่ทั้งหมด",
  "Starter template": "เทมเพลตตั้งต้น",
  "SV Cloud can't see any repositories yet.": "SV Cloud ยังมองไม่เห็นรีโพซิทอรีใดเลย",
  "SV Cloud needs access to one GitHub repository. It only sees the repositories you pick.": "SV Cloud ต้องเข้าถึงรีโพซิทอรี GitHub หนึ่งรายการ และเห็นเฉพาะรีโพซิทอรีที่คุณเลือกเท่านั้น",
  "That address is reserved. Try one with your project's name in it.": "ที่อยู่นั้นถูกสงวนไว้ ลองใช้ที่อยู่ที่มีชื่อโปรเจกต์ของคุณอยู่ด้วย",
  "That GitHub connection is no longer linked to your account. Reconnect and try again.": "การเชื่อมต่อ GitHub นั้นไม่ได้ผูกกับบัญชีของคุณแล้ว เชื่อมต่อใหม่แล้วลองอีกครั้ง",
  "That one is taken, What about:": "ชื่อนั้นถูกใช้แล้ว ลองชื่อนี้ไหม:",
  "That web address was just taken. Go back and pick another name.": "ที่อยู่เว็บนั้นเพิ่งถูกใช้ไป ย้อนกลับไปเลือกชื่ออื่น",
  "This account is already using all the active projects it may have.":
    "บัญชีนี้ใช้โปรเจกต์ที่ใช้งานอยู่ครบจำนวนที่มีได้แล้ว",
  "This is taking longer than usual. Setup keeps running even if you close this page. Check your project page for the result.": "ใช้เวลานานกว่าปกติ การตั้งค่ายังทำงานต่อแม้คุณปิดหน้านี้ ดูผลได้ที่หน้าโปรเจกต์ของคุณ",
  "This page needs a project to watch. Start a new project from the beginning.": "หน้านี้ต้องมีโปรเจกต์ให้เฝ้าดู เริ่มโปรเจกต์ใหม่ตั้งแต่ต้น",
  "This repository has no branches yet. We will create this one.": "รีโพซิทอรีนี้ยังไม่มีแบรนช์ เราจะสร้างแบรนช์นี้ให้",
  "This repository is already connected to a different project.": "รีโพซิทอรีนี้เชื่อมกับโปรเจกต์อื่นอยู่แล้ว",
  "unknown error": "ข้อผิดพลาดที่ไม่ทราบสาเหตุ",
  "Use a different account": "ใช้บัญชีอื่น",
  "We committed your starter code. If your repository was empty, it's building right now and this page will say so when it's live.": "เราคอมมิตโค้ดตั้งต้นของคุณแล้ว ถ้ารีโพซิทอรีของคุณว่างเปล่า ตอนนี้มันกำลังสร้างอยู่ และหน้านี้จะแจ้งเมื่อออนไลน์",
  "We set up a database and file storage for you. Your app is already connected to both.": "เราตั้งค่าฐานข้อมูลและพื้นที่เก็บไฟล์ให้คุณแล้ว แอปของคุณเชื่อมกับทั้งสองอย่างเรียบร้อย",
  "We'll add starter files to {repo} and deploy it.": "เราจะเพิ่มไฟล์ตั้งต้นลงใน {repo} แล้วดีพลอยให้",
  "We'll create a new repository called {repo} on your GitHub account and put the starter files in it.": "เราจะสร้างรีโพซิทอรีใหม่ชื่อ {repo} บนบัญชี GitHub ของคุณ แล้วใส่ไฟล์ตั้งต้นลงไป",
  "We'll make one for you": "เราจะสร้างให้คุณ",
  "You can choose which ones we see at any time, and change it later.": "คุณเลือกได้ว่าจะให้เราเห็นอันไหน และเปลี่ยนภายหลังได้",
  "Your database and file storage are ready. Only the build didn't finish. One file calls a function named formatServings, but the file that defines it spells it formatServing, so the build stopped before your app could go online.": "ฐานข้อมูลและพื้นที่เก็บไฟล์ของคุณพร้อมแล้ว มีเพียงการบิลด์ที่ไม่เสร็จ ไฟล์หนึ่งเรียกฟังก์ชันชื่อ formatServings แต่ไฟล์ที่ประกาศไว้สะกดว่า formatServing การบิลด์จึงหยุดก่อนที่แอปของคุณจะขึ้นออนไลน์ได้",
  "Your first build is running": "การสร้างครั้งแรกของคุณกำลังทำงาน",
  "Your repositories": "รีโพซิทอรีของคุณ",
  "Your web address": "ที่อยู่เว็บของคุณ",

  /* screens/project */
  "...": "...",
  "{address} stops working and becomes available for someone else to use.": "{address} จะหยุดทำงานและเปิดให้คนอื่นนำไปใช้ได้",
  "{column}, largest first": "{column} มากไปน้อย",
  "{column}, smallest first": "{column} น้อยไปมาก",
  "{n} bytes": "{n} ไบต์",
  "{n} changes saved.": "บันทึก {n} รายการแล้ว",
  "{n} errors": "{n} ข้อผิดพลาด",
  "{n} errors stopped after your last deploy.": "{n} ข้อผิดพลาดหยุดไปหลังการดีพลอยครั้งล่าสุดของคุณ",
  "{n} files": "{n} ไฟล์",
  "{n} files didn't upload": "อัปโหลดไม่สำเร็จ {n} ไฟล์",
  "{n} keys": "{n} คีย์",
  "{n} of them match what you picked.": "{n} รายการตรงกับที่คุณเลือก",
  "{n} rows": "{n} แถว",
  "{n} rows deleted.": "ลบแล้ว {n} แถว",
  "{n} rows in {ms} ms": "{n} แถว ใน {ms} มิลลิวินาที",
  "{n} rows match this filter": "{n} แถวตรงกับตัวกรองนี้",
  "{n} tables": "{n} ตาราง",
  "{n}+ files": "{n}+ ไฟล์",
  "{name} is managed by SV Cloud, so it cannot be replaced here.": "{name} ดูแลโดย SV Cloud จึงเปลี่ยนที่นี่ไม่ได้",
  "{name} was not deleted. Try again.": "ลบ {name} ไม่สำเร็จ ลองอีกครั้ง",
  "{name} was not saved. Try adding it again.": "บันทึก {name} ไม่สำเร็จ ลองเพิ่มอีกครั้ง",
  "{visits} visits, {errors} errors": "{visits} การเข้าชม, {errors} ข้อผิดพลาด",
  "1 byte": "1 ไบต์",
  "1 change saved.": "บันทึก 1 รายการแล้ว",
  "1 day": "1 วัน",
  "1 error": "1 ข้อผิดพลาด",
  "1 error stopped after your last deploy.": "1 ข้อผิดพลาดหยุดไปหลังการดีพลอยครั้งล่าสุดของคุณ",
  "1 file": "1 ไฟล์",
  "1 file didn't upload": "อัปโหลดไม่สำเร็จ 1 ไฟล์",
  "1 hour": "1 ชั่วโมง",
  "1 key": "1 คีย์",
  "1 of them matches what you picked.": "1 รายการตรงกับที่คุณเลือก",
  "1 row": "1 แถว",
  "1 row deleted.": "ลบแล้ว 1 แถว",
  "1 row in {ms} ms": "1 แถว ใน {ms} มิลลิวินาที",
  "1 row matches this filter": "1 แถวตรงกับตัวกรองนี้",
  "1 table": "1 ตาราง",
  "7 days": "7 วัน",
  "A key called {name} already exists. Adding it again replaces the old one.": "มีคีย์ชื่อ {name} อยู่แล้ว การเพิ่มซ้ำจะแทนที่ของเดิม",
  "A newer deploy didn't work.": "ดีพลอยที่ใหม่กว่าไม่สำเร็จ",
  "A visit is one request your app answered. One person usually makes several.": "การเข้าชมคือหนึ่งคำขอที่แอปของคุณตอบ คนหนึ่งคนมักทำหลายครั้ง",
  "Account usage": "การใช้งานของบัญชี",
  "Add a key": "เพิ่มคีย์",
  "Add key": "เพิ่มคีย์",
  "Add row": "เพิ่มแถว",
  "Added {when}": "เพิ่มเมื่อ {when}",
  "Added by {who}": "เพิ่มโดย {who}",
  "Added by {who}, {when}": "เพิ่มโดย {who} เมื่อ {when}",
  "All files": "ไฟล์ทั้งหมด",
  "Always on": "เปิดอยู่เสมอ",
  "Anyone with the link can open this file": "ใครก็ตามที่มีลิงก์เปิดไฟล์นี้ได้",
  "Anyone with this link can open the file until it stops working.": "ใครก็ตามที่มีลิงก์นี้เปิดไฟล์ได้จนกว่าลิงก์จะหมดอายุ",
  "App name": "ชื่อแอป",
  "Apply": "ใช้งาน",
  "At the limit": "ถึงขีดจำกัดแล้ว",
  "Backup file": "ไฟล์สำรอง",
  "Browsing only, for now": "ตอนนี้ดูได้อย่างเดียว",
  "Build identifier": "รหัสบิลด์",
  "Build log": "ล็อกการบิลด์",
  "build minutes": "นาทีบิลด์",
  "Build output": "ผลลัพธ์การบิลด์",
  "Build your app": "สร้างแอปของคุณ",
  "Can be empty": "เว้นว่างได้",
  "Change the time range or the path below to see the rest.": "เปลี่ยนช่วงเวลาหรือพาธด้านล่างเพื่อดูส่วนที่เหลือ",
  "Changed": "เปลี่ยนแล้ว",
  "changed {when}": "เปลี่ยนเมื่อ {when}",
  "Checking whether your app is fine": "กำลังตรวจว่าแอปของคุณปกติดีไหม",
  "Cleaning up. This takes a few seconds and you can leave this page.": "กำลังเก็บกวาด ใช้เวลาไม่กี่วินาที และคุณออกจากหน้านี้ได้",
  "Clear": "ล้าง",
  "Clear the filter": "ล้างตัวกรอง",
  "Clone repository": "โคลนรีโพซิทอรี",
  "Clone your repository and let your coding agent complete the setup.": "โคลนรีโพซิทอรีของคุณ แล้วให้เอเจนต์เขียนโค้ดตั้งค่าให้เสร็จ",
  "Close to the limit": "ใกล้ถึงขีดจำกัด",
  "Column": "คอลัมน์",
  "Coming later": "จะมาภายหลัง",
  "Command and Enter runs it.": "Command และ Enter เพื่อสั่งทำงาน",
  "Common keys": "คีย์ที่ใช้บ่อย",
  "compute": "การประมวลผล",
  "Connected": "เชื่อมต่อแล้ว",
  "Connecting to your app": "กำลังเชื่อมต่อกับแอปของคุณ",
  "Connection": "การเชื่อมต่อ",
  "Connection details": "รายละเอียดการเชื่อมต่อ",
  "contains": "มีคำว่า",
  "Content type": "ชนิดเนื้อหา",
  "Control and Enter runs it.": "Control และ Enter เพื่อสั่งทำงาน",
  "Counted at": "นับเมื่อ",
  "Created": "สร้างเมื่อ",
  "CSV": "CSV",
  "CSV file": "ไฟล์ CSV",
  "database": "ฐานข้อมูล",
  "Delete": "ลบ",
  "Delete \"{first}\" and \"{second}\"?": "ลบ \"{first}\" และ \"{second}\" ไหม",
  "Delete \"{name}\"?": "ลบ \"{name}\" ไหม",
  "Delete {n} rows": "ลบ {n} แถว",
  "Delete {n} rows?": "ลบ {n} แถวไหม",
  "Delete {name}": "ลบ {name}",
  "Delete {name}?": "ลบ {name} ไหม",
  "Delete {project}": "ลบ {project}",
  "Delete {project}?": "ลบ {project} ไหม",
  "Delete 1 row": "ลบ 1 แถว",
  "Delete 1 row?": "ลบ 1 แถวไหม",
  "Delete anyway": "ยืนยันลบ",
  "Delete this project": "ลบโปรเจกต์นี้",
  "Deleted until your next deploy.": "ถูกลบไปจนกว่าจะดีพลอยครั้งถัดไป",
  "Deploy history": "ประวัติการดีพลอย",
  "Each column is one day.": "แต่ละแท่งคือหนึ่งวัน",
  "Each column is one hour.": "แต่ละแท่งคือหนึ่งชั่วโมง",
  "Editing rows works on a bigger screen.": "การแก้ไขแถวใช้ได้บนหน้าจอที่ใหญ่กว่านี้",
  "Empty row": "แถวว่าง",
  "Errors that stopped": "ข้อผิดพลาดที่หยุดไปแล้ว",
  "Errors your app is returning": "ข้อผิดพลาดที่แอปของคุณส่งกลับ",
  "Errors, up to {n} in a column": "ข้อผิดพลาด สูงสุด {n} ต่อแท่ง",
  "Every other row in this table stays as it is.": "แถวอื่นทั้งหมดในตารางนี้ยังคงเดิม",
  "Every table in one file. Open it in a spreadsheet.": "ทุกตารางในไฟล์เดียว เปิดด้วยโปรแกรมสเปรดชีตได้",
  "Everything": "ทั้งหมด",
  "Everything here is on your activity page too, in plain language.": "ทุกอย่างที่นี่อยู่ในหน้าความเคลื่อนไหวของคุณด้วย ในภาษาที่เข้าใจง่าย",
  "Everything in it is deleted. This can't be undone.": "ทุกอย่างในนั้นจะถูกลบ และย้อนกลับไม่ได้",
  "Everything in your database, in one file another database can read.": "ทุกอย่างในฐานข้อมูลของคุณ ในไฟล์เดียวที่ฐานข้อมูลอื่นอ่านได้",
  "Everything your app has already saved stays as it is.": "ทุกอย่างที่แอปของคุณบันทึกไว้แล้วยังคงเดิม",
  "Exact size": "ขนาดที่แน่นอน",
  "EXAMPLES": "ตัวอย่าง",
  "Export": "ส่งออก",
  "Extra things your app can use. Switching one on connects it to your app automatically.": "สิ่งเพิ่มเติมที่แอปของคุณใช้ได้ เปิดใช้แล้วระบบจะเชื่อมเข้ากับแอปของคุณให้อัตโนมัติ",
  "failed after {n} seconds": "ล้มเหลวหลังผ่านไป {n} วินาที",
  "failed after 1 second": "ล้มเหลวหลังผ่านไป 1 วินาที",
  "File deleted.": "ลบไฟล์แล้ว",
  "File in your code": "ไฟล์ในโค้ดของคุณ",
  "File path": "พาธไฟล์",
  "file storage": "พื้นที่เก็บไฟล์",
  "Files can be up to {cap}.": "ไฟล์มีขนาดได้ไม่เกิน {cap}",
  "Files in this folder": "ไฟล์ในโฟลเดอร์นี้",
  "Filter": "ตัวกรอง",
  "Filter is on": "ตัวกรองเปิดอยู่",
  "Folder": "โฟลเดอร์",
  "Folders": "โฟลเดอร์",
  "Got it": "เข้าใจแล้ว",
  "Hide the value": "ซ่อนค่า",
  "History": "ประวัติ",
  "How far back": "ย้อนหลังแค่ไหน",
  "How we count": "เรานับอย่างไร",
  "If you need a different address, make a new project with the name you want and connect the same repository to it.": "ถ้าคุณต้องการที่อยู่อื่น ให้สร้างโปรเจกต์ใหม่ด้วยชื่อที่ต้องการ แล้วเชื่อมรีโพซิทอรีเดิมเข้ากับมัน",
  "If your app still asks for this file, visitors get an error where it used to be.": "ถ้าแอปของคุณยังเรียกไฟล์นี้อยู่ ผู้เข้าชมจะเจอข้อผิดพลาดตรงที่ไฟล์เคยอยู่",
  "is": "เท่ากับ",
  "is less than": "น้อยกว่า",
  "is more than": "มากกว่า",
  "It is running. These are the limits it has room for.": "มันทำงานอยู่ นี่คือขีดจำกัดที่มันมีพื้นที่รองรับ",
  "It ran. Nothing in this table matched it.": "คำสั่งทำงานแล้ว แต่ไม่มีอะไรในตารางนี้ตรงกับเงื่อนไข",
  "Kept for {n} months": "เก็บไว้ {n} เดือน",
  "Keys": "คีย์",
  "Kind": "ประเภท",
  "Last {n} days": "{n} วันล่าสุด",
  "Last 24 hours": "24 ชั่วโมงล่าสุด",
  "Last 30 days": "30 วันล่าสุด",
  "Last 7 days": "7 วันล่าสุด",
  "Last checked just now": "ตรวจล่าสุดเมื่อสักครู่",
  "Last picked up by deploy {sha}": "ถูกนำไปใช้ล่าสุดโดยดีพลอย {sha}",
  "Leave it switched on": "เปิดใช้งานไว้ตามเดิม",
  "Let's get started": "เริ่มกันเลย",
  "Link that expires": "ลิงก์ที่หมดอายุได้",
  "Live deploy": "ดีพลอยที่ใช้งานอยู่",
  "Live logs are an Advanced detail.": "ล็อกสดเป็นรายละเอียดในโหมดขั้นสูง",
  "Load more": "โหลดเพิ่ม",
  "Loading live deploy": "กำลังโหลดดีพลอยที่ใช้งานอยู่",
  "Loading the version your app is running": "กำลังโหลดเวอร์ชันที่แอปของคุณกำลังใช้",
  "Loading what happened": "กำลังโหลดสิ่งที่เกิดขึ้น",
  "Loading your add-ons": "กำลังโหลดส่วนเสริมของคุณ",
  "Loading your database": "กำลังโหลดฐานข้อมูลของคุณ",
  "Loading your deploys": "กำลังโหลดการดีพลอยของคุณ",
  "Loading your files": "กำลังโหลดไฟล์ของคุณ",
  "Loading your keys": "กำลังโหลดคีย์ของคุณ",
  "Loading your project": "กำลังโหลดโปรเจกต์ของคุณ",
  "Loading your rows": "กำลังโหลดแถวของคุณ",
  "Loading your settings": "กำลังโหลดการตั้งค่าของคุณ",
  "Loading your tables": "กำลังโหลดตารางของคุณ",
  "Loading your traffic": "กำลังโหลดผู้เข้าชมของคุณ",
  "Locked": "ล็อกอยู่",
  "Looking for errors": "กำลังค้นหาข้อผิดพลาด",
  "Lost track of the deletion. Reload this page to check on it.": "ติดตามการลบไม่ได้แล้ว โหลดหน้านี้ใหม่เพื่อตรวจสอบ",
  "Managed by SV Cloud": "ดูแลโดย SV Cloud",
  "Managed by SV Cloud. Your app is connected to your database with this.": "ดูแลโดย SV Cloud แอปของคุณเชื่อมกับฐานข้อมูลด้วยสิ่งนี้",
  "Past your allowance. Extra use is paid from your credits, and nothing is limited.": "เกินโควตาแล้ว การใช้งานส่วนเกินจะหักจากเครดิตของคุณ และไม่มีการจำกัดการใช้งาน",
  "Over the limit. Storage past the allowance is not charged yet, and nothing is limited.": "เกินขีดจำกัดแล้ว พื้นที่จัดเก็บที่เกินโควตายังไม่มีการคิดค่าบริการ และไม่มีการจำกัดการใช้งาน",
  "No": "ไม่",
  "No errors": "ไม่มีข้อผิดพลาด",
  "No errors in the last {n} days.": "ไม่มีข้อผิดพลาดใน {n} วันที่ผ่านมา",
  "No errors in the last 24 hours.": "ไม่มีข้อผิดพลาดใน 24 ชั่วโมงที่ผ่านมา",
  "No errors in this window.": "ไม่มีข้อผิดพลาดในช่วงเวลานี้",
  "No files": "ไม่มีไฟล์",
  "no files yet": "ยังไม่มีไฟล์",
  "No files yet": "ยังไม่มีไฟล์",
  "No rows match this filter.": "ไม่มีแถวใดตรงกับตัวกรองนี้",
  "No rows yet": "ยังไม่มีแถว",
  "no tables yet": "ยังไม่มีตาราง",
  "No tables yet": "ยังไม่มีตาราง",
  "Nobody has visited yet.": "ยังไม่มีใครเข้าชม",
  "Nobody has visited your app yet.": "ยังไม่มีใครเข้าชมแอปของคุณ",
  "Nobody has visited your apps yet.": "ยังไม่มีใครเข้าชมแอปของคุณ",
  "None of them match what you picked.": "ไม่มีรายการใดตรงกับที่คุณเลือก",
  "Not switched on": "ยังไม่ได้เปิดใช้",
  "Nothing has come through yet. Lines appear here the first time somebody opens your app.": "ยังไม่มีอะไรเข้ามา บรรทัดจะปรากฏที่นี่ครั้งแรกที่มีคนเปิดแอปของคุณ",
  "Nothing has deployed yet.": "ยังไม่มีการดีพลอย",
  "Nothing in this folder yet.": "ยังไม่มีอะไรในโฟลเดอร์นี้",
  "Nothing is live yet, so nothing about your web address changed.": "ยังไม่มีอะไรออนไลน์ ที่อยู่เว็บของคุณจึงไม่มีอะไรเปลี่ยน",
  "Nothing is live yet. Nobody has been charged and nothing was left half-built.": "ยังไม่มีอะไรออนไลน์ ไม่มีการเรียกเก็บเงิน และไม่มีอะไรค้างสร้างครึ่งทาง",
  "Nothing is wrong with your app. Try again in a moment.": "แอปของคุณไม่ได้มีปัญหา ลองอีกครั้งในอีกสักครู่",
  "Nothing matches what you picked.": "ไม่มีอะไรตรงกับที่คุณเลือก",
  "Numbers are estimates, counted from a sample and multiplied up": "ตัวเลขเป็นค่าประมาณ นับจากตัวอย่างแล้วคูณขึ้น",
  "Numbers are exact": "ตัวเลขเป็นค่าที่แน่นอน",
  "Off by default, because a shaded row can read as a selected one.": "ปิดไว้เป็นค่าเริ่มต้น เพราะแถวที่มีแรเงาอาจดูเหมือนแถวที่ถูกเลือก",
  "Once you save a key, we can't show it to you again. If you lose it, replace it with a new one.": "เมื่อบันทึกคีย์แล้ว เราแสดงให้คุณดูอีกไม่ได้ ถ้าทำหาย ให้แทนที่ด้วยคีย์ใหม่",
  "OpenAI key": "คีย์ OpenAI",
  "Passwords and keys your app needs. Your app can read these. We can't.": "รหัสผ่านและคีย์ที่แอปของคุณต้องใช้ แอปของคุณอ่านได้ เราอ่านไม่ได้",
  "Pause": "หยุดชั่วคราว",
  "Pause this project": "หยุดโปรเจกต์นี้ชั่วคราว",
  "Pausing": "กำลังหยุดชั่วคราว",
  "Turning it back on": "กำลังเปิดกลับคืน",
  "That didn't work. Try again.": "ทำไม่สำเร็จ ลองอีกครั้ง",
  "This project is paused because your account used up its allowance. Your web address shows a holding page until you add credits, change your plan, or the allowance resets.": "โปรเจกต์นี้หยุดชั่วคราวเพราะบัญชีของคุณใช้โควตาหมดแล้ว ที่อยู่เว็บของคุณจะแสดงหน้าพักไว้จนกว่าคุณจะเติมเครดิต เปลี่ยนแพลน หรือโควตารีเซ็ต",
  "This project is already paused because your account used up its allowance, so there's nothing to turn on here.": "โปรเจกต์นี้หยุดชั่วคราวอยู่แล้วเพราะบัญชีของคุณใช้โควตาหมด จึงไม่มีอะไรให้เปิดจากหน้านี้",
  "Plan · resets in {n} days": "แพลน · รีเซ็ตในอีก {n} วัน",
  "Plan · resets today": "แพลน · รีเซ็ตวันนี้",
  "Plan · resets tomorrow": "แพลน · รีเซ็ตพรุ่งนี้",
  "Preparing your export": "กำลังเตรียมไฟล์ส่งออกของคุณ",
  "Primary key": "คีย์หลัก",
  "Project ID": "รหัสโปรเจกต์",
  "Not counted": "ยังไม่ได้นับ",
  "Prompt for your coding agent": "พรอมต์สำหรับเอเจนต์เขียนโค้ดของคุณ",
  "Public link": "ลิงก์สาธารณะ",
  "Count the database and files again": "นับฐานข้อมูลและไฟล์ใหม่อีกครั้ง",
  "Counting the database and files again.": "กำลังนับฐานข้อมูลและไฟล์ใหม่อีกครั้ง",
  "Could not count again. The numbers below are the last ones taken.": "นับใหม่ไม่สำเร็จ ตัวเลขด้านล่างคือตัวเลขล่าสุดที่นับไว้",
  "Database and files counted {when}.": "นับฐานข้อมูลและไฟล์เมื่อ {when}",
  "Put the newest version back": "นำเวอร์ชันใหม่ที่สุดกลับมา",
  "Put this version back": "นำเวอร์ชันนี้กลับมา",
  "Put this version back?": "นำเวอร์ชันนี้กลับมาไหม",
  "Putting this version back.": "กำลังนำเวอร์ชันนี้กลับมา",
  "Queries you run will show up here.": "คำสั่งค้นที่คุณเรียกใช้จะแสดงที่นี่",
  "Query results": "ผลลัพธ์คำสั่งค้น",
  "Quick links": "ลิงก์ด่วน",
  "Refresh": "รีเฟรช",
  "Redeploy now": "ดีพลอยใหม่เดี๋ยวนี้",
  "Remove": "นำออก",
  "Replace": "แทนที่",
  "Replace key": "แทนที่คีย์",
  "Resend key": "ส่งคีย์อีกครั้ง",
  "Restarting your app": "กำลังรีสตาร์ทแอปของคุณ",
  "Route prefix": "คำนำหน้าเส้นทาง",
  "Row {id}": "แถว {id}",
  "Row 1 of 1": "แถว 1 จาก 1",
  "Rows 1 to {last} of {total}": "แถว 1 ถึง {last} จาก {total}",
  "Rows appear here when your app writes to this table.": "แถวจะปรากฏที่นี่เมื่อแอปของคุณเขียนลงตารางนี้",
  "Run": "เรียกใช้",
  "Run it anyway": "ยืนยันเรียกใช้",
  "Running a query is an Advanced detail.": "การเรียกใช้คำสั่งค้นเป็นรายละเอียดในโหมดขั้นสูง",
  "Schema": "สคีมา",
  "See all activity": "ดูความเคลื่อนไหวทั้งหมด",
  "See all deploys": "ดูการดีพลอยทั้งหมด",
  "See it against your plan limits": "ดูเทียบกับขีดจำกัดของแพลนคุณ",
  "See what's taking space": "ดูว่าอะไรกินพื้นที่",
  "See your apps": "ดูแอปของคุณ",
  "Settings are readable. Keys are not.": "การตั้งค่าอ่านได้ คีย์อ่านไม่ได้",
  "Setup didn't finish": "การตั้งค่าไม่เสร็จ",
  "Shade every other row": "แรเงาแถวเว้นแถว",
  "Show": "แสดง",
  "Show more": "แสดงเพิ่ม",
  "Show the build behind each deploy": "แสดงการบิลด์เบื้องหลังแต่ละดีพลอย",
  "Show the connection": "แสดงการเชื่อมต่อ",
  "Show the schema and run a query": "แสดงสคีมาและเรียกใช้คำสั่งค้น",
  "Show the settings your app can read": "แสดงการตั้งค่าที่แอปของคุณอ่านได้",
  "Show the value": "แสดงค่า",
  "Show what we keep, and for how long": "แสดงว่าเราเก็บอะไรไว้ และนานแค่ไหน",
  "Showing the first {n} files.": "แสดง {n} ไฟล์แรก",
  "Showing the last {n} lines.": "แสดง {n} บรรทัดล่าสุด",
  "Sign in to OpenAI, open your account, then API keys.": "เข้าสู่ระบบ OpenAI เปิดหน้าบัญชีของคุณ แล้วไปที่ API keys",
  "Sign in to Resend, then open API keys.": "เข้าสู่ระบบ Resend แล้วเปิด API keys",
  "Sign in to Stripe, open Developers, then API keys.": "เข้าสู่ระบบ Stripe เปิด Developers แล้วไปที่ API keys",
  "Size": "ขนาด",
  "So we can tell you how many visits there were, not how many people.": "เพื่อให้เราบอกได้ว่ามีการเข้าชมกี่ครั้ง ไม่ใช่มีกี่คน",
  "Something went wrong deleting this project.": "การลบโปรเจกต์นี้มีปัญหา",
  "Something went wrong. Try again.": "เกิดปัญหาบางอย่าง ลองอีกครั้ง",
  "Sort": "เรียงลำดับ",
  "SQL": "SQL",
  "Start again": "เริ่มใหม่",
  "Started by": "เริ่มโดย",
  "Stops working after": "หยุดทำงานหลังจาก",
  "Streaming": "กำลังสตรีม",
  "Stripe key": "คีย์ Stripe",
  "Switch it off": "ปิดใช้งาน",
  "Switch off your {addOn}?": "ปิด {addOn} ของคุณไหม",
  "Switching it on connects it to your app and tells your agent about it.": "การเปิดใช้จะเชื่อมเข้ากับแอปของคุณและแจ้งให้เอเจนต์ของคุณรู้",
  "Table": "ตาราง",
  "Test": "ทดสอบ",
  "That deploy didn't work either. Nothing changed on your live site.": "ดีพลอยนั้นก็ไม่สำเร็จเช่นกัน ไม่มีอะไรบนเว็บที่ออนไลน์อยู่เปลี่ยนแปลง",
  "That is about the same as the 24 hours before.": "ใกล้เคียงกับ 24 ชั่วโมงก่อนหน้า",
  "That is about the same as the 30 days before.": "ใกล้เคียงกับ 30 วันก่อนหน้า",
  "That is about the same as the 7 days before.": "ใกล้เคียงกับ 7 วันก่อนหน้า",
  "That is fewer than the 24 hours before.": "น้อยกว่า 24 ชั่วโมงก่อนหน้า",
  "That is fewer than the 30 days before.": "น้อยกว่า 30 วันก่อนหน้า",
  "That is fewer than the 7 days before.": "น้อยกว่า 7 วันก่อนหน้า",
  "That is more than the 24 hours before.": "มากกว่า 24 ชั่วโมงก่อนหน้า",
  "That is more than the 30 days before.": "มากกว่า 30 วันก่อนหน้า",
  "That is more than the 7 days before.": "มากกว่า 7 วันก่อนหน้า",
  "That name couldn't be saved. Try again.": "บันทึกชื่อนั้นไม่สำเร็จ ลองอีกครั้ง",
  "That query came back with no rows.": "คำสั่งค้นนั้นไม่คืนแถวใดเลย",
  "That worked. Your app is running the new version.": "สำเร็จแล้ว แอปของคุณกำลังใช้เวอร์ชันใหม่",
  "The 24 hours before: {n} visits": "24 ชั่วโมงก่อนหน้า: {n} การเข้าชม",
  "The 30 days before: {n} visits": "30 วันก่อนหน้า: {n} การเข้าชม",
  "The 7 days before: {n} visits": "7 วันก่อนหน้า: {n} การเข้าชม",
  "The build": "การบิลด์",
  "The build behind each deploy": "การบิลด์เบื้องหลังแต่ละดีพลอย",
  "The columns in this table": "คอลัมน์ในตารางนี้",
  "The last 24 hours.": "24 ชั่วโมงที่ผ่านมา",
  "The last 30 days.": "30 วันที่ผ่านมา",
  "The last 7 days.": "7 วันที่ผ่านมา",
  "The last lines of the build log are in the fix request, so your coding agent can read them and work it out. Nothing changed on your live site.": "บรรทัดท้ายของล็อกการบิลด์อยู่ในคำขอให้แก้ เอเจนต์เขียนโค้ดของคุณจึงอ่านและแก้ต่อได้ ไม่มีอะไรบนเว็บที่ออนไลน์อยู่เปลี่ยนแปลง",
  "The order your app wrote them": "ลำดับที่แอปของคุณเขียนไว้",
  "The request that hit it, and where it came from in your code.": "คำขอที่ทำให้เกิดขึ้น และตำแหน่งที่มาในโค้ดของคุณ",
  "The rollback did not go through. Try again.": "การย้อนเวอร์ชันไม่สำเร็จ ลองอีกครั้ง",
  "The same visits at the window you picked, with the errors underneath on their own scale.": "การเข้าชมชุดเดิมในช่วงเวลาที่คุณเลือก โดยมีข้อผิดพลาดอยู่ด้านล่างในสเกลของตัวเอง",
  "The thin lines are your deploys.": "เส้นบางคือการดีพลอยของคุณ",
  "The value is hidden": "ค่าถูกซ่อนไว้",
  "The version your app is running": "เวอร์ชันที่แอปของคุณกำลังใช้",
  "There are still rows in this table.": "ยังมีแถวอยู่ในตารางนี้",
  "There is no earlier stretch to compare with.": "ไม่มีช่วงก่อนหน้าให้เปรียบเทียบ",
  "There is no where clause, so every row is included.": "ไม่มีเงื่อนไข where ทุกแถวจึงถูกรวมทั้งหมด",
  "These are estimates, not exact counts.": "ตัวเลขเหล่านี้เป็นค่าประมาณ ไม่ใช่จำนวนที่แน่นอน",
  "These rows are deleted from {table}. This can't be undone.": "แถวเหล่านี้จะถูกลบออกจาก {table} และย้อนกลับไม่ได้",
  "They are running. These are the limits they have room for.": "แอปกำลังทำงานอยู่ นี่คือขีดจำกัดที่ยังมีพื้นที่รองรับ",
  "This app is running an older version": "แอปนี้กำลังใช้เวอร์ชันเก่ากว่า",
  "This changes every row in {table}.": "คำสั่งนี้เปลี่ยนทุกแถวใน {table}",
  "This deploy didn't work, so your app kept running the version from {date}. Nobody visiting your site noticed.": "ดีพลอยนี้ไม่สำเร็จ แอปของคุณจึงยังใช้เวอร์ชันจากวันที่ {date} ต่อไป ผู้เข้าชมเว็บของคุณไม่มีใครสังเกตเห็น",
  "This file comes from your repository at {path}. Deleting it here removes it until your next deploy, and then it comes back.": "ไฟล์นี้มาจากรีโพซิทอรีของคุณที่ {path} การลบที่นี่จะเอาออกจนกว่าจะดีพลอยครั้งถัดไป แล้วมันจะกลับมา",
  "This file comes from your repository. It comes back the next time you deploy.": "ไฟล์นี้มาจากรีโพซิทอรีของคุณ มันจะกลับมาในการดีพลอยครั้งถัดไป",
  "This file is deleted from your file storage. This can't be undone.": "ไฟล์นี้จะถูกลบออกจากพื้นที่เก็บไฟล์ของคุณ และย้อนกลับไม่ได้",
  "This is just the name you see. Your web address doesn't change.": "นี่เป็นเพียงชื่อที่คุณเห็น ที่อยู่เว็บของคุณไม่เปลี่ยน",
  "This is the version your app is running.": "นี่คือเวอร์ชันที่แอปของคุณกำลังใช้",
  "This version is old enough that we no longer keep its build. To go back to it, put that code back on your branch and push.":
    "เวอร์ชันนี้เก่าพอที่เราจะไม่เก็บไฟล์บิลด์ไว้แล้ว หากต้องการย้อนกลับไปใช้ ให้นำโค้ดนั้นกลับขึ้นแบรนช์ของคุณแล้ว push",
  "This is your first deploy.": "นี่คือการดีพลอยครั้งแรกของคุณ",
  "This project": "โปรเจกต์นี้",
  "This project is paused": "โปรเจกต์นี้หยุดชั่วคราวอยู่",
  "This project is paused, so nothing is running and nothing is being printed.": "โปรเจกต์นี้หยุดชั่วคราวอยู่ จึงไม่มีอะไรทำงานและไม่มีอะไรถูกบันทึกออกมา",
  "This prototype answers three example queries. Anything else says so.": "ต้นแบบนี้ตอบคำสั่งค้นตัวอย่างได้สามแบบ นอกนั้นจะแจ้งให้ทราบ",
  "This row is deleted from {table}. This can't be undone.": "แถวนี้จะถูกลบออกจาก {table} และย้อนกลับไม่ได้",
  "This table can't be edited here yet. Its rows are identified by more than one column.": "ตารางนี้ยังแก้ไขที่นี่ไม่ได้ เพราะแถวถูกระบุด้วยคอลัมน์มากกว่าหนึ่ง",
  "This table can't be edited here. Rows can only be changed when the table has a single column that identifies each one.": "ตารางนี้แก้ไขที่นี่ไม่ได้ แถวจะแก้ได้ก็ต่อเมื่อตารางมีคอลัมน์เดียวที่ระบุแต่ละแถว",
  "This table can't be edited here. Some of its names use characters this editor can't work with.": "ตารางนี้แก้ไขที่นี่ไม่ได้ ชื่อบางส่วนใช้อักขระที่ตัวแก้ไขนี้รองรับไม่ได้",
  "This table doesn't have any rows yet.": "ตารางนี้ยังไม่มีแถวใดเลย",
  "This table records the changes your app has made to your other tables.": "ตารางนี้บันทึกการเปลี่ยนแปลงที่แอปของคุณทำกับตารางอื่น",
  "Time range": "ช่วงเวลา",
  "To remove it for good, delete it from your code.": "หากต้องการเอาออกถาวร ให้ลบออกจากโค้ดของคุณ",
  "Today": "วันนี้",
  "Trying this deploy again": "กำลังลองดีพลอยนี้อีกครั้ง",
  "Turn this off and only your app can read it.": "ปิดสิ่งนี้แล้วจะมีเพียงแอปของคุณที่อ่านได้",
  "Turning it off would break your app the next time it deploys. Remove it from your code first, then come back.": "การปิดจะทำให้แอปของคุณพังในการดีพลอยครั้งถัดไป ให้เอาออกจากโค้ดของคุณก่อน แล้วค่อยกลับมา",
  "Type": "ชนิด",
  "Type the name to confirm": "พิมพ์ชื่อเพื่อยืนยัน",
  "Type the number of rows to confirm": "พิมพ์จำนวนแถวเพื่อยืนยัน",
  "Uploading {n} files": "กำลังอัปโหลด {n} ไฟล์",
  "Uploading {name}": "กำลังอัปโหลด {name}",
  "Uploading 1 file": "กำลังอัปโหลด 1 ไฟล์",
  "Uploading here isn't available yet. Ask your coding agent to add files instead.": "การอัปโหลดที่นี่ยังใช้ไม่ได้ ให้เอเจนต์เขียนโค้ดของคุณเพิ่มไฟล์แทน",
  "Uploads": "การอัปโหลด",
  "Uploads and images your app uses.": "ไฟล์อัปโหลดและรูปภาพที่แอปของคุณใช้",
  "Uploads are paused while you are over your file storage limit.": "การอัปโหลดถูกหยุดไว้ระหว่างที่คุณใช้พื้นที่เก็บไฟล์เกินขีดจำกัด",
  "Value": "ค่า",
  "Visits": "การเข้าชม",
  "Visits and errors": "การเข้าชมและข้อผิดพลาด",
  "Visits, one point per day": "การเข้าชม หนึ่งจุดต่อหนึ่งวัน",
  "Visits, one point per hour": "การเข้าชม หนึ่งจุดต่อหนึ่งชั่วโมง",
  "Visits, up to {n} in a column": "การเข้าชม สูงสุด {n} ต่อแท่ง",
  "Watch the live logs": "ดูล็อกสด",
  "We are building a full export that can handle any size.": "เรากำลังสร้างระบบส่งออกแบบเต็มที่รองรับได้ทุกขนาด",
  "We can only read the first {n} rows of a table at a time right now, so these tables were cut short: {tables}.": "ตอนนี้เราอ่านได้ครั้งละ {n} แถวแรกของตารางเท่านั้น ตารางเหล่านี้จึงถูกตัดสั้น: {tables}",
  "We cannot find that project.": "เราหาโปรเจกต์นั้นไม่พบ",
  "We cannot find that table.": "เราหาตารางนั้นไม่พบ",
  "We could not load your traffic.": "เราโหลดข้อมูลผู้เข้าชมของคุณไม่ได้",
  "We could not read your usage just now.": "ตอนนี้เราอ่านข้อมูลการใช้งานของคุณไม่ได้",
  "We couldn't load your add-ons. Try refreshing the page.": "เราโหลดส่วนเสริมของคุณไม่ได้ ลองโหลดหน้าใหม่",
  "We couldn't load your files. Try refreshing the page.": "เราโหลดไฟล์ของคุณไม่ได้ ลองโหลดหน้าใหม่",
  "We couldn't load your keys. Try refreshing the page.": "เราโหลดคีย์ของคุณไม่ได้ ลองโหลดหน้าใหม่",
  "We couldn't load your tables. Try refreshing the page.": "เราโหลดตารางของคุณไม่ได้ ลองโหลดหน้าใหม่",
  "We couldn't work out what went wrong from the build log.": "เราหาสาเหตุจากล็อกการบิลด์ไม่ได้",
  "We count the requests your app answers at your web address. We do not know who your visitors are, and we do not put anything in their browser.": "เรานับคำขอที่แอปของคุณตอบที่ที่อยู่เว็บของคุณ เราไม่รู้ว่าผู้เข้าชมเป็นใคร และเราไม่ใส่อะไรลงในเบราว์เซอร์ของพวกเขา",
  "We don't have a canned result for that query in this prototype.": "ต้นแบบนี้ไม่มีผลลัพธ์สำเร็จรูปสำหรับคำสั่งค้นนั้น",
  "We're building these. None of them is ready yet, and there is nothing to do here.": "เรากำลังสร้างสิ่งเหล่านี้อยู่ ยังไม่มีอันไหนพร้อม และยังไม่มีอะไรให้ทำที่นี่",
  "We're showing the first {n} rows to keep this fast.": "เราแสดง {n} แถวแรกเพื่อให้หน้าเร็ว",
  "Show more rows": "แสดงแถวเพิ่ม",
  "Loading more rows": "กำลังโหลดแถวเพิ่ม",
  "Couldn't load more rows. Try again.": "โหลดแถวเพิ่มไม่สำเร็จ ลองอีกครั้ง",
  "What happened at one moment": "เกิดอะไรขึ้นในช่วงเวลาหนึ่ง",
  "What happened lately": "เกิดอะไรขึ้นช่วงนี้",
  "What happens now": "ตอนนี้จะเกิดอะไรขึ้น",
  "What happens when you delete this": "จะเกิดอะไรขึ้นเมื่อคุณลบสิ่งนี้",
  "What is running": "อะไรกำลังทำงานอยู่",
  "What the build did": "การบิลด์ทำอะไรไป",
  "What this deploy did": "ดีพลอยนี้ทำอะไรไป",
  "What this means for your app": "สิ่งนี้หมายความว่าอย่างไรกับแอปของคุณ",
  "What we count": "เรานับอะไรบ้าง",
  "When": "เมื่อไร",
  "Where it sits": "อยู่ที่ไหน",
  "Where to get it": "หาได้จากที่ไหน",
  "Who visited {address}, and what they got.": "ใครเข้าชม {address} บ้าง และได้อะไรกลับไป",
  "Yes": "ใช่",
  "Yesterday": "เมื่อวาน",
  "You can leave this page. Setup keeps running.": "คุณออกจากหน้านี้ได้ การตั้งค่ายังทำงานต่อ",
  "You can put the newer version back at any time.": "คุณนำเวอร์ชันที่ใหม่กว่ากลับมาได้ทุกเมื่อ",
  "You can switch it back on whenever you want.": "คุณเปิดกลับคืนได้เมื่อไรก็ได้",
  "You have not used anything yet.": "คุณยังไม่ได้ใช้อะไรเลย",
  "You'll need one when your app uses another service, like sending email or taking payments.": "คุณจะต้องใช้เมื่อแอปของคุณเรียกใช้บริการอื่น เช่น การส่งอีเมลหรือการรับชำระเงิน",
  "You're over your file storage limit.": "คุณใช้พื้นที่เก็บไฟล์เกินขีดจำกัดแล้ว",
  "Your agent has been told about this.": "เอเจนต์ของคุณได้รับแจ้งเรื่องนี้แล้ว",
  "Your app": "แอปของคุณ",
  "Your app answered {n} visits in the last 24 hours.": "แอปของคุณตอบ {n} การเข้าชมใน 24 ชั่วโมงที่ผ่านมา",
  "Your app answered {n} visits in the last 30 days.": "แอปของคุณตอบ {n} การเข้าชมใน 30 วันที่ผ่านมา",
  "Your app answered {n} visits in the last 7 days.": "แอปของคุณตอบ {n} การเข้าชมใน 7 วันที่ผ่านมา",
  "Your app answered 1 visit in the last 24 hours.": "แอปของคุณตอบ 1 การเข้าชมใน 24 ชั่วโมงที่ผ่านมา",
  "Your app answered 1 visit in the last 30 days.": "แอปของคุณตอบ 1 การเข้าชมใน 30 วันที่ผ่านมา",
  "Your app answered 1 visit in the last 7 days.": "แอปของคุณตอบ 1 การเข้าชมใน 7 วันที่ผ่านมา",
  "Your app answered about {n} visits in the last 24 hours.": "แอปของคุณตอบประมาณ {n} การเข้าชมใน 24 ชั่วโมงที่ผ่านมา",
  "Your app answered about {n} visits in the last 30 days.": "แอปของคุณตอบประมาณ {n} การเข้าชมใน 30 วันที่ผ่านมา",
  "Your app answered about {n} visits in the last 7 days.": "แอปของคุณตอบประมาณ {n} การเข้าชมใน 7 วันที่ผ่านมา",
  "Your app can also save files here on its own.": "แอปของคุณบันทึกไฟล์มาไว้ที่นี่เองได้ด้วย",
  "Your app goes back to the code from {date}.": "แอปของคุณจะกลับไปใช้โค้ดจากวันที่ {date}",
  "Your app goes offline immediately.": "แอปของคุณจะออฟไลน์ทันที",
  "Your app hasn't used this yet.": "แอปของคุณยังไม่ได้ใช้สิ่งนี้",
  "Your app is not affected. This is the page, not the app.": "แอปของคุณไม่ได้รับผลกระทบ ปัญหาอยู่ที่หน้านี้ ไม่ใช่ที่แอป",
  "Your app is paused, so nothing is reaching it.": "แอปของคุณหยุดชั่วคราวอยู่ จึงไม่มีอะไรเข้าถึงมันได้",
  "Your app is running an older version on purpose. Your newest code is not live.": "แอปของคุณใช้เวอร์ชันเก่าอยู่โดยตั้งใจ โค้ดใหม่ที่สุดของคุณยังไม่ออนไลน์",
  "Your app is running. Nothing is wrong.": "แอปของคุณทำงานอยู่ ไม่มีอะไรผิดปกติ",
  "Your app is still live": "แอปของคุณยังออนไลน์อยู่",
  "Your app is still running your old keys. It picks these up on its next deploy.": "แอปของคุณยังใช้คีย์เดิมอยู่ และจะรับคีย์ใหม่ในการดีพลอยครั้งถัดไป",
  "Your app is still running your old keys. It picks up the ones on your keys page on its next deploy.": "แอปของคุณยังใช้คีย์เดิมอยู่ และจะรับคีย์ในหน้าคีย์ของคุณในการดีพลอยครั้งถัดไป",
  "Your app is too new to compare with the day before.": "แอปของคุณใหม่เกินกว่าจะเทียบกับวันก่อนหน้า",
  "Your app is too new to compare with the month before.": "แอปของคุณใหม่เกินกว่าจะเทียบกับเดือนก่อนหน้า",
  "Your app is too new to compare with the week before.": "แอปของคุณใหม่เกินกว่าจะเทียบกับสัปดาห์ก่อนหน้า",
  "Your app isn't live yet. Nothing has deployed yet.": "แอปของคุณยังไม่ออนไลน์ ยังไม่มีการดีพลอย",
  "Your app isn't live yet. This was its first deploy.": "แอปของคุณยังไม่ออนไลน์ นี่คือการดีพลอยครั้งแรกของมัน",
  "Your app keeps running and your files stay where they are. New uploads are paused until you free some space.": "แอปของคุณยังทำงานต่อและไฟล์ของคุณยังอยู่ที่เดิม การอัปโหลดใหม่ถูกหยุดไว้จนกว่าคุณจะเพิ่มพื้นที่ว่าง",
  "Your app keeps running the version from {date}.": "แอปของคุณยังใช้เวอร์ชันจากวันที่ {date} ต่อไป",
  "Your app keeps running the whole time.": "แอปของคุณทำงานต่อเนื่องตลอดเวลา",
  "Your app keeps running.": "แอปของคุณยังทำงานต่อ",
  "Your app may have renamed it, or the address may have a typo in it.": "แอปของคุณอาจเปลี่ยนชื่อไฟล์ไปแล้ว หรือที่อยู่อาจพิมพ์ผิด",
  "Your app reads it as": "แอปของคุณอ่านค่าเป็น",
  "Your app restarted and picked up your changes.": "แอปของคุณรีสตาร์ทและรับการเปลี่ยนแปลงของคุณแล้ว",
  "Your app stops running and your web address shows a holding page. Your database, your files, and your code all stay exactly as they are. New deploys wait until you turn it back on, and you can do that any time.": "แอปของคุณจะหยุดทำงานและที่อยู่เว็บจะแสดงหน้าพักไว้ ฐานข้อมูล ไฟล์ และโค้ดของคุณยังคงเดิมทั้งหมด การดีพลอยใหม่จะรอจนกว่าคุณจะเปิดกลับคืน ซึ่งทำได้ทุกเมื่อ",
  "Your app will restart to pick this up. That takes about 30 seconds.": "แอปของคุณจะรีสตาร์ทเพื่อรับค่านี้ ใช้เวลาประมาณ 30 วินาที",
  "Your app will stop being able to use it.": "แอปของคุณจะใช้สิ่งนี้ไม่ได้อีก",
  "Your app's data lives here. Your app is connected to it automatically.": "ข้อมูลของแอปคุณอยู่ที่นี่ แอปของคุณเชื่อมต่อกับมันโดยอัตโนมัติ",
  "Your app's own words, as they happen, from {address}.": "ถ้อยคำของแอปคุณเอง ตามเวลาจริง จาก {address}",
  "Your changes weren't saved": "การเปลี่ยนแปลงของคุณไม่ได้ถูกบันทึก",
  "Your code stays on GitHub. We don't touch your repository.": "โค้ดของคุณยังอยู่บน GitHub เราไม่แตะรีโพซิทอรีของคุณ",
  "Your code still uses your {addOn}.": "โค้ดของคุณยังใช้ {addOn} อยู่",
  "Your coding agent writes them in your code. This console does not.": "เอเจนต์เขียนโค้ดของคุณเป็นคนเขียนสิ่งเหล่านี้ลงในโค้ด ไม่ใช่คอนโซลนี้",
  "Your database and files are kept for {days} days, then permanently deleted. Getting them back before then means asking us, and there is no undo button yet.": "ฐานข้อมูลและไฟล์ของคุณจะถูกเก็บไว้ {days} วัน แล้วลบถาวร การขอคืนก่อนหน้านั้นต้องแจ้งเรา และยังไม่มีปุ่มย้อนคืน",
  "Your database and your files did not change.": "ฐานข้อมูลและไฟล์ของคุณไม่เปลี่ยนแปลง",
  "Your database does not change. Everything your app has saved since {date} stays exactly where it is.": "ฐานข้อมูลของคุณไม่เปลี่ยน ทุกอย่างที่แอปของคุณบันทึกไว้ตั้งแต่ {date} ยังอยู่ที่เดิม",
  "Your export does not have everything in it.": "ไฟล์ส่งออกของคุณมีข้อมูลไม่ครบ",
  "Your export is ready.": "ไฟล์ส่งออกของคุณพร้อมแล้ว",
  "Your files do not change.": "ไฟล์ของคุณไม่เปลี่ยนแปลง",
  "Your first deploy starts on its own, the next time you push to main.": "การดีพลอยครั้งแรกของคุณจะเริ่มเองในครั้งถัดไปที่คุณพุชขึ้น main",
  "Your next push to main tries again on its own.": "การพุชขึ้น main ครั้งถัดไปจะลองใหม่ให้เอง",
  "Your other files stay exactly where they are.": "ไฟล์อื่นของคุณยังอยู่ที่เดิมทั้งหมด",
  "Your query": "คำสั่งค้นของคุณ",
  "Your web address is fixed when your project is created and cannot be changed.": "ที่อยู่เว็บของคุณถูกกำหนดตอนสร้างโปรเจกต์และเปลี่ยนไม่ได้",
  "Your web address will be {address} once this finishes.": "ที่อยู่เว็บของคุณจะเป็น {address} เมื่อขั้นตอนนี้เสร็จ",

  /* screens/projects */
  "{n} projects": "{n} โปรเจกต์",
  "{n}d ago": "{n} วันก่อน",
  "{n}h ago": "{n} ชั่วโมงก่อน",
  "{n}m ago": "{n} นาทีก่อน",
  "1 project": "1 โปรเจกต์",
  "Across your projects": "รวมทุกโปรเจกต์ของคุณ",
  "Everything you have put on the internet lives here.": "ทุกอย่างที่คุณนำขึ้นอินเทอร์เน็ตอยู่ที่นี่",
  "Finish moving it in": "ย้ายเข้ามาให้เสร็จ",
  "just now": "เมื่อสักครู่",
  "Last deploy": "ดีพลอยล่าสุด",
  "Loading your projects": "กำลังโหลดโปรเจกต์ของคุณ",
  "Nothing deployed yet.": "ยังไม่มีการดีพลอย",
  "Nothing here yet. Let's get started.": "ยังไม่มีอะไรที่นี่ เริ่มกันเลย",
  "One line for each app you have put on the internet, and how it is doing.": "หนึ่งบรรทัดต่อหนึ่งแอปที่คุณนำขึ้นอินเทอร์เน็ต พร้อมสถานะของมัน",
  "Project": "โปรเจกต์",
  "Waiting for you to finish moving it in from Loop.": "กำลังรอให้คุณย้ายเข้ามาจาก Loop ให้เสร็จ",
  "Your plan, added up over every project above.": "แพลนของคุณ รวมทุกโปรเจกต์ด้านบน",

  /* Reached through t(variable): a constant table, a fixture's label, a
     status word. These never appear as t('literal') anywhere, so a sweep of
     source for literals does not find them. check-i18n.mjs resolves them from
     the property names those call sites actually read. */
  "30 days before": "30 วันก่อน",
  "48 hours before": "48 ชั่วโมงก่อน",
  "7 days before": "7 วันก่อน",
  "7 days left to claim {project}": "เหลืออีก 7 วันในการรับสิทธิ์ {project}",
  "A break in the cloud deck, dark underneath.": "ช่องว่างบนแผ่นเมฆ มืดอยู่ด้านล่าง",
  "A build failure, with the fix request already written out.": "การบิลด์ที่ล้มเหลว พร้อมคำขอให้แก้ที่เขียนไว้ให้แล้ว",
  "A commit, a green lamp, and the seconds since it deployed.": "คอมมิตหนึ่งครั้ง ไฟสีเขียว และจำนวนวินาทีตั้งแต่ดีพลอย",
  "A deploy went out this morning and it did not work.": "มีการดีพลอยเมื่อเช้านี้และไม่สำเร็จ",
  "A health band settling on everything being fine.": "แถบสุขภาพที่ลงเอยว่าทุกอย่างปกติดี",
  "A high anvil cloud bank, seen from above and far away.": "กลุ่มเมฆทั่งสูง มองจากด้านบนและระยะไกล",
  "A monthly total was read before it was worked out.": "ยอดรวมรายเดือนถูกอ่านก่อนที่จะคำนวณเสร็จ",
  "A project overview saying that nothing has gone wrong today.": "หน้าภาพรวมโปรเจกต์ที่บอกว่าวันนี้ไม่มีอะไรผิดพลาด",
  "A sentence typing itself at a prompt, asking for a recipe site.": "ประโยคที่พิมพ์ตัวเองอยู่ที่ช่องพรอมต์ ขอให้สร้างเว็บสูตรอาหาร",
  "A storm tower, seen from beside it at height.": "หอเมฆพายุ มองจากด้านข้างที่ระดับความสูงเดียวกัน",
  "A thin streak of high cloud pulled sideways by the wind.": "เมฆสูงเป็นริ้วบางถูกลมดึงไปด้านข้าง",
  "A web address with a live pill beside it, deployed seconds ago.": "ที่อยู่เว็บพร้อมป้ายออนไลน์อยู่ข้างๆ ดีพลอยไปเมื่อไม่กี่วินาทีก่อน",
  "Add, edit, and delete rows in your apps’ databases": "เพิ่ม แก้ไข และลบแถวในฐานข้อมูลของแอปคุณ",
  "Added a key: RESEND_API_KEY": "เพิ่มคีย์: RESEND_API_KEY",
  "Added a key: STRIPE_SECRET_KEY": "เพิ่มคีย์: STRIPE_SECRET_KEY",
  "An agent checking off its own plan, one item at a time.": "เอเจนต์กำลังติ๊กแผนของตัวเองทีละข้อ",
  "An error, with the fix request written out ready to copy.": "ข้อผิดพลาด พร้อมคำขอให้แก้ที่เขียนไว้ให้คัดลอกได้เลย",
  "App": "แอป",
  "Browse your apps’ databases": "เรียกดูฐานข้อมูลของแอปคุณ",
  "Build it": "สร้างเลย",
  "Change app settings, including secrets": "เปลี่ยนการตั้งค่าแอป รวมถึงข้อมูลลับ",
  "Charlie, one of the three founders, running against the light.": "ชาลี หนึ่งในสามผู้ก่อตั้ง กำลังวิ่งย้อนแสง",
  "Checking your GitHub account": "กำลังตรวจบัญชี GitHub ของคุณ",
  "Checking your app is answering": "กำลังตรวจว่าแอปของคุณตอบสนอง",
  "Claim {project}": "รับสิทธิ์ {project}",
  "Cleaning up": "กำลังเก็บกวาด",
  "Clearing the cleanup date": "กำลังล้างวันเก็บกวาด",
  "Close whatever is open": "ปิดสิ่งที่เปิดอยู่",
  "Cmd K": "Cmd K",
  "Committing your starter code": "กำลังคอมมิตโค้ดตั้งต้นของคุณ",
  "Connecting it to your app": "กำลังเชื่อมเข้ากับแอปของคุณ",
  "Create a new project, name it, connect it to GitHub and start building.": "สร้างโปรเจกต์ใหม่ ตั้งชื่อ เชื่อมกับ GitHub แล้วเริ่มสร้างได้เลย",
  "Create and delete apps": "สร้างและลบแอป",
  "Created a table: saved_recipes": "สร้างตาราง: saved_recipes",
  "Creating your database": "กำลังสร้างฐานข้อมูลของคุณ",
  "Creating your file storage": "กำลังสร้างพื้นที่เก็บไฟล์ของคุณ",
  "D": "D",
  "Deployed \"Add a monthly total to the invoice list\"": "ดีพลอย \"Add a monthly total to the invoice list\"",
  "Deployed \"Add print view to recipe pages\"": "ดีพลอย \"Add print view to recipe pages\"",
  "Deployed \"First deploy\"": "ดีพลอย \"First deploy\"",
  "Deployed \"Make the search box work on phones\"": "ดีพลอย \"Make the search box work on phones\"",
  "Deploying": "กำลังดีพลอย",
  "Email, and a notice where you built it": "อีเมล และประกาศในที่ที่คุณสร้างมันขึ้นมา",
  "Esc": "Esc",
  "Fetching your starter template": "กำลังดึงเทมเพลตตั้งต้นของคุณ",
  "File storage switched on": "เปิดใช้พื้นที่เก็บไฟล์แล้ว",
  "Finishing up": "กำลังเก็บรายละเอียดสุดท้าย",
  "Getting that version ready": "กำลังเตรียมเวอร์ชันนั้น",
  "Holds files your app keeps: images, uploads, anything a person sends you.": "เก็บไฟล์ที่แอปของคุณถือไว้ ทั้งรูปภาพ ไฟล์อัปโหลด และทุกอย่างที่คนส่งมาให้คุณ",
  "If nobody claims it, the cleanup removes this project on {cleanup}. Your code stays in GitHub.": "ถ้าไม่มีใครรับสิทธิ์ การเก็บกวาดจะลบโปรเจกต์นี้ในวันที่ {cleanup} โค้ดของคุณยังอยู่ใน GitHub",
  "If nobody claims it, the cleanup removes {project} on {cleanup} and {host} stops working. Your database and your files go with it, and that cannot be undone.": "ถ้าไม่มีใครรับสิทธิ์ การเก็บกวาดจะลบ {project} ในวันที่ {cleanup} และ {host} จะหยุดทำงาน ฐานข้อมูลและไฟล์ของคุณจะหายไปด้วย และย้อนกลับไม่ได้",
  "Image processing": "การประมวลผลรูปภาพ",
  "Install dependencies": "ติดตั้งแพ็กเกจที่ต้องใช้",
  "It takes about a minute, your app does not go offline, and your web address does not change. Your link works until {expires}.": "ใช้เวลาประมาณหนึ่งนาที แอปของคุณไม่ออฟไลน์ และที่อยู่เว็บไม่เปลี่ยน ลิงก์ของคุณใช้ได้ถึง {expires}",
  "Keys & settings": "คีย์และการตั้งค่า",
  "Last reminder: {project} comes down on {cleanup}": "เตือนครั้งสุดท้าย: {project} จะถูกปิดในวันที่ {cleanup}",
  "Lets people search everything your app has saved, without you building it yourself.": "ให้คนค้นทุกอย่างที่แอปของคุณบันทึกไว้ได้ โดยคุณไม่ต้องสร้างเอง",
  "Log lines streaming until a live web address lands.": "บรรทัดล็อกไหลผ่านจนที่อยู่เว็บที่ใช้งานได้ปรากฏขึ้น",
  "March, one of the three founders, running against the light.": "มาร์ช หนึ่งในสามผู้ก่อตั้ง กำลังวิ่งย้อนแสง",
  "Move through a list of results": "เลื่อนดูรายการผลลัพธ์",
  "Move to the next control": "ไปยังตัวควบคุมถัดไป",
  "Moving ownership": "กำลังโอนความเป็นเจ้าของ",
  "Named build steps ticking off while a bar fills.": "ขั้นตอนการบิลด์ที่มีชื่อกำกับถูกติ๊กไปทีละขั้นขณะที่แถบเต็มขึ้น",
  "Nothing has gone wrong today.": "วันนี้ไม่มีอะไรผิดพลาด",
  "One database query took too long and gave up.": "คำสั่งค้นฐานข้อมูลหนึ่งใช้เวลานานเกินไปและยอมแพ้",
  "Project created": "สร้างโปรเจกต์แล้ว",
  "Project paused": "หยุดโปรเจกต์ชั่วคราวแล้ว",
  "Prom, one of the three founders, running against the light.": "พรหม หนึ่งในสามผู้ก่อตั้ง กำลังวิ่งย้อนแสง",
  "Provisioning": "กำลังจัดเตรียม",
  "Publish": "เผยแพร่",
  "Quick navigation": "สลับไปมาอย่างรวดเร็ว",
  "Quickly jump between projects.": "สลับไปมาระหว่างโปรเจกต์ได้อย่างรวดเร็ว",
  "Read your app configuration": "อ่านการตั้งค่าของแอปคุณ",
  "Reading your repositories": "กำลังอ่านรีโพซิทอรีของคุณ",
  "Replaced": "ถูกแทนที่แล้ว",
  "Skipped": "ข้ามไป",
  "Resizes and crops the images your app stores, so your pages stay quick to open.": "ย่อและครอบตัดรูปที่แอปของคุณเก็บไว้ เพื่อให้หน้าเว็บเปิดเร็ว",
  "Runs a piece of your app on a schedule, like a summary every morning.": "เรียกใช้ส่วนหนึ่งของแอปคุณตามกำหนดเวลา เช่น สรุปทุกเช้า",
  "Scheduled jobs": "งานตามกำหนดเวลา",
  "Screens": "หน้าจอ",
  "See your apps, their deploys, and their status": "ดูแอปของคุณ การดีพลอย และสถานะของแอป",
  "Sends email from your app: sign in links, receipts, the note after somebody orders.": "ส่งอีเมลจากแอปของคุณ ทั้งลิงก์เข้าสู่ระบบ ใบเสร็จ และข้อความหลังมีคนสั่งซื้อ",
  "Set up this project on SV Cloud for me. First install the SV Cloud CLI and sign in by running `npm install -g svcloud && svcloud login` (the sign-in opens a browser, so wait for me to finish it). Then clone https://github.com/{repo}.git and work inside it. Then read SVAGENTS.md in the repository root and follow it to finish the setup.": "ช่วยตั้งค่าโปรเจกต์นี้บน SV Cloud ให้ฉัน เริ่มจากติดตั้ง SV Cloud CLI แล้วเข้าสู่ระบบด้วยคำสั่ง `npm install -g svcloud && svcloud login` (การเข้าสู่ระบบจะเปิดเบราว์เซอร์ ให้รอจนฉันทำเสร็จ) จากนั้นโคลน https://github.com/{repo}.git แล้วทำงานอยู่ในนั้น แล้วอ่าน SVAGENTS.md ที่รากของรีโพซิทอรีและทำตามเพื่อตั้งค่าให้เสร็จ",
  "Setting up access to your repository": "กำลังตั้งค่าการเข้าถึงรีโพซิทอรีของคุณ",
  "Show or hide the demo controls": "แสดงหรือซ่อนตัวควบคุมเดโม",
  "Soi Cats arrived from Loop": "Soi Cats ย้ายมาจาก Loop",
  "Start here": "เริ่มที่นี่",
  "Basic plan": "แพลน Basic",
  "Store files people upload. Provisioned automatically for every project.": "เก็บไฟล์ที่คนอัปโหลด จัดเตรียมให้อัตโนมัติทุกโปรเจกต์",
  "Store your app's data. Provisioned automatically for every project.": "เก็บข้อมูลของแอปคุณ จัดเตรียมให้อัตโนมัติทุกโปรเจกต์",
  "Stores your app's information: users, posts, orders, anything you save.": "เก็บข้อมูลของแอปคุณ ทั้งผู้ใช้ โพสต์ คำสั่งซื้อ และทุกอย่างที่คุณบันทึก",
  "Switching your plan": "กำลังสลับแพลนของคุณ",
  "Switching your web address over": "กำลังสลับที่อยู่เว็บของคุณ",
  "Sync with system": "ซิงก์กับระบบ",
  "Tab": "Tab",
  "Table rows arriving one by one until six tables are ready.": "แถวในตารางทยอยมาทีละแถวจนครบหกตาราง",
  "Pro plan": "แพลน Pro",
  "Max plan": "แพลน Max",
  "Telling your agent about it": "กำลังแจ้งเอเจนต์ของคุณ",
  "Ten requests came back with an error in the last hour.": "สิบคำขอได้ข้อผิดพลาดกลับมาในชั่วโมงที่ผ่านมา",
  "The deploys screen, with the newest deploy marked live.": "หน้าการดีพลอย โดยดีพลอยล่าสุดถูกทำเครื่องหมายว่าออนไลน์",
  "Things to do": "สิ่งที่ทำได้",
  "This is the last one we send. Your claim link expires on {expires}.": "นี่คือฉบับสุดท้ายที่เราส่ง ลิงก์รับสิทธิ์ของคุณหมดอายุวันที่ {expires}",
  "Three figures walking on the cloud deck, far above the weather.": "คนสามคนเดินอยู่บนแผ่นเมฆ สูงเหนือสภาพอากาศ",
  "Trigger deploys and roll back to an earlier one": "สั่งดีพลอยและย้อนกลับไปเวอร์ชันก่อนหน้า",
  "Up and down": "ขึ้นและลง",
  "Usage shows how much of your plan you have spent: compute, database and files, each against its own limit.": "หน้าการใช้งานแสดงว่าคุณใช้แพลนไปเท่าไร ทั้งการประมวลผล ฐานข้อมูล และไฟล์ โดยเทียบกับขีดจำกัดของแต่ละอย่าง",
  "View your account settings: your profile, how the console looks, and how much detail it shows you.": "ดูการตั้งค่าบัญชีของคุณ ทั้งโปรไฟล์ หน้าตาของคอนโซล และระดับรายละเอียดที่แสดง",
  "View your account settings: your profile and how the console looks.": "ดูการตั้งค่าบัญชีของคุณ ทั้งโปรไฟล์และหน้าตาของคอนโซล",
  "What you have used": "คุณใช้ไปเท่าไรแล้ว",
  "Your account": "บัญชีของคุณ",
  "Your app started returning errors on some invoices": "แอปของคุณเริ่มส่งข้อผิดพลาดกลับในใบแจ้งหนี้บางใบ",
  "Your claim link expires on {expires}. After that you can still ask for a new one, but it is one more thing to do.": "ลิงก์รับสิทธิ์ของคุณหมดอายุวันที่ {expires} หลังจากนั้นคุณยังขอลิงก์ใหม่ได้ แต่ก็เป็นอีกเรื่องที่ต้องทำ",
  "Your code stays in GitHub. We do not touch your repository.": "โค้ดของคุณยังอยู่ใน GitHub เราไม่แตะรีโพซิทอรีของคุณ",
  "Your project is finished and it is still running at {host}. You can move it to your own SV Cloud account whenever you like.": "โปรเจกต์ของคุณเสร็จแล้วและยังทำงานอยู่ที่ {host} คุณย้ายไปยังบัญชี SV Cloud ของคุณเองได้ทุกเมื่อ",
  "a minute ago": "หนึ่งนาทีก่อน",
  "agent": "เอเจนต์",
  "an hour ago": "หนึ่งชั่วโมงก่อน",
  "binding, wire, integration": "binding, wire, integration",
  "build": "การบิลด์",
  "custom domain, hostname": "custom domain, hostname",
  "deploy": "ดีพลอย",
  "deploys": "การดีพลอย",
  "didn't work": "ไม่สำเร็จ",
  "health": "สุขภาพ",
  "instance, pool, gateway": "instance, pool, gateway",
  "new project": "โปรเจกต์ใหม่",
  "object storage, bucket, blob": "object storage, bucket, blob",
  "release, revision, build artifact": "release, revision, build artifact",
  "secrets, environment variables": "secrets, environment variables",
  "src/routes/invoices.ts calls a function named formatCurency, but the file that defines it spells it formatCurrency. The build stopped before it could finish, so nothing changed on your live site.": "src/routes/invoices.ts เรียกฟังก์ชันชื่อ formatCurency แต่ไฟล์ที่ประกาศไว้สะกดว่า formatCurrency การบิลด์จึงหยุดก่อนเสร็จ และไม่มีอะไรบนเว็บที่ออนไลน์อยู่เปลี่ยนแปลง",
  "worker, service, function": "worker, service, function",
  "yesterday": "เมื่อวาน",
  "{host} is still answering, and everything in your database and your files is still there.": "{host} ยังตอบสนองอยู่ และทุกอย่างในฐานข้อมูลกับไฟล์ของคุณยังอยู่ครบ",
  "{n} days ago": "{n} วันก่อน",
  "{n} hours ago": "{n} ชั่วโมงก่อน",
  "{n} minutes ago": "{n} นาทีก่อน",
  "{n} tables ready": "{n} ตารางพร้อมแล้ว",
  "{project} is yours to keep": "{project} เป็นของคุณแล้ว",

  /* components/patterns/UploadLinkPanel, the owner's side of an upload link */
  "Upload links": "ลิงก์อัปโหลด",
  "Upload link": "ลิงก์อัปโหลด",
  "Send a link to someone outside your team, a photographer or a client, and they can upload straight into your files. They need no account.": "ส่งลิงก์ให้คนนอกทีม เช่น ช่างภาพหรือลูกค้า แล้วเขาจะอัปโหลดเข้าไฟล์ของคุณได้โดยตรง โดยไม่ต้องมีบัญชี",
  "Create a link": "สร้างลิงก์",
  "Create an upload link": "สร้างลิงก์อัปโหลด",
  "Create link": "สร้างลิงก์",
  "Creating…": "กำลังสร้าง…",
  "Anyone with the link can send you files until it expires. They cannot see or download anything you already have.": "ใครก็ตามที่มีลิงก์นี้จะส่งไฟล์ถึงคุณได้จนกว่าลิงก์จะหมดอายุ แต่จะดูหรือดาวน์โหลดไฟล์ที่คุณมีอยู่แล้วไม่ได้",
  "What is this for?": "ลิงก์นี้ใช้ทำอะไร",
  "The person opening the link sees this. For example: Wedding photos, August.": "คนที่เปิดลิงก์จะเห็นข้อความนี้ เช่น รูปงานแต่ง เดือนสิงหาคม",
  "Give the link a name so the person opening it knows what it is for.": "ตั้งชื่อลิงก์เพื่อให้คนที่เปิดรู้ว่าลิงก์นี้ใช้ทำอะไร",
  "Where the files land in your storage.": "ตำแหน่งที่ไฟล์จะไปอยู่ในพื้นที่เก็บไฟล์ของคุณ",
  "How long should it work?": "ให้ลิงก์ใช้งานได้นานแค่ไหน",
  "How much can they send?": "ให้ส่งได้มากแค่ไหน",
  "3 days": "3 วัน",
  "10 GB": "10 GB",
  "50 GB": "50 GB",
  "200 GB": "200 GB",
  "Send this link": "ส่งลิงก์นี้",
  "This is the only time it is shown. If you lose it, create another one. This one keeps working until it expires.": "ลิงก์นี้จะแสดงแค่ครั้งเดียว ถ้าทำหาย ให้สร้างใหม่ ส่วนลิงก์นี้จะยังใช้งานได้จนหมดอายุ",
  "No upload links yet. Create one when somebody needs to send you files.": "ยังไม่มีลิงก์อัปโหลด สร้างได้เมื่อมีคนต้องส่งไฟล์ถึงคุณ",
  "Nothing sent yet": "ยังไม่มีไฟล์ส่งเข้ามา",
  "{n} files · {size}": "{n} ไฟล์ · {size}",
  "{size} on the way": "กำลังส่งอีก {size}",
  "Open for {n} more hours": "เปิดอีก {n} ชั่วโมง",
  "Open for {n} more days": "เปิดอีก {n} วัน",
  "Turn off": "ปิด",
  "Turn off this link?": "ปิดลิงก์นี้ไหม",
  "Turn it off": "ปิดลิงก์",
  "Turned off": "ปิดแล้ว",
  "Keep it on": "เปิดไว้ต่อ",
  "Nobody will be able to send anything new with it. The files already uploaded stay exactly where they are. This does not delete a delivery.": "จะไม่มีใครส่งไฟล์ใหม่ด้วยลิงก์นี้ได้อีก ไฟล์ที่อัปโหลดไว้แล้วยังอยู่ที่เดิมทุกไฟล์ การปิดลิงก์ไม่ได้ลบไฟล์ที่ได้รับมาแล้ว",
  "Upload link turned off.": "ปิดลิงก์อัปโหลดแล้ว",
  "Could not create the link.": "สร้างลิงก์ไม่สำเร็จ",
  "Could not load your upload links.": "โหลดลิงก์อัปโหลดของคุณไม่สำเร็จ",
  "Could not turn off the link.": "ปิดลิงก์ไม่สำเร็จ",
  "Something went wrong": "มีบางอย่างผิดพลาด",
  "Expired": "หมดอายุแล้ว",

  /* screens/drop, the page the person with the link sees */
  "Upload files": "อัปโหลดไฟล์",
  "Drop your files below. You do not need an account, and there is nothing to install.": "วางไฟล์ของคุณด้านล่าง ไม่ต้องมีบัญชีและไม่ต้องติดตั้งอะไร",
  "Photos and video are both fine.": "ส่งได้ทั้งรูปและวิดีโอ",
  "Large video is expected. Please do not zip anything, it makes this slower rather than faster.": "วิดีโอไฟล์ใหญ่เป็นเรื่องปกติ ไม่ต้องบีบอัดเป็น zip เพราะจะยิ่งช้าลงแทนที่จะเร็วขึ้น",
  "If your internet drops, nothing is lost.": "ถ้าอินเทอร์เน็ตหลุด ไฟล์ที่ส่งแล้วจะไม่หาย",
  "Open this same link again and pick the same files. The ones that already arrived are skipped, and a large video carries on from where it stopped.": "เปิดลิงก์เดิมนี้อีกครั้งแล้วเลือกไฟล์ชุดเดิม ไฟล์ที่มาถึงแล้วจะถูกข้าม ส่วนวิดีโอไฟล์ใหญ่จะส่งต่อจากจุดที่หยุดไว้",
  "This link stops working {when}.": "ลิงก์นี้จะหยุดทำงาน{when}",
  "There is room for {size} in total.": "มีพื้นที่รวมทั้งหมด {size}",
  "Drag your photos or your whole folder here": "ลากรูปหรือทั้งโฟลเดอร์มาวางที่นี่",
  "Choose the photos or videos you want to send": "เลือกรูปหรือวิดีโอที่ต้องการส่ง",
  "Choose files": "เลือกไฟล์",
  "Choose a folder": "เลือกโฟลเดอร์",
  "This link is not available": "ลิงก์นี้ใช้งานไม่ได้",
  "Ask the person who sent it to you for a new one. Links stop working after a while, on purpose.": "ขอลิงก์ใหม่จากคนที่ส่งให้คุณ ลิงก์จะหยุดทำงานหลังผ่านไประยะหนึ่งโดยตั้งใจ",
  "Loading…": "กำลังโหลด…",
  "The upload stopped": "การอัปโหลดหยุดลง",
  "The upload could not start.": "เริ่มอัปโหลดไม่สำเร็จ",
  "{done} of {total} sent, {size} so far": "ส่งแล้ว {done} จาก {total} รวม {size}",
  "{done} of {total} files are on their way to the owner.": "ส่งไฟล์ถึงเจ้าของแล้ว {done} จาก {total} ไฟล์",
  "and {n} more waiting": "และอีก {n} ไฟล์ที่รออยู่",
  "{n} files didn't make it": "มี {n} ไฟล์ที่ส่งไม่สำเร็จ",
  "Pick the same files again. Everything that already arrived is skipped, so only these are retried.": "เลือกไฟล์ชุดเดิมอีกครั้ง ไฟล์ที่มาถึงแล้วจะถูกข้าม จึงลองใหม่เฉพาะไฟล์เหล่านี้",
  "All done": "เสร็จทั้งหมดแล้ว",
  "Everything arrived. You can close this page.": "ไฟล์มาถึงครบแล้ว ปิดหน้านี้ได้เลย",
  "Sent": "ส่งแล้ว",
  "Already sent": "ส่งไปแล้ว",
  "Not accepted": "ไม่รับไฟล์นี้",
  "Didn't finish": "ยังไม่เสร็จ",
  "Sending": "กำลังส่ง",
  "expired": "หมดอายุแล้ว",
  "in under an hour": "ในไม่ถึงหนึ่งชั่วโมง",
  "in about {n} hours": "ในอีกประมาณ {n} ชั่วโมง",
  "tomorrow": "พรุ่งนี้",
  "in about {n} days": "ในอีกประมาณ {n} วัน",
  /* S21, the bundle (apps/cloud F27). "บันเดิล" transliterates rather than
     translates, for the reason the header gives about anything a person types
     or copies: it is the word `svcloud bundle` and the bundle_* tools use, so
     a translated noun would stop matching what they are looking at. Version
     numbers are values, not copy, so they stay in the {placeholder}. */
  "Bundle version": "เวอร์ชันบันเดิล",
  "Every app is built on a bundle that we keep improving. This is where you catch up with it.": "ทุกแอปสร้างขึ้นบนบันเดิลที่เราปรับปรุงอยู่เสมอ หน้านี้คือที่ที่คุณอัปเดตให้ทันกับมัน",
  "Checking your bundle": "กำลังตรวจสอบบันเดิลของคุณ",
  "We could not check your bundle just now.": "เราตรวจสอบบันเดิลของคุณตอนนี้ไม่ได้",
  "Up to date": "เป็นเวอร์ชันล่าสุดแล้ว",
  "This app is on the newest bundle, {version}.": "แอปนี้ใช้บันเดิลเวอร์ชันล่าสุดแล้ว คือ {version}",
  "We will say so here when there is a newer one.": "เมื่อมีเวอร์ชันใหม่กว่า เราจะแจ้งไว้ที่นี่",
  "A newer bundle is available": "มีบันเดิลเวอร์ชันใหม่กว่า",
  "You are on {current}. The newest is {latest}.": "คุณใช้ {current} อยู่ เวอร์ชันล่าสุดคือ {latest}",
  "The newest is {latest}.": "เวอร์ชันล่าสุดคือ {latest}",
  "This app has no recorded starting point.": "แอปนี้ไม่มีจุดเริ่มต้นที่บันทึกไว้",
  "It was set up before we started recording which bundle an app is on, so this first update cannot tell which parts you changed. Your agent will treat every change as a suggestion and ask you about anything it is unsure of. After this, we will know.": "แอปนี้ถูกตั้งค่าก่อนที่เราจะเริ่มบันทึกว่าแต่ละแอปใช้บันเดิลเวอร์ชันใด การอัปเดตครั้งแรกนี้จึงบอกไม่ได้ว่าส่วนใดที่คุณแก้ไขเอง ผู้ช่วยเขียนโค้ดของคุณจะถือว่าทุกการเปลี่ยนแปลงเป็นเพียงข้อเสนอ และจะถามคุณทุกครั้งที่ไม่แน่ใจ หลังจากครั้งนี้เราจะรู้แล้ว",
  "Nothing in your own code changes.": "โค้ดของคุณเองไม่มีอะไรเปลี่ยน",
  "{n} files change. Your own code is not overwritten.": "มีไฟล์เปลี่ยน {n} ไฟล์ โค้ดของคุณเองจะไม่ถูกเขียนทับ",
  "{n} of them need a decision from you. Your agent will explain each one in plain language before changing anything.": "ในจำนวนนั้น {n} รายการต้องให้คุณตัดสินใจ ผู้ช่วยเขียนโค้ดจะอธิบายแต่ละรายการด้วยภาษาที่เข้าใจง่ายก่อนเปลี่ยนอะไรก็ตาม",
  "Open this app with your coding agent and give it this:": "เปิดแอปนี้ด้วยผู้ช่วยเขียนโค้ดของคุณ แล้วส่งข้อความนี้ให้มัน",
  "Update request": "คำขออัปเดต",
  /* Kept in English on purpose, like the commands elsewhere in this file: it
     is an instruction a person pastes into a coding agent, and it names the
     platform's own tools. A Thai translation would be a string that does not
     work when it is used. */
  "Update this app to the newest SV Cloud bundle. Use the bundle_status and bundle_plan tools to get the plan, follow the action on each file, and update contents SVAGENTS.md with the latest bundle, do not commit or push. Tell me what changed before I commit.": "Update this app to the newest SV Cloud bundle. Use the bundle_status and bundle_plan tools to get the plan, follow the action on each file, and update contents SVAGENTS.md with the latest bundle, do not commit or push. Tell me what changed before I commit.",
  "It reads the same plan we do, and it will not commit anything without showing you first.": "มันอ่านแผนเดียวกับที่เราอ่าน และจะไม่คอมมิตอะไรโดยไม่ให้คุณดูก่อน",
  "What changes": "สิ่งที่จะเปลี่ยน",
  "Show me the files": "ดูรายชื่อไฟล์",
  "What is new": "มีอะไรใหม่",
  "Back to settings": "กลับไปที่การตั้งค่า",
  "You never changed this one, so it is swapped for the new version.": "คุณไม่เคยแก้ไฟล์นี้ จึงเปลี่ยนเป็นเวอร์ชันใหม่ได้เลย",
  "Merged": "รวมเข้าด้วยกัน",
  "You changed this one and so did we. Your agent keeps both.": "ทั้งคุณและเราแก้ไฟล์นี้ ผู้ช่วยเขียนโค้ดจะเก็บทั้งสองฝั่งไว้",
  "Added": "เพิ่มใหม่",
  "New in this version of the bundle.": "เป็นของใหม่ในบันเดิลเวอร์ชันนี้",
  "Your list of packages and commands gains what is missing. Nothing is removed.": "รายการแพ็กเกจและคำสั่งของคุณจะได้ส่วนที่ขาดเพิ่มเข้ามา โดยไม่มีอะไรถูกลบ",
  "Needs a look": "ต้องตรวจสอบก่อน",
  "We expected this somewhere and it is not there. Your agent finds it first.": "เราคาดว่าไฟล์นี้อยู่ที่หนึ่ง แต่ไม่พบ ผู้ช่วยเขียนโค้ดจะหาให้เจอก่อน",
  "Removed": "นำออกแล้ว",
  "No longer part of the bundle, and unused here.": "ไม่ได้เป็นส่วนหนึ่งของบันเดิลแล้ว และแอปนี้ไม่ได้ใช้",
  "Needs a decision": "ต้องให้คุณตัดสินใจ",
  "No longer part of the bundle, and your app uses it. Your agent will explain the options before changing anything.": "ไม่ได้เป็นส่วนหนึ่งของบันเดิลแล้ว แต่แอปของคุณใช้อยู่ ผู้ช่วยเขียนโค้ดจะอธิบายทางเลือกให้ฟังก่อนเปลี่ยนอะไรก็ตาม",
  "One of your build steps is failing.": "มีขั้นตอนการสร้างแอปของคุณขั้นหนึ่งทำงานไม่สำเร็จ",
  "How to fix it": "วิธีแก้",
  "There is a newer bundle for your app. Nothing is wrong, and nothing changes until you ask for it.": "มีบันเดิลเวอร์ชันใหม่กว่าสำหรับแอปของคุณ ไม่มีอะไรผิดปกติ และจะไม่มีอะไรเปลี่ยนจนกว่าคุณจะสั่ง",
  "Have a look": "ดูรายละเอียด",
  "Check the bundle": "ตรวจสอบบันเดิล",
  "Your app is built on a bundle we keep improving. Updating it is how your app gets things the platform can now do.": "แอปของคุณสร้างบนบันเดิลที่เราปรับปรุงอยู่เสมอ การอัปเดตคือวิธีที่แอปของคุณจะได้ความสามารถใหม่ที่แพลตฟอร์มรองรับแล้ว",
  // --- Staging banner (components/shell/StagingBanner.tsx)
  "This is not the app your customers use. Changes here reach nobody.":
    "นี่ไม่ใช่แอปที่ลูกค้าของคุณใช้งาน การเปลี่ยนแปลงที่นี่จะไม่ถึงผู้ใช้",
  "Go to the live app":
    "ไปที่แอปที่ใช้งานจริง",

  // --- Custom domain (components/patterns/CustomDomainSection.tsx)
  "Custom domain":
    "โดเมนของคุณเอง",
  "Serve your app on a domain you own, like app.yourcompany.com. Your SV Cloud address keeps working too.":
    "ให้บริการแอปของคุณบนโดเมนที่คุณเป็นเจ้าของ เช่น app.yourcompany.com ที่อยู่ SV Cloud ของคุณก็ยังใช้งานได้เช่นกัน",
  "Serve your app on up to {limit} domains you own, like yourcompany.com and www.yourcompany.com. Your SV Cloud address keeps working too.":
    "ให้บริการแอปของคุณบนโดเมนที่คุณเป็นเจ้าของได้สูงสุด {limit} โดเมน เช่น yourcompany.com และ www.yourcompany.com ที่อยู่ SV Cloud ของคุณก็ยังใช้งานได้เช่นกัน",
  "{limit} custom domains per app. Remove one to use a different address.":
    "ใช้โดเมนของคุณเองได้ {limit} โดเมนต่อหนึ่งแอป ลบโดเมนหนึ่งออกก่อนหากต้องการใช้ที่อยู่อื่น",
  "Loading your custom domains":
    "กำลังโหลดโดเมนของคุณ",
  "Issuing your certificate. This can take a few minutes.":
    "กำลังออกใบรับรองให้คุณ อาจใช้เวลาสองสามนาที",
  "Waiting for your DNS records. Add the ones below, then check again.":
    "กำลังรอเรกคอร์ด DNS ของคุณ เพิ่มรายการด้านล่าง แล้วตรวจสอบอีกครั้ง",
  "Add these records at your DNS provider:":
    "เพิ่มเรกคอร์ดเหล่านี้ที่ผู้ให้บริการ DNS ของคุณ:",
  "Record 1: CNAME":
    "เรกคอร์ดที่ 1: CNAME",
  "Record 2: TXT":
    "เรกคอร์ดที่ 2: TXT",
  "Record {number}: TXT":
    "เรกคอร์ดที่ {number}: TXT",
  "Points to":
    "ชี้ไปที่",
  "DNS records to add for this domain":
    "เรกคอร์ด DNS ที่ต้องเพิ่มสำหรับโดเมนนี้",
  "Name field":
    "ช่อง Name",
  "Content field":
    "ช่อง Content",
  "Proxy":
    "พร็อกซี",
  "DNS only":
    "DNS only",
  "On Cloudflare, DNS only is the grey cloud. A proxied record will not work.":
    "บน Cloudflare คำว่า DNS only คือเมฆสีเทา เรกคอร์ดที่พร็อกซีอยู่จะใช้งานไม่ได้",
  "n/a":
    "ไม่มี",
  "Each name is shown two ways: the short form on top for providers that add your domain for you, and the full form under it for providers that do not.":
    "ชื่อแต่ละรายการแสดงสองแบบ: แบบสั้นด้านบนสำหรับผู้ให้บริการที่เติมโดเมนของคุณให้เอง และแบบเต็มด้านล่างสำหรับผู้ให้บริการที่ไม่เติมให้",
  "Your certificate record is taking longer than usual. Add the CNAME now, and use Check status for the rest.":
    "เรกคอร์ดใบรับรองของคุณใช้เวลานานกว่าปกติ เพิ่มเรกคอร์ด CNAME ไปก่อน แล้วใช้ปุ่มตรวจสอบสถานะสำหรับส่วนที่เหลือ",
  "Waiting for your certificate record from Cloudflare":
    "กำลังรอเรกคอร์ดใบรับรองจาก Cloudflare",
  "Skip the typing: download these records as one file and import it at your DNS provider. Cloudflare, GoDaddy and Route 53 can import it. If yours cannot, add the records by hand.":
    "ไม่ต้องพิมพ์เอง: ดาวน์โหลดเรกคอร์ดทั้งหมดเป็นไฟล์เดียว แล้วนำเข้าที่ผู้ให้บริการ DNS ของคุณ ไฟล์นี้นำเข้าได้ที่ Cloudflare, GoDaddy และ Route 53 ถ้าผู้ให้บริการของคุณนำเข้าไม่ได้ ให้เพิ่มเรกคอร์ดเอง",
  "Download DNS file":
    "ดาวน์โหลดไฟล์ DNS",
  "Your certificate record is on its way. It appears here on its own, usually within a few seconds.":
    "เรกคอร์ดใบรับรองของคุณกำลังมา จะปรากฏที่นี่เอง โดยปกติภายในไม่กี่วินาที",
  "Your certificate record is taking longer than usual. Add the CNAME above now, and use Check status for the second record.":
    "เรกคอร์ดใบรับรองของคุณใช้เวลานานกว่าปกติ เพิ่มเรกคอร์ด CNAME ด้านบนไปก่อน แล้วใช้ปุ่มตรวจสอบสถานะเพื่อดูเรกคอร์ดที่สอง",
  "Cloudflare will not serve this domain. Remove it and add it again.":
    "Cloudflare ไม่ให้บริการโดเมนนี้ ลบออกแล้วเพิ่มใหม่อีกครั้ง",
  "The names above are written out in full. Many providers, Cloudflare among them, add your domain to the name for you: if yours does, leave your domain off the end of what you paste.":
    "ชื่อด้านบนเขียนไว้แบบเต็ม ผู้ให้บริการหลายราย รวมถึง Cloudflare จะเติมโดเมนของคุณต่อท้ายชื่อให้เอง ถ้าผู้ให้บริการของคุณทำแบบนั้น ให้ตัดโดเมนของคุณออกจากท้ายข้อความที่วาง",
  "If your domain is on Cloudflare, set the CNAME to DNS only. A proxied record, the orange cloud, will not work.":
    "ถ้าโดเมนของคุณอยู่บน Cloudflare ให้ตั้งเรกคอร์ด CNAME เป็น DNS only เรกคอร์ดที่พร็อกซีอยู่ หรือเมฆสีส้ม จะใช้งานไม่ได้",
  "Check status":
    "ตรวจสอบสถานะ",
  "One custom domain per app. Remove this one to use a different address.":
    "หนึ่งโดเมนต่อหนึ่งแอป ลบโดเมนนี้ออกก่อนหากต้องการใช้ที่อยู่อื่น",
  "Adding a custom domain is not part of this demo.":
    "การเพิ่มโดเมนของคุณเองไม่ได้เป็นส่วนหนึ่งของเดโมนี้",
  "Domain":
    "โดเมน",
  "Add domain":
    "เพิ่มโดเมน",

  // --- Staging (components/patterns/StagingSection.tsx)
  "That did not go through. Try again.":
    "ดำเนินการไม่สำเร็จ กรุณาลองอีกครั้ง",
  "Pushes to {branch} go to staging. Your live app changes when you push a prod tag.":
    "การ push ไปที่ {branch} จะไปที่ staging แอปที่ใช้งานจริงจะเปลี่ยนเมื่อคุณ push แท็ก prod",
  "A staging environment is a second copy of your app, with its own database and files, that you can try changes on before they reach the people using it.":
    "สภาพแวดล้อม staging คือสำเนาที่สองของแอปคุณ มีฐานข้อมูลและไฟล์ของตัวเอง ให้คุณลองการเปลี่ยนแปลงก่อนที่จะถึงมือผู้ใช้จริง",
  "Staging":
    "Staging",
  "It runs a second app and a second database, and what it uses comes out of the same allowance as your live app.":
    "มันจะรันแอปที่สองและฐานข้อมูลที่สอง และปริมาณที่ใช้จะถูกหักจากโควตาเดียวกับแอปที่ใช้งานจริง",
  "Add a staging environment":
    "เพิ่มสภาพแวดล้อม staging",
  "Your staging app":
    "แอป staging ของคุณ",
  "Loading your staging environment":
    "กำลังโหลดสภาพแวดล้อม staging ของคุณ",
  "To put a version on your live app, tag the commit you tested: git tag prod-2026-01-01.1 then git push origin prod-2026-01-01.1":
    "หากต้องการนำเวอร์ชันขึ้นแอปที่ใช้งานจริง ให้ติดแท็กคอมมิตที่คุณทดสอบแล้ว: git tag prod-2026-01-01.1 จากนั้น git push origin prod-2026-01-01.1",
  "Go back to deploying straight to the live app":
    "กลับไป deploy ตรงไปยังแอปที่ใช้งานจริง",
  "Your repository is not set up to promote a tagged version yet.":
    "repository ของคุณยังไม่ได้ตั้งค่าให้เลื่อนเวอร์ชันที่ติดแท็กขึ้นใช้งานจริง",
  "Your staging app is missing {count} setting(s) your live app has: {names}. Add them under Keys and settings first, or its first build is likely to fail.":
    "แอป staging ของคุณขาดการตั้งค่า {count} รายการที่แอปที่ใช้งานจริงมี: {names} กรุณาเพิ่มในหัวข้อ Keys and settings ก่อน มิฉะนั้นการ build ครั้งแรกมีแนวโน้มจะล้มเหลว",
  "Turn this on and pushes to {branch} stop updating your live app. They go to staging, and your live app only changes when you push a prod tag. You can switch back at any time.":
    "เมื่อเปิดใช้งาน การ push ไปที่ {branch} จะไม่อัปเดตแอปที่ใช้งานจริงอีกต่อไป แต่จะไปที่ staging แทน และแอปที่ใช้งานจริงจะเปลี่ยนก็ต่อเมื่อคุณ push แท็ก prod คุณสามารถสลับกลับได้ทุกเมื่อ",
  "Send pushes to staging":
    "ส่งการ push ไปที่ staging",
  "Adding one is not part of this demo.":
    "การเพิ่มสภาพแวดล้อม staging ไม่ได้รวมอยู่ในเดโมนี้",

  // Admin Platform screen (2026-09-24).
  "Platform": "แพลตฟอร์ม",
  "{n} min ago": "{n} นาทีที่แล้ว",
  "{n} h ago": "{n} ชั่วโมงที่แล้ว",
  "GitHub Actions": "GitHub Actions",
  "Not recorded": "ไม่ได้บันทึก",
  "Count": "จำนวน",
  "None in this window.": "ไม่มีในช่วงเวลานี้",
  "From": "จาก",
  "Reason": "สาเหตุ",
  "Log": "บันทึก",
  "Last moved": "ขยับล่าสุด",
  "Compute units": "หน่วยประมวลผล",
  "How SV Cloud is doing across every account. Skipped deploys are runs GitHub cancelled because a newer push arrived, so they are not failures.": "ภาพรวมของ SV Cloud ในทุกบัญชี การ deploy ที่ข้ามไปคือรอบที่ GitHub ยกเลิกเพราะมีการ push ใหม่กว่าเข้ามา จึงไม่นับเป็นความล้มเหลว",
  "Time window": "ช่วงเวลา",
  "Loading platform numbers": "กำลังโหลดตัวเลขของแพลตฟอร์ม",
  "Could not load platform numbers. Try again in a moment.": "โหลดตัวเลขของแพลตฟอร์มไม่ได้ ลองใหม่อีกครั้งในอีกสักครู่",
  "Provisioning now": "กำลังเตรียมระบบอยู่ตอนนี้",
  "Stuck runs": "รอบที่ค้างอยู่",
  "Provisions that failed": "การเตรียมระบบที่ไม่สำเร็จ",
  "{n} finished": "เสร็จแล้ว {n}",
  "Deploy success rate": "อัตราการ deploy สำเร็จ",
  "Deploys that failed": "การ deploy ที่ไม่สำเร็จ",
  "{n} went live": "ขึ้นใช้งานแล้ว {n}",
  "Deploys skipped": "การ deploy ที่ข้ามไป",
  "Replaced by a newer push": "ถูกแทนที่ด้วยการ push ที่ใหม่กว่า",
  "Needs attention": "ต้องดูแล",
  "In flight and untouched for ten minutes or more. The five-minute sweep should close these; one that stays here means it is not.": "กำลังทำงานอยู่และไม่มีความเคลื่อนไหวนานสิบนาทีขึ้นไป การกวาดทุกห้านาทีควรปิดรายการเหล่านี้ หากรายการใดยังค้างอยู่ แปลว่าการกวาดไม่ได้ปิดมัน",
  "Why deploys failed": "สาเหตุที่ deploy ไม่สำเร็จ",
  "Not recorded means the deploy failed before SV Cloud started keeping reasons, on 24 September 2026.": "ไม่ได้บันทึก หมายถึงการ deploy ที่ไม่สำเร็จก่อนที่ SV Cloud จะเริ่มเก็บสาเหตุในวันที่ 24 กันยายน 2026",
  "Latest failures": "ความล้มเหลวล่าสุด",
  "Typical time to provision": "เวลาเตรียมระบบโดยทั่วไป",
  "Slowest tenth": "สิบเปอร์เซ็นต์ที่ช้าที่สุด",
  "Teardowns that failed": "การรื้อถอนที่ไม่สำเร็จ",
  "Step that failed": "ขั้นตอนที่ไม่สำเร็จ",
  "Step": "ขั้นตอน",
  "Error code": "รหัสข้อผิดพลาด",
  "Code": "รหัส",
  "Server error rate": "อัตราข้อผิดพลาดของเซิร์ฟเวอร์",
  "From the nightly rollup, up to a day behind": "จากการสรุปทุกคืน อาจช้าไปได้ถึงหนึ่งวัน",
  "Requests per day": "คำขอต่อวัน",
  "No traffic in this window.": "ไม่มีทราฟฟิกในช่วงเวลานี้",
  "Busiest apps": "แอปที่มีการใช้งานมากที่สุด",
  "Fleet": "ภาพรวมทั้งระบบ",
  "Live apps": "แอปที่ใช้งานอยู่",
  "Staging environments": "สภาพแวดล้อม staging",
  "{n} new": "ใหม่ {n}",
  "Waiting on the wait list": "รออยู่ในรายชื่อรอ",
  "Custom domains": "โดเมนของคุณเอง",

  /* GitHub access lost (2026-09-25). Uninstalling the App or losing the repo
     never deletes an app, so the notice leads with that. */
  "SV Cloud can no longer reach your GitHub repository.": "SV Cloud เข้าถึงรีโพสิทอรี GitHub ของคุณไม่ได้แล้ว",
  "your repository": "รีโพสิทอรีของคุณ",
  "The SV Cloud GitHub App was uninstalled from {repo}.": "GitHub App ของ SV Cloud ถูกถอนการติดตั้งออกจาก {repo}",
  "The SV Cloud GitHub App no longer has access to {repo}.": "GitHub App ของ SV Cloud ไม่มีสิทธิ์เข้าถึง {repo} แล้ว",
  "{repo} was deleted on GitHub.": "{repo} ถูกลบบน GitHub แล้ว",
  "Your app is still running and nothing was deleted. New pushes will not deploy until you connect the repository again: install the GitHub App on it and pick the same repository. To remove the app instead, delete it from Settings.": "แอปของคุณยังทำงานอยู่ และไม่มีอะไรถูกลบ การ push ใหม่จะยังไม่ถูกดีพลอยจนกว่าคุณจะเชื่อมต่อรีโพสิทอรีอีกครั้ง: ติดตั้ง GitHub App บนรีโพสิทอรีนั้นแล้วเลือกรีโพสิทอรีเดิม หากต้องการลบแอปแทน ให้ลบได้ที่หน้าการตั้งค่า",
  "Reconnect": "เชื่อมต่ออีกครั้ง",

  /* Alpha launch welcome and Submit Feedback (2026-09-27) */
  "Welcome to the SV Cloud Alpha": "ยินดีต้อนรับสู่ SV Cloud เวอร์ชัน Alpha",
  "SV Cloud is now open to everyone on the wait list. Here is what that means while we are in Alpha.": "ตอนนี้ SV Cloud เปิดให้ทุกคนในรายชื่อรอใช้งานแล้ว นี่คือสิ่งที่ควรรู้ระหว่างช่วง Alpha",
  "This is an Alpha launch.": "นี่คือการเปิดตัวเวอร์ชัน Alpha",
  "We are making updates continuously, so screens and features will change as we go.": "เราอัปเดตอย่างต่อเนื่อง หน้าจอและฟีเจอร์จึงอาจเปลี่ยนแปลงไปเรื่อย ๆ",
  "It is free to use right now.": "ตอนนี้ใช้งานได้ฟรี",
  "Usage is measured and shown to you, but limits are not enforced.": "เราวัดและแสดงการใช้งานให้คุณเห็น แต่ยังไม่บังคับใช้ขีดจำกัด",
  "Tell us what you think.": "บอกเราว่าคุณคิดอย่างไร",
  "Submit Feedback, at the bottom left, goes straight to the team.": "ปุ่มส่งความคิดเห็นที่มุมซ้ายล่างจะส่งตรงถึงทีมงาน",
  "Abuse is the exception.": "ยกเว้นกรณีการใช้งานในทางที่ผิด",
  "If we detect abuse, the account is banned immediately and its applications are deleted.": "หากเราตรวจพบการใช้งานในทางที่ผิด บัญชีจะถูกระงับทันทีและแอปพลิเคชันของบัญชีนั้นจะถูกลบ",
  "Submit Feedback": "ส่งความคิดเห็น",
  "Anything helps: what you were trying to do, what happened, what you expected.": "ข้อมูลทุกอย่างมีประโยชน์: คุณพยายามทำอะไร เกิดอะไรขึ้น และคุณคาดหวังอะไร",
  "Write a message before sending.": "กรุณาเขียนข้อความก่อนส่ง",
  "Thanks, your feedback was sent.": "ขอบคุณ ส่งความคิดเห็นของคุณแล้ว",
  "The team reads every message.": "ทีมงานอ่านทุกข้อความ",
  "Tell the SV Cloud team what is working and what is not.": "บอกทีม SV Cloud ว่าอะไรใช้งานได้ดีและอะไรยังไม่ดี",
  "Send feedback": "ส่งความคิดเห็น",
  "Kind of feedback": "ประเภทความคิดเห็น",
  "Idea": "ไอเดีย",
  "Problem": "ปัญหา",
  "Message": "ข้อความ",
  "Other": "อื่น ๆ",
  "Could not send your feedback. Try again in a moment.": "ส่งความคิดเห็นไม่สำเร็จ ลองอีกครั้งในอีกสักครู่",
  "Feedbacks": "ความคิดเห็นจากผู้ใช้",
  "What people sent with Submit Feedback, newest first.": "ความคิดเห็นที่ผู้ใช้ส่งผ่านปุ่มส่งความคิดเห็น เรียงจากใหม่ไปเก่า",
  "Sent by an admin viewing as this account": "ส่งโดยผู้ดูแลที่กำลังดูในฐานะบัญชีนี้",
  "All": "ทั้งหมด",
  "Loading feedback": "กำลังโหลดความคิดเห็น",
  "Could not load feedback. Try again in a moment.": "โหลดความคิดเห็นไม่สำเร็จ ลองอีกครั้งในอีกสักครู่",
  "Nobody has sent feedback yet.": "ยังไม่มีใครส่งความคิดเห็น",
  "No feedback of this kind.": "ไม่มีความคิดเห็นประเภทนี้",
  "Open menu": "เปิดเมนู",
  "Close menu": "ปิดเมนู",
  "Menu": "เมนู",
  "How the console looks.": "หน้าตาของคอนโซล",
  "Your app answered 1 request in the last 24 hours.": "แอปของคุณตอบ 1 คำขอใน 24 ชั่วโมงที่ผ่านมา",
  "Your app answered {n} requests in the last 24 hours.": "แอปของคุณตอบ {n} คำขอใน 24 ชั่วโมงที่ผ่านมา",
  "Your app answered about {n} requests in the last 24 hours.": "แอปของคุณตอบประมาณ {n} คำขอใน 24 ชั่วโมงที่ผ่านมา",
  "Your app answered 1 request in the last 7 days.": "แอปของคุณตอบ 1 คำขอใน 7 วันที่ผ่านมา",
  "Your app answered {n} requests in the last 7 days.": "แอปของคุณตอบ {n} คำขอใน 7 วันที่ผ่านมา",
  "Your app answered about {n} requests in the last 7 days.": "แอปของคุณตอบประมาณ {n} คำขอใน 7 วันที่ผ่านมา",
  "Your app answered 1 request in the last 30 days.": "แอปของคุณตอบ 1 คำขอใน 30 วันที่ผ่านมา",
  "Your app answered {n} requests in the last 30 days.": "แอปของคุณตอบ {n} คำขอใน 30 วันที่ผ่านมา",
  "Your app answered about {n} requests in the last 30 days.": "แอปของคุณตอบประมาณ {n} คำขอใน 30 วันที่ผ่านมา",
  "The 24 hours before: {n} requests": "24 ชั่วโมงก่อนหน้า: {n} คำขอ",
  "The 7 days before: {n} requests": "7 วันก่อนหน้า: {n} คำขอ",
  "The 30 days before: {n} requests": "30 วันก่อนหน้า: {n} คำขอ",
  "Each point is one hour.": "แต่ละจุดคือหนึ่งชั่วโมง",
  "Each point is one day.": "แต่ละจุดคือหนึ่งวัน",
  "The requests your app answered at {address}.": "คำขอที่แอปของคุณตอบที่ {address}",
  "errors": "ข้อผิดพลาด",
  "so far": "จนถึงตอนนี้",
  "No requests yet.": "ยังไม่มีคำขอ",
  "Requests, one point per hour": "คำขอ หนึ่งจุดต่อชั่วโมง",
  "Requests, one point per day": "คำขอ หนึ่งจุดต่อวัน",
  "A request is one thing a browser or program asked your app for. One page view usually makes several.": "คำขอคือสิ่งหนึ่งที่เบราว์เซอร์หรือโปรแกรมขอจากแอปของคุณ การเปิดหน้าเว็บหนึ่งครั้งมักทำหลายคำขอ",
  "Errors are drawn on the chart too, on the same scale, because an error is a request.": "ข้อผิดพลาดแสดงอยู่บนกราฟเดียวกันในสเกลเดียวกัน เพราะข้อผิดพลาดก็คือคำขอหนึ่ง",
  "{requests} requests, {errors} errors": "{requests} คำขอ, {errors} ข้อผิดพลาด",
  "So we can tell you how many requests there were, not how many people.": "เราจึงบอกได้ว่ามีกี่คำขอ ไม่ใช่มีกี่คน",
  "Requests and errors": "คำขอและข้อผิดพลาด",
  "Deployed {version} at {time}": "ดีพลอย {version} เมื่อ {time}",
  "Deploy {version} failed at {time}": "ดีพลอย {version} ไม่สำเร็จเมื่อ {time}",
  "Deploy, tagged with its version": "การดีพลอย พร้อมป้ายเวอร์ชัน",
  "Deploy that failed": "การดีพลอยที่ไม่สำเร็จ",

  /* components/patterns/AlphaWelcome (language choice) */
  "Choose your language": "เลือกภาษาของคุณ",
  "Saved to your account. You can change it later on the Account page.": "บันทึกไว้ในบัญชีของคุณแล้ว เปลี่ยนภายหลังได้ที่หน้าบัญชี",

  /* components/patterns/MembersSection (Teams) */
  "People": "ผู้ร่วมงาน",
  "Collaborators can do everything you can on this app, from this dashboard, the svcloud CLI, or their own coding agent. Only the owner can delete it.": "ผู้ร่วมงานทำได้ทุกอย่างที่คุณทำได้ในแอปนี้ ทั้งจากแดชบอร์ดนี้ svcloud CLI หรือเอเจนต์เขียนโค้ดของเขาเอง มีเพียงเจ้าของเท่านั้นที่ลบแอปได้",
  "Loading the people on this app": "กำลังโหลดรายชื่อผู้ร่วมงานในแอปนี้",
  "Collaborator": "ผู้ร่วมงาน",
  "(you)": "(คุณ)",
  "Leave": "ออกจากแอป",
  "Cancel invitation": "ยกเลิกคำเชิญ",
  "Invitation emailed to {email}.": "ส่งคำเชิญทางอีเมลถึง {email} แล้ว",
  "Invitation created for {email}. Send them this link:": "สร้างคำเชิญสำหรับ {email} แล้ว ส่งลิงก์นี้ให้เขา:",
  "{email} does not have an SV Cloud account yet. The invitation waits until they can sign in with that address.": "{email} ยังไม่มีบัญชี SV Cloud คำเชิญจะรออยู่จนกว่าเขาจะลงชื่อเข้าใช้ด้วยอีเมลนี้ได้",
  "Inviting people is not part of this demo.": "การเชิญผู้ร่วมงานไม่ได้อยู่ในเดโมนี้",
  "Invite by email": "เชิญทางอีเมล",
  "They accept by signing in to SV Cloud with this email address. Inviting someone does not create an account for them.": "ผู้ได้รับเชิญตอบรับได้โดยลงชื่อเข้าใช้ SV Cloud ด้วยอีเมลนี้ การเชิญไม่ได้สร้างบัญชีให้เขา",
  "Send invitation": "ส่งคำเชิญ",
  "You are a collaborator on this app. Its owner is the only one who can delete it.": "คุณเป็นผู้ร่วมงานในแอปนี้ มีเพียงเจ้าของเท่านั้นที่ลบแอปได้",
  "Only the owner of this app can delete it. You can leave it from People above.": "มีเพียงเจ้าของแอปนี้เท่านั้นที่ลบได้ คุณออกจากแอปได้ที่ส่วนผู้ร่วมงานด้านบน",

  /* components/patterns/PendingInvitations, screens/auth/ProjectInvite (Teams) */
  "Shared with you": "แชร์กับคุณ",
  "{person} invited you to work on {project}.": "{person} เชิญคุณร่วมงานใน {project}",
  "You were invited to work on {project}.": "คุณได้รับคำเชิญให้ร่วมงานใน {project}",
  "Accept": "ตอบรับ",
  "This invitation was sent to a different email address. You are signed in as {email}. Sign out and sign in with the address it was sent to.": "คำเชิญนี้ส่งถึงอีเมลอื่น ตอนนี้คุณลงชื่อเข้าใช้ด้วย {email} ให้ออกจากระบบ แล้วลงชื่อเข้าใช้ด้วยอีเมลที่ได้รับคำเชิญ",
  "This invitation has already been answered.": "คำเชิญนี้ได้รับการตอบแล้ว",
  "This invitation is no longer valid. Ask for a new one.": "คำเชิญนี้ใช้ไม่ได้แล้ว ขอคำเชิญใหม่ได้",
  "This invitation link is not valid.": "ลิงก์คำเชิญนี้ไม่ถูกต้อง",
  "Invitation": "คำเชิญ",
  "Checking your invitation": "กำลังตรวจสอบคำเชิญของคุณ",
  "You will be able to do everything its owner can, from this dashboard, the svcloud CLI, or your own coding agent. Only the owner can delete it.": "คุณจะทำได้ทุกอย่างที่เจ้าของทำได้ ทั้งจากแดชบอร์ดนี้ svcloud CLI หรือเอเจนต์เขียนโค้ดของคุณเอง มีเพียงเจ้าของเท่านั้นที่ลบแอปได้",
  "Invited {email}": "เชิญ {email} แล้ว",
  "{email} joined this project": "{email} เข้าร่วมโปรเจกต์นี้",
  "Removed {email}": "นำ {email} ออกแล้ว",
  "{email} left this project": "{email} ออกจากโปรเจกต์นี้",
  "Cancelled the invitation for {email}": "ยกเลิกคำเชิญของ {email} แล้ว",
  "{email} declined the invitation": "{email} ปฏิเสธคำเชิญ",

  /* Plans and credits (2026-10-05): components/patterns/PlanSummary, screens/admin/Billing */
  "{plan}, until {date}": "{plan} ถึง {date}",
  "Credits": "เครดิต",
  "Promotional credits": "เครดิตโปรโมชัน",
  "first ones expire {date}": "ชุดแรกหมดอายุ {date}",
  "Compute past your allowance is paid from your credits when each cycle ends, promotional credits first. Storage past the allowance is not charged yet.": "การประมวลผลที่เกินโควตาจะหักจากเครดิตของคุณเมื่อสิ้นสุดแต่ละรอบ โดยใช้เครดิตโปรโมชันก่อน พื้นที่จัดเก็บที่เกินโควตายังไม่มีการคิดค่าบริการ",
  "Past your allowance": "เกินโควตา",
  "{amount} so far": "{amount} จนถึงตอนนี้",
  "at {rate} per million compute units": "ในอัตรา {rate} ต่อหนึ่งล้านหน่วยประมวลผล",
  "Your credits will not cover all of this": "เครดิตของคุณไม่พอสำหรับยอดนี้ทั้งหมด",
  /* Credits used this cycle, and overage paid as it is used (2026-10-25) */
  "Compute past your allowance is paid from your credits as you use it, promotional credits first. Storage past the allowance is not charged yet.": "การประมวลผลที่เกินโควตาจะหักจากเครดิตของคุณระหว่างที่ใช้งาน โดยใช้เครดิตโปรโมชันก่อน พื้นที่จัดเก็บที่เกินโควตายังไม่มีการคิดค่าบริการ",
  "{amount} paid from credits": "หักจากเครดิตแล้ว {amount}",
  "Credits used this cycle": "เครดิตที่ใช้ในรอบนี้",
  "No credits used this cycle": "ยังไม่ได้ใช้เครดิตในรอบนี้",
  "{overage} past your allowance, {plan} for your plan": "ส่วนเกินโควตา {overage} ค่าแพลน {plan}",
  "{amount} of it is not covered by your credits. Your apps keep running and nothing is limited. Ask the SV Cloud team to add credits.": "มี {amount} ที่เครดิตของคุณไม่ครอบคลุม แอปของคุณยังทำงานตามปกติและไม่มีการจำกัดการใช้งาน ติดต่อทีม SV Cloud เพื่อเพิ่มเครดิต",
  "Plan and credits": "แพลนและเครดิต",
  "One allowance for your whole account, shared by all of your apps.": "โควตาเดียวสำหรับทั้งบัญชี ใช้ร่วมกันทุกแอปของคุณ",
  "This app spends from your account allowance, shared with your other apps.": "แอปนี้ใช้โควตาของบัญชีคุณ ร่วมกับแอปอื่นๆ ของคุณ",
  "Plans & credits": "แพลนและเครดิต",
  "Basic. {plan} ended {date}": "Basic ({plan} สิ้นสุดเมื่อ {date})",
  "Basic, with no end date.": "Basic ไม่มีวันสิ้นสุด",
  "{plan}, extended until {date}.": "{plan} ขยายถึง {date}",
  "{plan}, until {date}.": "{plan} ถึง {date}",
  "Months must be a whole number from 1 to {n}.": "จำนวนเดือนต้องเป็นจำนวนเต็มตั้งแต่ 1 ถึง {n}",
  "{email} is now on {reading}.": "ตอนนี้ {email} ใช้ {reading}",
  "Promotional credits need an end date.": "เครดิตโปรโมชันต้องมีวันสิ้นสุด",
  "Added {amount} in credits to {email}.": "เพิ่มเครดิต {amount} ให้ {email} แล้ว",
  "Credits revoked.": "เพิกถอนเครดิตแล้ว",
  "Manage": "จัดการ",
  "Paid": "ชำระเงิน",
  "Promotional": "โปรโมชัน",
  "Amount": "จำนวน",
  "Ends": "สิ้นสุด",
  "Never": "ไม่มี",
  "Note": "บันทึก",
  "Revoked {date}": "เพิกถอนเมื่อ {date}",
  "Set an account plan and add credits by hand. Compute past the allowance is paid from credits when each month closes; nothing is limited.": "ตั้งแพลนและเพิ่มเครดิตให้บัญชีด้วยตนเอง การประมวลผลที่เกินโควตาจะหักจากเครดิตเมื่อปิดยอดแต่ละเดือน และไม่มีการจำกัดการใช้งาน",
  "Search accounts by email...": "ค้นหาบัญชีด้วยอีเมล...",
  "Search accounts by email": "ค้นหาบัญชีด้วยอีเมล",
  "No accounts match that search.": "ไม่มีบัญชีที่ตรงกับการค้นหา",
  "Loading plan and credits": "กำลังโหลดแพลนและเครดิต",
  "Could not load this account. Try again in a moment.": "โหลดบัญชีนี้ไม่ได้ ลองใหม่อีกครั้งในอีกสักครู่",
  "Next promotional end date": "วันสิ้นสุดเครดิตโปรโมชันถัดไป",
  "Set plan": "ตั้งแพลน",
  "Granting the same plan again while it is running adds the months to its current end date.": "การให้แผนเดิมอีกครั้งระหว่างที่ยังใช้งานอยู่ จะเพิ่มจำนวนเดือนต่อจากวันสิ้นสุดเดิม",
  "Basic": "Basic",
  "Pro": "Pro",
  "Max": "Max",
  "Months": "จำนวนเดือน",
  "Add credits": "เพิ่มเครดิต",
  "Promotional credits are free, end on a date, and will be used before paid credits.": "เครดิตโปรโมชันใช้ฟรี มีวันสิ้นสุด และจะถูกใช้ก่อนเครดิตที่ชำระเงิน",
  "Amount in US dollars": "จำนวนเงิน (ดอลลาร์สหรัฐ)",
  "Ends on": "สิ้นสุดวันที่",
  "They can be used until the end of this day.": "ใช้ได้จนถึงสิ้นวันนี้",
  "Credit history": "ประวัติเครดิต",
  "No credits added yet.": "ยังไม่มีการเพิ่มเครดิต",
  "Left": "คงเหลือ",
  "Month": "เดือน",
  "Past allowance": "เกินโควตา",
  "From credits": "หักจากเครดิต",
  "Not covered": "ไม่ครอบคลุม",
  "Monthly statements": "ใบสรุปยอดรายเดือน",
  "Closed on the 2nd of each month. Compute only; storage past the allowance is not charged yet.": "ปิดยอดทุกวันที่ 2 ของเดือน คิดเฉพาะการประมวลผล พื้นที่จัดเก็บที่เกินโควตายังไม่มีการคิดค่าบริการ",
  "No months closed yet.": "ยังไม่มีเดือนที่ปิดยอด",
  "Credits are not paying for your plan, so it ends on {date}.": "เครดิตไม่ได้ใช้ชำระค่าแพลนของคุณ แพลนจึงจะสิ้นสุดในวันที่ {date}",
  "It ended on {date}. Ask the SV Cloud team to start it again.": "แพลนสิ้นสุดเมื่อวันที่ {date} ติดต่อทีม SV Cloud เพื่อเริ่มแพลนใหม่",
  "It ended on {date}. Your apps were shut down, and their data is deleted 30 days after that. Ask the SV Cloud team to start a plan again.": "แพลนสิ้นสุดเมื่อวันที่ {date} แอปของคุณถูกปิดแล้ว และข้อมูลจะถูกลบหลังจากนั้น 30 วัน ติดต่อทีม SV Cloud เพื่อเริ่มแพลนใหม่",
  "Renewing on {date} costs {amount}.": "การต่ออายุในวันที่ {date} มีค่าใช้จ่าย {amount}",
  "Your apps will be shut down then, and their data deleted 30 days later.": "เมื่อถึงวันนั้นแอปของคุณจะถูกปิด และข้อมูลจะถูกลบหลังจากนั้น 30 วัน",
  "Your credits do not cover the next renewal": "เครดิตของคุณไม่พอสำหรับการต่ออายุครั้งถัดไป",
  "Your plan has ended": "แพลนของคุณสิ้นสุดแล้ว",
  "Your plan will not renew": "แพลนของคุณจะไม่ต่ออายุ",
  "moves to {plan} on {date}": "เปลี่ยนเป็น {plan} ในวันที่ {date}",
  "{plan} trial, until {date}": "ทดลองใช้ {plan} ถึง {date}",
  "{plan}, ended {date}": "{plan} สิ้นสุดเมื่อ {date}",
  "{plan}, ends {date}": "{plan} สิ้นสุด {date}",
  "{plan}, renews {date}": "{plan} ต่ออายุ {date}",
  "Could not save that. Try again.": "บันทึกไม่สำเร็จ โปรดลองอีกครั้ง",
  "Each cycle, your plan is paid from your credits before anything else.": "ทุกรอบ ค่าแพลนจะหักจากเครดิตของคุณก่อนรายการอื่น",
  "Off: your credits only pay for use past your allowance, and your plan ends with its cycle.": "ปิดอยู่: เครดิตจะใช้จ่ายเฉพาะการใช้งานที่เกินโควตา และแพลนจะสิ้นสุดเมื่อครบรอบ",
  "Pay my plan from my credits": "ชำระค่าแพลนจากเครดิตของฉัน",
  "Change plan": "เปลี่ยนแพลน",
  "Charges {amount} from credits now.": "หัก {amount} จากเครดิตทันที",
  "Credits pay the plan": "ชำระค่าแพลนด้วยเครดิต",
  "Cycle": "รอบ",
  "Free months": "เดือนฟรี",
  "Free, for a trial, a comp, or a payment taken outside SV Cloud. Granting the same plan again while it is running adds the months to its current end date; any other plan starts a new cycle today.": "ฟรี สำหรับการทดลองใช้ การให้สิทธิ์พิเศษ หรือการชำระเงินนอก SV Cloud การให้แพลนเดิมอีกครั้งระหว่างที่ยังใช้งานอยู่จะเพิ่มจำนวนเดือนต่อจากวันสิ้นสุดเดิม ส่วนแพลนอื่นจะเริ่มรอบใหม่ตั้งแต่วันนี้",
  "Grant free months": "ให้เดือนฟรี",
  "Moves at the next cycle, {date}. No refund.": "เปลี่ยนในรอบถัดไป {date} ไม่มีการคืนเงิน",
  "Moves to {plan}": "เปลี่ยนเป็น {plan}",
  "New plan": "แพลนใหม่",
  "Next cycle": "รอบถัดไป",
  "No plan charges yet.": "ยังไม่มีค่าแพลน",
  "No, turned off by the owner": "ไม่ เจ้าของปิดไว้",
  "Paid from the account's credits. An upgrade applies now and charges the difference for the rest of this cycle; a downgrade applies at the next cycle. No refunds.": "ชำระจากเครดิตของบัญชี การอัปเกรดมีผลทันทีและคิดส่วนต่างสำหรับเวลาที่เหลือของรอบนี้ การดาวน์เกรดมีผลในรอบถัดไป ไม่มีการคืนเงิน",
  "Plan charges": "ค่าแพลน",
  "Renewal": "ต่ออายุ",
  "Restart": "เริ่มใหม่",
  "Standing": "สถานะ",
  "This cycle": "รอบนี้",
  "Trial": "ทดลองใช้",
  "Ended": "สิ้นสุดแล้ว",
  "Upgrade": "อัปเกรด",
  "{email} moves to {plan} at the next cycle.": "{email} จะเปลี่ยนเป็น {plan} ในรอบถัดไป",
  "{start} to {end}": "{start} ถึง {end}",
  "Revoke {amount} of credits?": "เพิกถอนเครดิต {amount} ใช่ไหม",
  "They stop counting toward the balance. The record stays in the history.": "เครดิตนี้จะไม่นับรวมในยอดคงเหลืออีก แต่ยังคงอยู่ในประวัติ",
  "Point at the chart to see the numbers. Point at a deploy line to pick it out.": "ชี้ที่กราฟเพื่อดูตัวเลข ชี้ที่เส้นดีพลอยเพื่อดูว่าเป็นเวอร์ชันไหน",
  "Hold the chart and drag to see the numbers. Drag onto a deploy line to pick it out.": "กดค้างที่กราฟแล้วลากเพื่อดูตัวเลข ลากไปที่เส้นดีพลอยเพื่อดูว่าเป็นเวอร์ชันไหน",

  /* screens/marketing/Privacy (2026-10-25) */
  "Cloudflare": "Cloudflare",
  "Cockroach Labs (AWS, Singapore)": "Cockroach Labs (AWS, สิงคโปร์)",
  "GitHub": "GitHub",
  "PostHog (United States)": "PostHog (สหรัฐอเมริกา)",
  "Telegram": "Telegram",
  "Your email address and what you are building, if you join the waitlist": "อีเมลของคุณและสิ่งที่คุณกำลังสร้าง หากคุณลงชื่อในรายชื่อรอ",
  "So we can invite you. Our team sees new sign ups in our internal Telegram chat.": "เพื่อให้เราเชิญคุณได้ ทีมของเราเห็นการลงชื่อใหม่ในแชต Telegram ภายในของเรา",
  "Your account: email address, name, how you sign in, your GitHub account name, your plan and credits": "บัญชีของคุณ: อีเมล ชื่อ วิธีที่คุณลงชื่อเข้าใช้ ชื่อบัญชี GitHub แพ็กเกจ และเครดิตของคุณ",
  "To make your account, sign you in, bill your plan, and email you about your own projects.": "เพื่อสร้างบัญชี ให้คุณลงชื่อเข้าใช้ เรียกเก็บค่าแพ็กเกจ และส่งอีเมลถึงคุณเกี่ยวกับโปรเจกต์ของคุณเอง",
  "While your account is open. Delete the account from the Account page and we remove it, except billing records the law makes us keep.": "ตราบที่บัญชียังเปิดอยู่ ลบบัญชีได้จากหน้าบัญชี แล้วเราจะลบข้อมูลนี้ ยกเว้นบันทึกการเรียกเก็บเงินที่กฎหมายกำหนดให้เราเก็บไว้",
  "Your projects: the code we deploy, your database, your files and your keys": "โปรเจกต์ของคุณ: โค้ดที่เรานำขึ้นใช้งาน ฐานข้อมูล ไฟล์ และคีย์ของคุณ",
  "Running your app is the service.": "การรันแอปของคุณคือบริการของเรา",
  "While the project exists, and 30 days after you delete it, in case you deleted it by mistake.": "ตราบที่โปรเจกต์ยังอยู่ และอีก 30 วันหลังจากคุณลบ เผื่อคุณลบโดยไม่ตั้งใจ",
  "Logs from your app: errors and messages your app writes, and request counts": "บันทึกจากแอปของคุณ: ข้อผิดพลาดและข้อความที่แอปเขียน และจำนวนคำขอ",
  "So you can see what went wrong, and so we can measure usage for your plan.": "เพื่อให้คุณเห็นว่าเกิดอะไรผิดพลาด และเพื่อให้เราวัดการใช้งานตามแพ็กเกจของคุณ",
  "Error and log messages for 30 days. Request and usage counts for as long as billing needs them.": "ข้อความข้อผิดพลาดและบันทึกเก็บไว้ 30 วัน จำนวนคำขอและการใช้งานเก็บไว้ตราบที่การเรียกเก็บเงินยังต้องใช้",
  "How you use this dashboard, only if you allow it": "วิธีที่คุณใช้แดชบอร์ดนี้ เฉพาะเมื่อคุณอนุญาต",
  "To find what is confusing and fix it. Page visits and clicks, never a screen recording, never your email address.": "เพื่อหาส่วนที่ใช้งานยากแล้วแก้ไข เราเก็บเพียงการเข้าชมหน้าและการคลิก ไม่บันทึกหน้าจอ และไม่เก็บอีเมลของคุณ",
  "You pressed Allow. Press Privacy choices at the bottom of any page to change your answer.": "คุณกดอนุญาต กดตัวเลือกความเป็นส่วนตัวที่ด้านล่างของหน้าใดก็ได้เพื่อเปลี่ยนคำตอบ",
  "Up to 12 months, then deleted.": "ไม่เกิน 12 เดือน แล้วจึงลบ",
  "Runs the dashboard and your apps, stores files, sends our emails, and checks upload links for bots (Turnstile).": "รันแดชบอร์ดและแอปของคุณ เก็บไฟล์ ส่งอีเมลของเรา และตรวจลิงก์อัปโหลดเพื่อกันบอต (Turnstile)",
  "Stores the databases your apps use.": "เก็บฐานข้อมูลที่แอปของคุณใช้",
  "Holds your code. We read the repository you connect.": "เก็บโค้ดของคุณ เราอ่านรีโพสิทอรีที่คุณเชื่อมต่อ",
  "Dashboard usage analytics, only if you allow it.": "การวัดผลการใช้งานแดชบอร์ด เฉพาะเมื่อคุณอนุญาต",
  "Tells our team about new waitlist sign ups.": "แจ้งทีมของเราเมื่อมีผู้ลงชื่อในรายชื่อรอใหม่",
  "Your sign in": "การลงชื่อเข้าใช้ของคุณ",
  "Keeps you signed in. Removed when you sign out.": "ทำให้คุณยังลงชื่อเข้าใช้อยู่ จะถูกลบเมื่อคุณออกจากระบบ",
  "Your theme and layout": "ธีมและรูปแบบของคุณ",
  "So the page opens the way you set it.": "เพื่อให้หน้าเปิดขึ้นตามที่คุณตั้งไว้",
  "Your waitlist form": "แบบฟอร์มรายชื่อรอของคุณ",
  "So a half typed waitlist form, including your email address, is still there if you come back.": "เพื่อให้แบบฟอร์มรายชื่อรอที่พิมพ์ค้างไว้ รวมถึงอีเมลของคุณ ยังอยู่เมื่อคุณกลับมา",
  "Small reminders and a project you started creating": "การแจ้งเตือนเล็กๆ และโปรเจกต์ที่คุณเริ่มสร้างไว้",
  "So a reminder you dismissed stays dismissed, and a half finished project survives a reload.": "เพื่อให้การแจ้งเตือนที่คุณปิดไปแล้วไม่กลับมาอีก และโปรเจกต์ที่สร้างค้างไว้ไม่หายเมื่อโหลดหน้าใหม่",
  "Your analytics answer": "คำตอบของคุณเรื่องการวัดผลการใช้งาน",
  "An analytics ID, only if you allow it": "รหัสสำหรับการวัดผลการใช้งาน เฉพาะเมื่อคุณอนุญาต",
  "So PostHog can count visits. Removed when you change your answer to no.": "เพื่อให้ PostHog นับการเข้าชมได้ จะถูกลบเมื่อคุณเปลี่ยนคำตอบเป็นไม่อนุญาต",
  "We do not sell your personal information, and we do not share it for advertising.": "เราไม่ขายข้อมูลส่วนบุคคลของคุณ และไม่แบ่งปันเพื่อการโฆษณา",
  "We never record your screen or what you type.": "เราไม่บันทึกหน้าจอหรือสิ่งที่คุณพิมพ์",
  "We put no advertising pixel and no social widget on this site.": "เราไม่ติดพิกเซลโฆษณาหรือวิดเจ็ตโซเชียลบนเว็บนี้",
  "Ask us to delete it, or delete your account yourself from the Account page.": "ขอให้เราลบข้อมูล หรือลบบัญชีด้วยตัวเองจากหน้าบัญชี",
  "Who handles it for us": "ใครดูแลข้อมูลแทนเรา",
  "Some of these are outside Thailand, so your data may be stored or read in other countries, including the United States and Singapore.": "บางรายอยู่นอกประเทศไทย ข้อมูลของคุณจึงอาจถูกเก็บหรืออ่านในประเทศอื่น รวมถึงสหรัฐอเมริกาและสิงคโปร์",
  "These sit in your own browser's storage on your own machine. We set no cookies.": "ข้อมูลเหล่านี้อยู่ในพื้นที่เก็บข้อมูลของเบราว์เซอร์บนเครื่องของคุณเอง เราไม่ตั้งคุกกี้",
  "Analytics starts only after you press Allow. If your browser sends Global Privacy Control or Do Not Track, we treat that as no and do not ask. Accounts that may belong to students under 18 never get analytics.": "การวัดผลการใช้งานเริ่มหลังจากคุณกดอนุญาตเท่านั้น หากเบราว์เซอร์ของคุณส่งสัญญาณ Global Privacy Control หรือ Do Not Track เราจะถือว่าไม่อนุญาตและจะไม่ถาม บัญชีที่อาจเป็นของนักเรียนอายุต่ำกว่า 18 ปีจะไม่มีการวัดผลการใช้งานเลย",
  "If you live in California": "หากคุณอยู่ในรัฐแคลิฟอร์เนีย",
  "You can ask us what personal information we have collected about you, ask us to correct it, and ask us to delete it. We will not treat you differently for asking. We do not sell or share personal information, as California law defines those words.": "คุณขอให้เราแจ้งว่าเราเก็บข้อมูลส่วนบุคคลอะไรเกี่ยวกับคุณ ขอให้แก้ไข และขอให้ลบได้ เราจะไม่ปฏิบัติต่อคุณต่างไปเพราะคุณขอ เราไม่ขายหรือแบ่งปันข้อมูลส่วนบุคคลตามความหมายในกฎหมายแคลิฟอร์เนีย",
  "Write to the address below. Someone you authorize can ask for you. We may need to confirm the request comes from you before we act on it.": "เขียนถึงเราที่อีเมลด้านล่าง ผู้ที่คุณมอบอำนาจขอแทนคุณได้ เราอาจต้องยืนยันว่าคำขอมาจากคุณก่อนดำเนินการ",
  "Children": "เด็ก",
  "SV Cloud is not for children under 13, and we do not knowingly collect their information. If you think a child under 13 has given us information, write to us and we will delete it.": "SV Cloud ไม่ได้มีไว้สำหรับเด็กอายุต่ำกว่า 13 ปี และเราไม่เก็บข้อมูลของเด็กโดยรู้ตัว หากคุณคิดว่าเด็กอายุต่ำกว่า 13 ปีให้ข้อมูลกับเรา โปรดเขียนถึงเรา แล้วเราจะลบข้อมูลนั้น",

  /* screens/marketing/Terms */
  "Who you are agreeing with": "คุณตกลงกับใคร",
  "SV Cloud is run by Silicon Valley Innovation Co., Ltd. in Bangkok, Thailand. By creating an account or using SV Cloud you agree to these terms. If you use it for an organisation, you agree for that organisation.": "SV Cloud ดำเนินการโดย Silicon Valley Innovation Co., Ltd. ที่กรุงเทพฯ ประเทศไทย เมื่อคุณสร้างบัญชีหรือใช้ SV Cloud ถือว่าคุณยอมรับข้อกำหนดเหล่านี้ หากคุณใช้ในนามองค์กร คุณยอมรับในนามองค์กรนั้น",
  "Who can use SV Cloud": "ใครใช้ SV Cloud ได้",
  "You must be at least 13. If you are under 18, you need permission from a parent or guardian, and you may use SV Cloud only through a class or program that gives you access.": "คุณต้องมีอายุอย่างน้อย 13 ปี หากอายุต่ำกว่า 18 ปี คุณต้องได้รับอนุญาตจากพ่อแม่หรือผู้ปกครอง และใช้ SV Cloud ได้เฉพาะผ่านชั้นเรียนหรือโครงการที่ให้สิทธิ์คุณเท่านั้น",
  "Keep your sign in safe. You are responsible for what happens under your account, including what your coding agent or collaborators do with the access you give them.": "เก็บข้อมูลการลงชื่อเข้าใช้ให้ปลอดภัย คุณรับผิดชอบสิ่งที่เกิดขึ้นภายใต้บัญชีของคุณ รวมถึงสิ่งที่เอเจนต์เขียนโค้ดหรือผู้ร่วมงานทำด้วยสิทธิ์ที่คุณให้",
  "What you may not do": "สิ่งที่คุณห้ามทำ",
  "Do not use SV Cloud to break the law, to host malware or phishing, to send spam, to attack or overload other systems, to mine cryptocurrency, to store or share material that sexually exploits children, or to share content you do not have the right to share. We may take an app offline or suspend an account that does any of these, and report it where the law requires.": "ห้ามใช้ SV Cloud เพื่อทำผิดกฎหมาย โฮสต์มัลแวร์หรือฟิชชิง ส่งสแปม โจมตีหรือทำให้ระบบอื่นทำงานเกินกำลัง ขุดคริปโทเคอร์เรนซี เก็บหรือเผยแพร่สื่อที่แสวงประโยชน์ทางเพศจากเด็ก หรือเผยแพร่เนื้อหาที่คุณไม่มีสิทธิ์ เราอาจปิดแอปหรือระงับบัญชีที่ทำสิ่งเหล่านี้ และรายงานตามที่กฎหมายกำหนด",
  "Your apps and your users": "แอปและผู้ใช้ของคุณ",
  "Your code, your data and your files stay yours. You let us store, copy and run them only as needed to provide SV Cloud to you.": "โค้ด ข้อมูล และไฟล์ของคุณยังเป็นของคุณ คุณอนุญาตให้เราเก็บ คัดลอก และรันสิ่งเหล่านี้เท่าที่จำเป็นเพื่อให้บริการ SV Cloud แก่คุณเท่านั้น",
  "When your app collects information about the people who use it, you decide what it collects and you are responsible for telling them, usually in your own privacy notice. We handle that information only to run your app for you: we keep error and log messages your app writes for 30 days, and we do not sell it or use it for anything else.": "เมื่อแอปของคุณเก็บข้อมูลของผู้ใช้ คุณเป็นผู้กำหนดว่าจะเก็บอะไร และมีหน้าที่แจ้งผู้ใช้ ซึ่งมักทำผ่านประกาศความเป็นส่วนตัวของคุณเอง เราจัดการข้อมูลนั้นเพื่อรันแอปให้คุณเท่านั้น เราเก็บข้อความข้อผิดพลาดและบันทึกที่แอปเขียนไว้ 30 วัน และไม่ขายหรือนำไปใช้เพื่อสิ่งอื่น",
  "Upload links and copyright complaints": "ลิงก์อัปโหลดและการร้องเรียนเรื่องลิขสิทธิ์",
  "If you send someone an upload link, you are responsible for what they upload into your app.": "หากคุณส่งลิงก์อัปโหลดให้ผู้อื่น คุณรับผิดชอบสิ่งที่เขาอัปโหลดเข้าแอปของคุณ",
  "If you believe something hosted on SV Cloud infringes your copyright or breaks these terms, write to us with the address of the content, what it infringes, and how to reach you. We will look at it and may remove it.": "หากคุณเชื่อว่าสิ่งที่โฮสต์บน SV Cloud ละเมิดลิขสิทธิ์ของคุณหรือผิดข้อกำหนดเหล่านี้ โปรดเขียนถึงเราพร้อมที่อยู่ของเนื้อหา สิ่งที่ถูกละเมิด และช่องทางติดต่อคุณ เราจะตรวจสอบและอาจนำเนื้อหานั้นออก",
  "Plans, renewal and cancelling": "แพ็กเกจ การต่ออายุ และการยกเลิก",
  "New accounts start with one free cycle of the Basic plan. Plans run in monthly cycles. Nothing renews unless you turn on auto-pay on the Account page, where the plan, the price and how to cancel are shown. With auto-pay on, the plan renews automatically at the start of each cycle at the price shown for it, paid from your credits, and we email you a confirmation.": "บัญชีใหม่เริ่มต้นด้วยแพ็กเกจ Basic ฟรีหนึ่งรอบ แพ็กเกจมีรอบเป็นรายเดือน จะไม่มีการต่ออายุใดๆ จนกว่าคุณจะเปิดการชำระอัตโนมัติที่หน้าบัญชี ซึ่งแสดงแพ็กเกจ ราคา และวิธียกเลิกไว้ เมื่อเปิดการชำระอัตโนมัติ แพ็กเกจจะต่ออายุเองเมื่อเริ่มแต่ละรอบในราคาที่แสดงไว้ โดยหักจากเครดิตของคุณ และเราจะส่งอีเมลยืนยันให้คุณ",
  "Before your free cycle turns into a paid one, and before each renewal, we email you the plan, the price, the date and how to cancel.": "ก่อนรอบฟรีจะเปลี่ยนเป็นรอบที่ต้องชำระ และก่อนการต่ออายุแต่ละครั้ง เราจะส่งอีเมลแจ้งแพ็กเกจ ราคา วันที่ และวิธียกเลิก",
  "To cancel, turn auto-pay off on the Account page at any time, in one step. Your plan then ends with its current cycle and nothing more is charged. Charges already made are not refunded, except where the law requires.": "หากต้องการยกเลิก ปิดการชำระอัตโนมัติที่หน้าบัญชีได้ทุกเมื่อในขั้นตอนเดียว แพ็กเกจจะสิ้นสุดเมื่อจบรอบปัจจุบันและจะไม่มีการเรียกเก็บเพิ่ม ค่าบริการที่เรียกเก็บไปแล้วจะไม่คืน ยกเว้นตามที่กฎหมายกำหนด",
  "When we may stop the service": "เมื่อใดที่เราอาจหยุดให้บริการ",
  "We may suspend or close an account that breaks these terms, puts others at risk, or leaves bills unpaid. Where we can, we tell you first. You can delete your account at any time from the Account page.": "เราอาจระงับหรือปิดบัญชีที่ผิดข้อกำหนดเหล่านี้ ทำให้ผู้อื่นเสี่ยง หรือค้างชำระ หากทำได้เราจะแจ้งคุณก่อน คุณลบบัญชีได้ทุกเมื่อจากหน้าบัญชี",
  "No guarantees": "ไม่มีการรับประกัน",
  "We work hard to keep SV Cloud running, but it is provided as it is. As far as the law allows, we are not responsible for lost profits or lost data, and our total responsibility is limited to what you paid us in the last 12 months. Keep your own copy of anything important.": "เราพยายามอย่างเต็มที่ให้ SV Cloud ทำงานได้ตลอด แต่บริการนี้ให้ตามสภาพที่เป็นอยู่ เท่าที่กฎหมายอนุญาต เราไม่รับผิดชอบต่อกำไรหรือข้อมูลที่สูญหาย และความรับผิดชอบรวมของเราจำกัดไม่เกินจำนวนที่คุณชำระให้เราในช่วง 12 เดือนล่าสุด โปรดเก็บสำเนาของสิ่งสำคัญไว้เอง",
  "When these terms change": "เมื่อข้อกำหนดเหล่านี้เปลี่ยนแปลง",
  "If we change these terms in a way that matters, we tell you by email or in the dashboard before the change takes effect, and we put the new date at the top.": "หากเราเปลี่ยนข้อกำหนดในส่วนที่สำคัญ เราจะแจ้งคุณทางอีเมลหรือในแดชบอร์ดก่อนการเปลี่ยนแปลงมีผล และใส่วันที่ใหม่ไว้ด้านบน",
  "The rules for using SV Cloud, in plain words.": "กติกาการใช้ SV Cloud ด้วยภาษาที่เข้าใจง่าย",
  "Questions, abuse reports and copyright complaints all go to the same place.": "คำถาม การแจ้งการใช้งานในทางที่ผิด และการร้องเรียนเรื่องลิขสิทธิ์ ส่งมาที่เดียวกันได้ทั้งหมด",

  /* screens/drop (Turnstile, 2026-10-25) */
  "The security check could not load. Reload the page and try again.": "โหลดการตรวจสอบความปลอดภัยไม่สำเร็จ โปรดโหลดหน้าใหม่แล้วลองอีกครั้ง",
  "Turnstile did not load.": "โหลด Turnstile ไม่สำเร็จ",
  "The check did not pass. Please try again.": "การตรวจสอบไม่ผ่าน โปรดลองอีกครั้ง",
  "The check expired. Please try again.": "การตรวจสอบหมดเวลา โปรดลองอีกครั้ง",
  "The check took too long. Please try again.": "การตรวจสอบใช้เวลานานเกินไป โปรดลองอีกครั้ง",

  /* components/patterns/PageCacheSection */
  "Page caching": "แคชหน้าเว็บ",
  "We keep a copy of the pages that look the same for everyone and send that copy to the next visitor, so the page opens faster and your app runs less often.": "เราเก็บสำเนาของหน้าที่ทุกคนเห็นเหมือนกัน แล้วส่งสำเนานั้นให้ผู้เข้าชมคนถัดไป หน้าจึงเปิดเร็วขึ้นและแอปของคุณทำงานน้อยลง",
  "Page caching is on. Every deploy starts with an empty cache, so a change you push shows up right away.": "แคชหน้าเว็บเปิดอยู่ ทุกการดีพลอยจะเริ่มด้วยแคชว่าง การเปลี่ยนแปลงที่คุณพุชจึงแสดงผลทันที",
  "Turn this on only after your app marks which pages are the same for everyone. A page with no marking is kept for two hours and shown to every visitor, even a page that shows one person their own account.": "เปิดใช้เฉพาะหลังจากแอปของคุณระบุแล้วว่าหน้าไหนที่ทุกคนเห็นเหมือนกัน หน้าที่ไม่ได้ระบุจะถูกเก็บไว้สองชั่วโมงและแสดงให้ผู้เข้าชมทุกคนเห็น แม้แต่หน้าที่แสดงบัญชีของคนคนเดียว",
  "Ask your coding agent to check first:": "ขอให้เอเจนต์เขียนโค้ดของคุณตรวจสอบก่อน:",
  "What to ask your coding agent": "สิ่งที่ควรขอให้เอเจนต์เขียนโค้ดของคุณทำ",
  "Turn the project back on to change this.": "เปิดโปรเจกต์กลับคืนก่อนจึงจะเปลี่ยนการตั้งค่านี้ได้",
  "Turning off": "กำลังปิด",
  "Turning on": "กำลังเปิด",
  "Turn off page caching": "ปิดแคชหน้าเว็บ",
  "Turn on page caching": "เปิดแคชหน้าเว็บ",
  "Turned on page caching": "เปิดแคชหน้าเว็บแล้ว",
  "Turned off page caching": "ปิดแคชหน้าเว็บแล้ว",
};
